/* crm-custom-objects.jsx — CO-003 Definition Builder + CO-004 Records + CO-007 Relationship UX */
import { useState, useEffect, useCallback, useRef, isValidElement } from 'react';
import { useWorkspace }      from '../WorkspaceRuntimeContext.jsx';
import { useRbac }           from '../../rbac/RbacContext.jsx';
import { useToast }          from '../../shared/feedback/index.jsx';
import {
  Modal, ConfirmDialog, Btn, IconBtn, EmptyState, NexusIcon, Pill,
  MenuItem, MenuDivider, Menu, SearchField, Avatar,
} from '../../shared/ui/index.jsx';
import { SortableTh }  from '../../shared/data-table/index.jsx';
import * as coApi      from '../../api/custom-objects.api.js';
import * as peopleApi  from '../../api/people.api.js';
import * as companyApi from '../../api/company.api.js';
import * as dealApi    from '../../api/deal.api.js';
import * as taskApi    from '../../api/task.api.js';

// ─────────────────────────────────────────────────────────────────────────────
// Constants — driven by certified backend contract (CoFieldType)
// ─────────────────────────────────────────────────────────────────────────────

export const CO_FIELD_TYPES = [
  { value: 'TEXT',         label: 'Text',          icon: 'Type',        desc: 'Single-line text' },
  { value: 'NUMBER',       label: 'Number',         icon: 'Hash',        desc: 'Numeric value' },
  { value: 'BOOLEAN',      label: 'Checkbox',       icon: 'CheckSquare', desc: 'True / false' },
  { value: 'DATE',         label: 'Date',           icon: 'Calendar',    desc: 'Date value' },
  { value: 'SELECT',       label: 'Single Select',  icon: 'List',        desc: 'Choose one option' },
  { value: 'MULTI_SELECT', label: 'Multi Select',   icon: 'Layers',      desc: 'Choose multiple' },
  { value: 'EMAIL',        label: 'Email',          icon: 'Mail',        desc: 'Email address' },
  { value: 'PHONE',        label: 'Phone',          icon: 'Phone',       desc: 'Phone number' },
  { value: 'URL',          label: 'URL',            icon: 'Link',        desc: 'Web address' },
  { value: 'TEXTAREA',     label: 'Long Text',      icon: 'AlignLeft',   desc: 'Multi-line text' },
];

export const CO_ICONS = [
  'Database','FileText','Box','Briefcase','Building','Calendar','BarChart','ClipboardList',
  'CreditCard','Globe','HeartPulse','Key','Layers','MapPin','Package','Shield',
  'Star','Tag','Target','Award','User','Zap','DollarSign','Activity',
];

export const RESERVED_API_NAMES = new Set([
  'id','object','workspace','created_at','updated_at','deleted',
  'deleted_at','object_id','workspace_id','type','status',
]);

export const API_NAME_REGEX = /^[a-z][a-z0-9_]{0,62}$/;

const STATUS_CONFIG = {
  DRAFT:    { tone: 'gray',  label: 'Draft' },
  ACTIVE:   { tone: 'green', label: 'Active' },
  ARCHIVED: { tone: 'amber', label: 'Archived' },
};

// CO-011d: operators per field type — mirrors ALLOWED_FILTER_OPS in custom-objects.service.ts
const FILTER_OPS = {
  TEXT:         [['contains','Contains'],['eq','Equals'],['isEmpty','Is empty']],
  TEXTAREA:     [['contains','Contains'],['eq','Equals'],['isEmpty','Is empty']],
  EMAIL:        [['contains','Contains'],['eq','Equals'],['isEmpty','Is empty']],
  PHONE:        [['contains','Contains'],['eq','Equals'],['isEmpty','Is empty']],
  URL:          [['contains','Contains'],['eq','Equals'],['isEmpty','Is empty']],
  NUMBER:       [['eq','='],['ne','≠'],['lt','<'],['gt','>'],['gte','≥'],['lte','≤'],['between','Between']],
  BOOLEAN:      [['eq','Is']],
  DATE:         [['before','Before'],['after','After'],['between','Between'],['isEmpty','Is empty']],
  SELECT:       [['eq','Equals'],['in','Is any of']],
  MULTI_SELECT: [['containsAny','Contains any of'],['containsAll','Contains all of']],
};

// ─────────────────────────────────────────────────────────────────────────────
// Pure utility functions (exported for testing)
// ─────────────────────────────────────────────────────────────────────────────

export function toApiName(label) {
  if (!label) return '';
  const base = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  if (!base) return '';
  const safe = /^[a-z]/.test(base) ? base : 'f_' + base;
  return safe.slice(0, 63);
}

export function validateApiName(name, existingNames = [], reserved = RESERVED_API_NAMES, isEdit = false) {
  if (!name || !name.trim()) return 'API name is required';
  if (!API_NAME_REGEX.test(name)) return 'Must start with a letter and contain only lowercase letters, digits, and underscores';
  if (reserved.has(name)) return `"${name}" is a reserved name`;
  if (existingNames.includes(name)) return 'This API name is already in use';
  return null;
}

export function getFieldTypeConfig(type) {
  return CO_FIELD_TYPES.find(t => t.value === type) || CO_FIELD_TYPES[0];
}

export function hasSelectOptions(type) {
  return type === 'SELECT' || type === 'MULTI_SELECT';
}

export function fmtRelTime(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export function fmtAbsTime(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// CO-004 utility functions — record value handling (exported for testing)
// ─────────────────────────────────────────────────────────────────────────────

export function formatFieldValue(value, apiType, _options = []) {
  if (value === null || value === undefined || value === '') return '—';
  switch (apiType) {
    case 'BOOLEAN': return value === 'true' ? 'Yes' : 'No';
    case 'DATE': {
      const d = new Date(value);
      return isNaN(d.getTime()) ? value : d.toLocaleDateString();
    }
    case 'MULTI_SELECT': {
      try {
        const arr = JSON.parse(value);
        return Array.isArray(arr) && arr.length > 0 ? arr.join(', ') : '—';
      } catch { return value; }
    }
    default: return String(value);
  }
}

export function parseFieldValueForSubmit(rawValue, apiType) {
  if (rawValue === null || rawValue === undefined) return null;
  if (apiType === 'BOOLEAN') return (rawValue === true || rawValue === 'true') ? 'true' : 'false';
  if (apiType === 'MULTI_SELECT') {
    if (!Array.isArray(rawValue) || rawValue.length === 0) return null;
    return JSON.stringify(rawValue);
  }
  if (rawValue === '') return null;
  return String(rawValue);
}

export function getDefaultFieldValue(apiType) {
  if (apiType === 'BOOLEAN') return false;
  if (apiType === 'MULTI_SELECT') return [];
  return '';
}

export function getPrimaryFieldValue(record, def) {
  if (!record || !def) return '';
  const primaryField = (def.fields || []).find(f => f.isPrimary && f.status === 'ACTIVE');
  const fallback = `#${(record.id || '').slice(0, 8) || '?'}`;
  if (!record.values) return fallback;
  if (primaryField) return record.values[primaryField.id] || fallback;
  const firstActive = (def.fields || []).find(f => f.status === 'ACTIVE');
  if (firstActive) return record.values[firstActive.id] || fallback;
  return fallback;
}

export function getColumnsFromDef(def, maxCols = 5) {
  const fields = (def?.fields || []).filter(f => f.status === 'ACTIVE');
  return fields.filter(f => !f.isPrimary).slice(0, maxCols);
}

export function extractApiError(err) {
  if (!err) return 'An unexpected error occurred';
  if (typeof err?.body?.message === 'string') return err.body.message;
  if (typeof err?.message === 'string') return err.message;
  return 'An unexpected error occurred';
}

export function paginationMeta(total, page, limit) {
  const l = (!limit || limit <= 0) ? 25 : limit;
  const pages = Math.max(1, Math.ceil(total / l));
  const safePage = Math.min(Math.max(1, page), pages);
  const start = total === 0 ? 0 : (safePage - 1) * l + 1;
  const end = Math.min(safePage * l, total);
  return { pages, safePage, hasPrev: safePage > 1, hasNext: safePage < pages, start, end };
}

// ─────────────────────────────────────────────────────────────────────────────
// CO-007 utility constants and functions (exported for testing)
// ─────────────────────────────────────────────────────────────────────────────

export const CARDINALITY_CONFIG = {
  BELONGS_TO:   { label: 'Single record',                  icon: 'Link',   desc: 'Each record links to one target' },
  HAS_MANY:     { label: 'Multiple records',               icon: 'Layers', desc: 'Each record links to many targets' },
  MANY_TO_MANY: { label: 'Multiple records on both sides', icon: 'Share2', desc: 'Many-to-many relationship' },
};

export const TARGET_TYPE_CONFIG = {
  people:    { label: 'Person',   plural: 'People',    icon: 'Users',       apiModule: 'people' },
  companies: { label: 'Company',  plural: 'Companies', icon: 'Building',    apiModule: 'companies' },
  deals:     { label: 'Deal',     plural: 'Deals',     icon: 'DollarSign',  apiModule: 'deals' },
  tasks:     { label: 'Task',     plural: 'Tasks',     icon: 'CheckSquare', apiModule: 'tasks' },
};

export const TARGET_TYPES = ['people', 'companies', 'deals', 'tasks'];

export function cardinalityLabel(cardinality) {
  return CARDINALITY_CONFIG[cardinality]?.label || cardinality;
}

export function targetTypeLabel(targetType) {
  return TARGET_TYPE_CONFIG[targetType]?.label || targetType;
}

export function isMultiLink(cardinality) {
  return cardinality === 'HAS_MANY' || cardinality === 'MANY_TO_MANY';
}

export function getEntityDisplayName(entity) {
  if (!entity) return '—';
  return entity.displayName || entity.name || entity.title || entity.email || entity.id || '—';
}

// ─── CO-008 utilities ─────────────────────────────────────────────────────────

export const RELATED_RECORDS_DISPLAY_LIMIT = 5;

export function groupLinksByRelDefinition(relationships) {
  return Array.isArray(relationships) ? relationships : [];
}

export function getRelGroupCount(group) {
  return Array.isArray(group?.links) ? group.links.length : 0;
}

export function isEntityUnavailable(link) {
  if (!link) return true;
  if (!link.entity) return true;
  return link.entity.unavailable === true || link.entity.deleted === true;
}

export function formatRelGroupHeader(group) {
  if (!group) return '';
  return group.label || group.reverseLabel || group.targetType || '';
}

export function getRemoveLinkConfirmMsg(displayName, targetType) {
  const ttCfg = TARGET_TYPE_CONFIG[targetType] || {};
  const typeName = ttCfg.label || targetType || 'record';
  return `Remove link to "${displayName}"? This removes the connection, not the ${typeName} itself.`;
}

// ─────────────────────────────────────────────────────────────────────────────
// StatusPill
// ─────────────────────────────────────────────────────────────────────────────

const StatusPill = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;
  return <Pill tone={cfg.tone}>{cfg.label}</Pill>;
};

// ─────────────────────────────────────────────────────────────────────────────
// FieldTypeBadge — compact inline display of a field's type
// ─────────────────────────────────────────────────────────────────────────────

const FieldTypeBadge = ({ type }) => {
  const cfg = getFieldTypeConfig(type);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11,
      color: 'var(--nx-text-muted)', background: 'var(--nx-surface-alt)',
      borderRadius: 4, padding: '2px 6px' }}>
      <NexusIcon name={cfg.icon} size={11} /> {cfg.label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// OptionsEditor — for SELECT / MULTI_SELECT fields
// ─────────────────────────────────────────────────────────────────────────────

const OptionsEditor = ({ options = [], onChange }) => {
  const [draft, setDraft] = useState('');

  const add = () => {
    const val = draft.trim();
    if (!val || options.includes(val)) return;
    onChange([...options, val]);
    setDraft('');
  };

  const remove = (idx) => onChange(options.filter((_, i) => i !== idx));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          className="nx-input"
          style={{ flex: 1 }}
          placeholder="Add option…"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
        />
        <Btn variant="ghost" icon="Plus" onClick={add} disabled={!draft.trim()}>Add</Btn>
      </div>
      {options.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {options.map((opt, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)',
              borderRadius: 6, padding: '3px 8px', fontSize: 12 }}>
              {opt}
              <button
                type="button"
                onClick={() => remove(i)}
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--nx-text-muted)', padding: 0, display: 'flex', lineHeight: 1 }}>
                <NexusIcon name="X" size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
      {options.length === 0 && (
        <p style={{ fontSize: 11, color: 'var(--nx-text-muted)', margin: 0 }}>
          No options yet. Add at least one option.
        </p>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FieldModal — add or edit a field
// Props:
//   mode: 'new' | 'edit'
//   field: existing field object (edit mode) | null (new mode)
//   existingApiNames: string[] — names already in use (to prevent duplicate)
//   onSave(fieldData): called with field payload
//   onClose()
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_FIELD = { label: '', apiName: '', type: 'TEXT', required: false, isUnique: false, isPrimary: false, options: [] };

const FieldModal = ({ mode = 'new', field = null, existingApiNames = [], onSave, onClose }) => {
  const isEdit = mode === 'edit';

  const [form, setForm]    = useState(() => {
    if (isEdit && field) {
      return {
        label:     field.label     || '',
        apiName:   field.apiName   || '',
        type:      field.apiType   || field.type || 'TEXT',
        required:  field.required  ?? false,
        isUnique:  field.isUnique  ?? false,
        isPrimary: field.isPrimary ?? false,
        options:   field.options   || [],
      };
    }
    return { ...EMPTY_FIELD };
  });
  const [errors, setErrors] = useState({});
  const [apiNameManual, setApiNameManual] = useState(isEdit);

  const setField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleLabelChange = (val) => {
    setField('label', val);
    if (!apiNameManual && !isEdit) setField('apiName', toApiName(val));
  };

  const validate = () => {
    const errs = {};
    if (!form.label.trim()) errs.label = 'Label is required';
    const otherNames = existingApiNames.filter(n => isEdit ? n !== field?.apiName : true);
    const apiErr = validateApiName(form.apiName, otherNames);
    if (apiErr && !isEdit) errs.apiName = apiErr;
    if (hasSelectOptions(form.type) && form.options.length === 0)
      errs.options = 'Add at least one option for select fields';
    return errs;
  };

  const handleSave = () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const payload = isEdit
      ? { label: form.label, required: form.required, isPrimary: form.isPrimary, options: form.options }
      : { label: form.label, apiName: form.apiName, type: form.type,
          required: form.required, isUnique: form.isUnique, isPrimary: form.isPrimary,
          options: form.options };

    onSave(payload);
  };

  const title = isEdit ? 'Edit Field' : 'Add Field';

  return (
    <Modal
      title={title}
      icon={getFieldTypeConfig(form.type).icon}
      onClose={onClose}
      width={560}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" icon="Check" onClick={handleSave}>
            {isEdit ? 'Save Changes' : 'Add Field'}
          </Btn>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Label + API Name side-by-side */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className={`nx-form-field${errors.label ? ' is-error' : ''}`}>
            <label className="nx-form-label">
              Label <span className="nx-form-req">*</span>
            </label>
            <input
              className="nx-input"
              autoFocus
              value={form.label}
              onChange={e => handleLabelChange(e.target.value)}
              placeholder="e.g. Contract Value"
            />
            {errors.label && <span className="nx-form-error">{errors.label}</span>}
          </div>

          <div className={`nx-form-field${errors.apiName ? ' is-error' : ''}`}>
            <label className="nx-form-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              API Name {!isEdit && <span className="nx-form-req">*</span>}
              {isEdit && (
                <span style={{ fontSize: 10, color: 'var(--nx-text-muted)',
                  background: 'var(--nx-surface-alt)', borderRadius: 4, padding: '1px 5px' }}>
                  immutable
                </span>
              )}
            </label>
            <input
              className="nx-input"
              value={form.apiName}
              onChange={e => { setApiNameManual(true); setField('apiName', e.target.value); }}
              placeholder="e.g. contract_value"
              readOnly={isEdit}
              style={isEdit ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
            />
            {!isEdit && (
              <span style={{ fontSize: 10, color: 'var(--nx-text-faint)' }}>
                Immutable after creation. a–z, 0–9, _
              </span>
            )}
            {errors.apiName && <span className="nx-form-error">{errors.apiName}</span>}
          </div>
        </div>

        {/* Field Type visual picker */}
        <div className="nx-form-field">
          <label className="nx-form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Type <span className="nx-form-req">*</span>
            {isEdit && (
              <span style={{ fontSize: 10, color: 'var(--nx-text-muted)',
                background: 'var(--nx-surface-alt)', borderRadius: 4, padding: '1px 5px' }}>
                immutable
              </span>
            )}
          </label>
          <FieldTypePicker value={form.type} onChange={v => setField('type', v)} disabled={isEdit} />
        </div>

        {/* Options editor — SELECT / MULTI_SELECT only */}
        {hasSelectOptions(form.type) && (
          <div className={`nx-form-field${errors.options ? ' is-error' : ''}`}>
            <label className="nx-form-label">Options <span className="nx-form-req">*</span></label>
            <OptionsEditor options={form.options} onChange={opts => setField('options', opts)} />
            {errors.options && <span className="nx-form-error">{errors.options}</span>}
          </div>
        )}

        {/* Field properties — styled checkbox cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)',
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
            Field Properties
          </div>
          {[
            { key: 'required',  icon: 'AlertCircle', label: 'Required',
              desc: 'Records must have a value for this field' },
            { key: 'isPrimary', icon: 'Star',         label: 'Primary Field',
              desc: 'Used as the record\'s display name — only one per object' },
            ...(!isEdit ? [{ key: 'isUnique', icon: 'Shield', label: 'Unique',
              desc: 'No two records can share the same value' }] : []),
          ].map(({ key, icon, label, desc }) => (
            <label key={key} style={{
              display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer',
              padding: '10px 12px', borderRadius: 8,
              border: `1.5px solid ${form[key] ? 'var(--nx-accent)' : 'var(--nx-border)'}`,
              background: form[key] ? 'var(--nx-accent-tint-2)' : 'transparent',
              transition: 'border-color 0.1s, background 0.1s',
            }}>
              <div style={{ position: 'relative', width: 16, height: 16, flexShrink: 0, marginTop: 2 }}>
                <input type="checkbox" checked={form[key]}
                  onChange={e => setField(key, e.target.checked)}
                  style={{ opacity: 0, position: 'absolute', inset: 0, cursor: 'pointer', margin: 0, width: '100%', height: '100%' }} />
                <div style={{
                  width: 16, height: 16, borderRadius: 4, pointerEvents: 'none',
                  background: form[key] ? 'var(--nx-accent)' : 'transparent',
                  border: `1.5px solid ${form[key] ? 'var(--nx-accent)' : 'var(--nx-border)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.1s',
                }}>
                  {form[key] && <NexusIcon name="Check" size={10} style={{ color: '#fff' }} />}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600,
                  color: form[key] ? 'var(--nx-accent)' : 'var(--nx-text)' }}>{label}</div>
                <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 1 }}>{desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FieldTypePicker — visual 5×2 grid replacing the plain <select> for field type
// ─────────────────────────────────────────────────────────────────────────────

const FieldTypePicker = ({ value, onChange, disabled }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
    {CO_FIELD_TYPES.map(t => {
      const sel = value === t.value;
      return (
        <button
          key={t.value}
          type="button"
          title={t.desc}
          disabled={disabled && !sel}
          onClick={() => !disabled && onChange(t.value)}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 5, padding: '10px 4px 8px',
            borderRadius: 8,
            border: `1.5px solid ${sel ? 'var(--nx-accent)' : 'var(--nx-border)'}`,
            background: sel ? 'var(--nx-accent-tint-2)' : 'var(--nx-surface)',
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled && !sel ? 0.45 : 1,
            transition: 'border-color 0.1s, background 0.1s',
          }}
        >
          <NexusIcon name={t.icon} size={17}
            style={{ color: sel ? 'var(--nx-accent)' : 'var(--nx-text-muted)' }} />
          <span style={{
            fontSize: 10, fontWeight: sel ? 700 : 500,
            color: sel ? 'var(--nx-accent)' : 'var(--nx-text-muted)',
            textAlign: 'center', lineHeight: 1.3,
          }}>{t.label}</span>
        </button>
      );
    })}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// IconPicker — grid of icons to choose from
// ─────────────────────────────────────────────────────────────────────────────

const IconPicker = ({ value, onChange }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 4 }}>
    {CO_ICONS.map(icon => (
      <button
        key={icon}
        type="button"
        title={icon}
        onClick={() => onChange(icon)}
        style={{
          width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 6, border: `1px solid ${value === icon ? 'var(--nx-accent)' : 'var(--nx-border)'}`,
          background: value === icon ? 'var(--nx-accent-tint-2)' : 'transparent',
          cursor: 'pointer', color: value === icon ? 'var(--nx-accent)' : 'var(--nx-text-muted)',
          transition: 'all 0.12s',
        }}
      >
        <NexusIcon name={icon} size={16} />
      </button>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// InlineFieldRow — compact row in the wizard field list
// ─────────────────────────────────────────────────────────────────────────────

const InlineFieldRow = ({ field, index, onEdit, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) => (
  <div style={{ display: 'flex', alignItems: 'center', padding: '8px 10px',
    background: 'var(--nx-surface)', border: '1px solid var(--nx-border)',
    borderRadius: 8, gap: 10 }}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <button type="button" onClick={onMoveUp} disabled={isFirst}
        style={{ background: 'none', border: 'none', cursor: isFirst ? 'default' : 'pointer',
          opacity: isFirst ? 0.3 : 0.6, padding: 2, color: 'var(--nx-text-muted)', lineHeight: 1 }}>
        <NexusIcon name="ChevronUp" size={11} />
      </button>
      <button type="button" onClick={onMoveDown} disabled={isLast}
        style={{ background: 'none', border: 'none', cursor: isLast ? 'default' : 'pointer',
          opacity: isLast ? 0.3 : 0.6, padding: 2, color: 'var(--nx-text-muted)', lineHeight: 1 }}>
        <NexusIcon name="ChevronDown" size={11} />
      </button>
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>{field.label}</span>
        {field.isPrimary && (
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--nx-accent)',
            background: 'var(--nx-accent-tint-2)', borderRadius: 4, padding: '1px 5px' }}>
            PRIMARY
          </span>
        )}
        {field.required && (
          <span style={{ fontSize: 10, color: 'var(--nx-text-muted)' }}>required</span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
        <span style={{ fontSize: 11, color: 'var(--nx-text-faint)', fontFamily: 'monospace' }}>{field.apiName}</span>
        <FieldTypeBadge type={field.type} />
      </div>
    </div>
    <div style={{ display: 'flex', gap: 2 }}>
      <IconBtn icon="Edit2" title="Edit field" onClick={onEdit} />
      <IconBtn icon="Trash2" title="Remove field" onClick={onRemove}
        style={{ color: 'var(--nx-text-muted)' }} />
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// CreateObjectModal — 3-step wizard
// ─────────────────────────────────────────────────────────────────────────────

const WIZARD_STEPS = [
  { id: 1, label: 'Basics' },
  { id: 2, label: 'Fields' },
  { id: 3, label: 'Review' },
];

const INIT_BASICS = { singularName: '', pluralName: '', apiName: '', description: '', icon: 'Database' };

const CreateObjectModal = ({ existingApiNames = [], onCreated, onClose }) => {
  const [step, setStep]             = useState(1);
  const [basics, setBasics]         = useState(INIT_BASICS);
  const [basicsErrors, setBErrors]  = useState({});
  const [apiNameManual, setApiNameManual] = useState(false);
  const [wizardFields, setWFields]  = useState([]);
  const [editingIdx, setEditingIdx] = useState(null);  // index into wizardFields being edited
  const [addingField, setAddingField] = useState(false);
  const [wizardRels, setWRels]      = useState([]);    // staged relationships (pre-save)
  const [editingRelIdx, setEditRelIdx] = useState(null);
  const [addingRel, setAddingRel]   = useState(false);
  const [saving, setSaving]         = useState(false);
  const workspace = useWorkspace();
  const wsId = workspace?.id;
  const toast = useToast();

  const setBasic = (key, val) => setBasics(prev => ({ ...prev, [key]: val }));

  const handleNameChange = (val) => {
    setBasic('singularName', val);
    if (!apiNameManual) {
      setBasic('apiName', toApiName(val));
      setBasic('pluralName', val ? val + 's' : '');
    }
  };

  const validateBasics = () => {
    const errs = {};
    if (!basics.singularName.trim()) errs.singularName = 'Object name is required';
    const otherNames = existingApiNames;
    const apiErr = validateApiName(basics.apiName, otherNames);
    if (apiErr) errs.apiName = apiErr;
    return errs;
  };

  const goToStep2 = () => {
    const errs = validateBasics();
    setBErrors(errs);
    if (Object.keys(errs).length) return;
    setStep(2);
  };

  const goToStep3 = () => setStep(3);
  const goBack    = () => setStep(s => s - 1);

  const fieldApiNames = wizardFields.map(f => f.apiName);

  const handleAddField = (fieldData) => {
    setWFields(prev => [...prev, fieldData]);
    setAddingField(false);
  };

  const handleEditField = (fieldData) => {
    setWFields(prev => prev.map((f, i) => i === editingIdx ? { ...f, ...fieldData } : f));
    setEditingIdx(null);
  };

  const handleRemoveField = (idx) => {
    setWFields(prev => prev.filter((_, i) => i !== idx));
  };

  const handleMoveField = (idx, dir) => {
    setWFields(prev => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const handleCreate = async () => {
    if (!wsId || saving) return;
    setSaving(true);
    try {
      const dto = {
        singularName: basics.singularName.trim(),
        pluralName:   basics.pluralName.trim() || basics.singularName.trim() + 's',
        apiName:      basics.apiName.trim(),
        description:  basics.description.trim() || undefined,
        icon:         basics.icon,
        fields:       wizardFields,
      };
      const created = await coApi.createDef(wsId, dto);

      // Create staged relationships (fire-and-forget per item; warn on partial failure)
      const relErrors = [];
      for (const rel of wizardRels) {
        try {
          await coApi.createRelationship(wsId, created.id, rel);
        } catch {
          relErrors.push(rel.label);
        }
      }
      if (relErrors.length) {
        toast({ tone: 'warning', icon: 'AlertCircle',
          title: `"${created.singularName}" created (some relationships failed)`,
          desc: `Failed to add: ${relErrors.join(', ')}` });
      } else {
        toast({ tone: 'success', icon: 'Check', title: `"${created.singularName}" created` });
      }
      onCreated(created);
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Failed to create object';
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Creation failed', desc: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleAddRel  = (dto) => { setWRels(prev => [...prev, dto]); setAddingRel(false); };
  const handleEditRel = (dto) => {
    setWRels(prev => prev.map((r, i) => i === editingRelIdx ? { ...r, ...dto } : r));
    setEditRelIdx(null);
  };
  const handleRemoveRel = (idx) => setWRels(prev => prev.filter((_, i) => i !== idx));

  const primaryCount = wizardFields.filter(f => f.isPrimary).length;

  const stepFooter = (
    <>
      {step === 1 && (
        <>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" icon="ArrowRight" onClick={goToStep2}>Next: Fields</Btn>
        </>
      )}
      {step === 2 && (
        <>
          <Btn variant="ghost" onClick={goBack}>Back</Btn>
          <Btn variant="primary" icon="ArrowRight" onClick={goToStep3}>Review</Btn>
        </>
      )}
      {step === 3 && (
        <>
          <Btn variant="ghost" onClick={goBack}>Back</Btn>
          <Btn variant="primary" icon="Plus" onClick={handleCreate} disabled={saving}>
            {saving ? 'Creating…' : 'Create Object'}
          </Btn>
        </>
      )}
    </>
  );

  const stepIndicator = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      {WIZARD_STEPS.map((s, i) => (
        <div key={s.id} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
              background: step === s.id ? 'var(--nx-accent)' : step > s.id ? '#22c55e' : 'var(--nx-surface-alt)',
              color: step >= s.id ? '#fff' : 'var(--nx-text-muted)',
            }}>
              {step > s.id ? <NexusIcon name="Check" size={12} /> : s.id}
            </div>
            <span style={{ fontSize: 12, fontWeight: step === s.id ? 700 : 400,
              color: step === s.id ? 'var(--nx-text)' : 'var(--nx-text-muted)' }}>
              {s.label}
            </span>
          </div>
          {i < WIZARD_STEPS.length - 1 && (
            <div style={{ width: 32, height: 1, background: 'var(--nx-border)', margin: '0 8px' }} />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <>
      <Modal
        title="New Custom Object"
        icon="Database"
        onClose={onClose}
        width={640}
        footer={stepFooter}
        subHeader={stepIndicator}
      >
        {/* ── Step 1: Basics ── */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className={`nx-form-field${basicsErrors.singularName ? ' is-error' : ''}`}>
              <label className="nx-form-label">
                Singular Name <span className="nx-form-req">*</span>
              </label>
              <input
                className="nx-input"
                autoFocus
                value={basics.singularName}
                onChange={e => handleNameChange(e.target.value)}
                placeholder="e.g. Contract, Subscription, Permit"
              />
              <span style={{ fontSize: 11, color: 'var(--nx-text-faint)' }}>
                How one record is referred to (e.g. "Contract")
              </span>
              {basicsErrors.singularName && <span className="nx-form-error">{basicsErrors.singularName}</span>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="nx-form-field">
                <label className="nx-form-label">Plural Name</label>
                <input
                  className="nx-input"
                  value={basics.pluralName}
                  onChange={e => setBasic('pluralName', e.target.value)}
                  placeholder="e.g. Contracts"
                />
              </div>

              <div className={`nx-form-field${basicsErrors.apiName ? ' is-error' : ''}`}>
                <label className="nx-form-label">
                  API Name <span className="nx-form-req">*</span>
                </label>
                <input
                  className="nx-input"
                  value={basics.apiName}
                  onChange={e => { setApiNameManual(true); setBasic('apiName', e.target.value); }}
                  placeholder="e.g. contract"
                />
                {basicsErrors.apiName
                  ? <span className="nx-form-error">{basicsErrors.apiName}</span>
                  : <span style={{ fontSize: 11, color: 'var(--nx-text-faint)' }}>
                      Cannot be changed after creation
                    </span>
                }
              </div>
            </div>

            <div className="nx-form-field">
              <label className="nx-form-label">Description</label>
              <textarea
                className="nx-input nx-textarea"
                rows={2}
                value={basics.description}
                onChange={e => setBasic('description', e.target.value)}
                placeholder="What this object represents in your business…"
              />
            </div>

            <div className="nx-form-field">
              <label className="nx-form-label">Icon</label>
              <IconPicker value={basics.icon} onChange={v => setBasic('icon', v)} />
            </div>
          </div>
        )}

        {/* ── Step 2: Fields ── */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)' }}>Object Fields</div>
                <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                  {wizardFields.length === 0
                    ? 'No fields yet — you can also add fields after creating the object.'
                    : `${wizardFields.length} field${wizardFields.length !== 1 ? 's' : ''}${primaryCount === 0 ? ' — consider marking one as Primary' : ''}`}
                </div>
              </div>
              <Btn variant="ghost" icon="Plus" onClick={() => setAddingField(true)}>Add Field</Btn>
            </div>

            {wizardFields.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {wizardFields.map((f, i) => (
                  <InlineFieldRow
                    key={i}
                    field={f}
                    index={i}
                    onEdit={() => setEditingIdx(i)}
                    onRemove={() => handleRemoveField(i)}
                    onMoveUp={() => handleMoveField(i, -1)}
                    onMoveDown={() => handleMoveField(i, 1)}
                    isFirst={i === 0}
                    isLast={i === wizardFields.length - 1}
                  />
                ))}
              </div>
            )}

            {wizardFields.length === 0 && (
              <div style={{ textAlign: 'center', padding: '32px 16px', border: '1px dashed var(--nx-border)',
                borderRadius: 10, color: 'var(--nx-text-muted)' }}>
                <NexusIcon name="Columns" size={24} style={{ marginBottom: 8, opacity: 0.4 }} />
                <div style={{ fontSize: 13 }}>No fields defined yet.</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  You can skip this and add fields after creating the object.
                </div>
              </div>
            )}

            {primaryCount === 0 && wizardFields.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--nx-warning)', background: 'var(--nx-warning-tint, #FEF3C7)',
                borderRadius: 6, padding: '8px 12px', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <NexusIcon name="AlertCircle" size={13} style={{ marginTop: 1, flexShrink: 0 }} />
                No Primary Field set. The primary field is used as the record display name.
                Edit a field to mark it as primary.
              </div>
            )}

            {/* ── Relationships subsection ── */}
            <div style={{ borderTop: '1px solid var(--nx-border)', paddingTop: 16, marginTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)' }}>Object Relationships</div>
                  <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                    {wizardRels.length === 0
                      ? 'Link records to People, Companies, Deals, or Tasks.'
                      : `${wizardRels.length} relationship${wizardRels.length !== 1 ? 's' : ''} staged`}
                  </div>
                </div>
                <Btn variant="ghost" icon="Plus" onClick={() => setAddingRel(true)}>Add Relationship</Btn>
              </div>

              {wizardRels.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {wizardRels.map((r, i) => (
                    <InlineRelRow
                      key={i}
                      rel={r}
                      onEdit={() => setEditRelIdx(i)}
                      onRemove={() => handleRemoveRel(i)}
                    />
                  ))}
                </div>
              )}

              {wizardRels.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px 16px',
                  border: '1px dashed var(--nx-border)', borderRadius: 10,
                  color: 'var(--nx-text-muted)' }}>
                  <NexusIcon name="Share2" size={20} style={{ marginBottom: 6, opacity: 0.4 }} />
                  <div style={{ fontSize: 12 }}>Optional — add relationships after creating the object if preferred.</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Step 3: Review ── */}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)',
              borderRadius: 10, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10,
                  background: 'var(--nx-accent-tint-2)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <NexusIcon name={basics.icon} size={20} style={{ color: 'var(--nx-accent)' }} />
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--nx-text)' }}>
                    {basics.singularName}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', display: 'flex', gap: 8 }}>
                    <code style={{ fontSize: 11 }}>{basics.apiName}</code>
                    <StatusPill status="DRAFT" />
                  </div>
                </div>
              </div>
              {basics.description && (
                <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', margin: 0 }}>
                  {basics.description}
                </p>
              )}
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                {wizardFields.length} Field{wizardFields.length !== 1 ? 's' : ''}
                {wizardRels.length > 0 && ` · ${wizardRels.length} Relationship${wizardRels.length !== 1 ? 's' : ''}`}
              </div>
              {wizardFields.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {wizardFields.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8,
                      padding: '6px 10px', background: 'var(--nx-surface)',
                      border: '1px solid var(--nx-border)', borderRadius: 6 }}>
                      <NexusIcon name={getFieldTypeConfig(f.type).icon} size={13}
                        style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)', flex: 1 }}>
                        {f.label}
                      </span>
                      <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--nx-text-muted)' }}>
                        {f.apiName}
                      </span>
                      {f.isPrimary && <Pill tone="blue">Primary</Pill>}
                      {f.required && <Pill tone="gray">Required</Pill>}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--nx-text-muted)', fontStyle: 'italic' }}>
                  No fields — you can add them after creating the object.
                </div>
              )}
            </div>

            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)',
              background: 'var(--nx-surface-alt)', borderRadius: 6, padding: '10px 12px' }}>
              The object will be created in <strong>Draft</strong> status.
              You can add more fields and publish it from the settings tab.
            </div>
          </div>
        )}
      </Modal>

      {/* Field modal for wizard field editor */}
      {addingField && (
        <FieldModal
          mode="new"
          existingApiNames={fieldApiNames}
          onSave={handleAddField}
          onClose={() => setAddingField(false)}
        />
      )}
      {editingIdx !== null && (
        <FieldModal
          mode="edit"
          field={wizardFields[editingIdx]}
          existingApiNames={fieldApiNames}
          onSave={handleEditField}
          onClose={() => setEditingIdx(null)}
        />
      )}

      {/* Relationship modals for wizard (staged — no API call until object is created) */}
      {addingRel && (
        <RelationshipModal
          mode="new"
          staged={true}
          onSave={handleAddRel}
          onClose={() => setAddingRel(false)}
        />
      )}
      {editingRelIdx !== null && (
        <RelationshipModal
          mode="edit"
          rel={wizardRels[editingRelIdx]}
          staged={true}
          onSave={handleEditRel}
          onClose={() => setEditRelIdx(null)}
        />
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// OverviewTab
// ─────────────────────────────────────────────────────────────────────────────

const OverviewTab = ({ def }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
    {/* Description */}
    <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)',
      borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Description</div>
      {def.description ? (
        <p style={{ fontSize: 14, color: 'var(--nx-text)', lineHeight: 1.6, margin: 0 }}>
          {def.description}
        </p>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--nx-text-faint)', fontStyle: 'italic', margin: 0 }}>
          No description — add one in Settings.
        </p>
      )}
    </div>

    {/* Properties grid */}
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Properties</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {[
          { label: 'Singular Name', value: def.singularName },
          { label: 'Plural Name',   value: def.pluralName || def.singularName + 's' },
          { label: 'API Name',      value: def.apiName, mono: true },
          { label: 'Status',        value: <StatusPill status={def.status} /> },
          { label: 'Active Fields', value: (def.fields || []).filter(f => f.status === 'ACTIVE').length },
          { label: 'Created',       value: fmtAbsTime(def.createdAt) },
          { label: 'Last Updated',  value: fmtAbsTime(def.updatedAt) },
        ].map(({ label, value, mono }) => (
          <div key={label} style={{ background: 'var(--nx-surface-alt)', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--nx-text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 }}>
              {label}
            </div>
            {typeof value === 'object' && value !== null && isValidElement(value)
              ? value
              : <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)',
                  fontFamily: mono ? 'monospace' : undefined }}>
                  {value ?? '—'}
                </div>
            }
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// FieldsTab — manage fields on an existing object
// ─────────────────────────────────────────────────────────────────────────────

const FieldsTab = ({ def, wsId, canManage, onDefUpdate, toast }) => {
  const [fields, setFields]           = useState(def.fields || []);
  const [addingField, setAddingField]   = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [confirm, setConfirm]           = useState(null);

  // Refresh fields list whenever def changes
  useEffect(() => { setFields(def.fields || []); }, [def]);

  const activeApiNames = fields.filter(f => f.status === 'ACTIVE').map(f => f.apiName);

  const handleAdd = async (fieldData) => {
    try {
      const created = await coApi.addField(wsId, def.id, fieldData);
      setFields(prev => [...prev, created]);
      setAddingField(false);
      toast({ tone: 'success', icon: 'Check', title: `Field "${created.label}" added` });
      onDefUpdate({ ...def, fields: [...fields, created] });
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Failed to add field';
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Add failed', desc: msg });
    }
  };

  const handleEdit = async (fieldData) => {
    const fieldId = editingField.id;
    try {
      const updated = await coApi.updateField(wsId, def.id, fieldId, fieldData);
      const next = fields.map(f => f.id === fieldId ? updated : f);
      setFields(next);
      setEditingField(null);
      toast({ tone: 'success', icon: 'Check', title: `Field "${updated.label}" updated` });
      onDefUpdate({ ...def, fields: next });
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Failed to update field';
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Update failed', desc: msg });
    }
  };

  const handleArchive = async (field) => {
    try {
      const archived = await coApi.archiveField(wsId, def.id, field.id);
      const next = fields.map(f => f.id === field.id ? { ...f, status: 'ARCHIVED' } : f);
      setFields(next);
      setConfirm(null);
      toast({ tone: 'success', icon: 'Check', title: `Field "${field.label}" archived` });
      onDefUpdate({ ...def, fields: next });
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Failed to archive field';
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Archive failed', desc: msg });
      setConfirm(null);
    }
  };

  const handleMoveField = async (field, dir) => {
    const idx = fields.findIndex(f => f.id === field.id);
    const target = idx + dir;
    if (target < 0 || target >= fields.length) return;
    // Capture sortOrder values BEFORE swapping the array — reading after the swap
    // returns each field's own (unchanged) value because the objects are the same references.
    const aOrder = fields[idx].sortOrder;
    const bOrder = fields[target].sortOrder;
    const next = [...fields];
    [next[idx], next[target]] = [next[target], next[idx]];
    // Assign the swapped sortOrder values so both positions are correctly persisted.
    next[idx].sortOrder = aOrder;
    next[target].sortOrder = bOrder;
    setFields(next);
    try {
      await Promise.all([
        coApi.updateField(wsId, def.id, next[idx].id,     { sortOrder: aOrder }),
        coApi.updateField(wsId, def.id, next[target].id,  { sortOrder: bOrder }),
      ]);
    } catch {
      setFields(fields);
    }
  };

  const activeFields   = fields.filter(f => f.status === 'ACTIVE');
  const archivedFields = fields.filter(f => f.status === 'ARCHIVED');

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>Fields</div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 1 }}>
              {activeFields.length} active{archivedFields.length > 0 ? `, ${archivedFields.length} archived` : ''}
            </div>
          </div>
          {canManage && (
            <Btn variant="primary" icon="Plus" onClick={() => setAddingField(true)}>Add Field</Btn>
          )}
        </div>

        {activeFields.length === 0 ? (
          <EmptyState
            icon="Columns"
            title="No active fields"
            desc="Add fields to define the schema for this object."
            action={canManage
              ? <Btn variant="primary" icon="Plus" onClick={() => setAddingField(true)}>Add Field</Btn>
              : null}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1,
            border: '1px solid var(--nx-border)', borderRadius: 10, overflow: 'hidden' }}>
            {activeFields.map((field, i) => (
              <div key={field.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '11px 14px',
                background: 'var(--nx-surface)',
                borderBottom: i < activeFields.length - 1 ? '1px solid var(--nx-border)' : 'none',
              }}>
                {canManage && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <button type="button" onClick={() => handleMoveField(field, -1)}
                      disabled={i === 0}
                      style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer',
                        opacity: i === 0 ? 0.2 : 0.5, padding: '1px', color: 'var(--nx-text-muted)', lineHeight: 1 }}>
                      <NexusIcon name="ChevronUp" size={12} />
                    </button>
                    <button type="button" onClick={() => handleMoveField(field, 1)}
                      disabled={i === activeFields.length - 1}
                      style={{ background: 'none', border: 'none',
                        cursor: i === activeFields.length - 1 ? 'default' : 'pointer',
                        opacity: i === activeFields.length - 1 ? 0.2 : 0.5, padding: '1px',
                        color: 'var(--nx-text-muted)', lineHeight: 1 }}>
                      <NexusIcon name="ChevronDown" size={12} />
                    </button>
                  </div>
                )}
                <NexusIcon name={getFieldTypeConfig(field.apiType || field.type).icon} size={15}
                  style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>
                      {field.label}
                    </span>
                    {field.isPrimary && <Pill tone="blue">Primary</Pill>}
                    {field.required && <Pill tone="gray">Required</Pill>}
                    {field.isUnique && <Pill tone="purple">Unique</Pill>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <code style={{ fontSize: 11, color: 'var(--nx-text-faint)' }}>{field.apiName}</code>
                    <FieldTypeBadge type={field.apiType || field.type} />
                  </div>
                </div>
                {canManage && (
                  <div style={{ display: 'flex', gap: 2 }}>
                    <IconBtn icon="Edit2" title="Edit field" onClick={() => setEditingField(field)} />
                    <IconBtn icon="Archive" title="Archive field"
                      onClick={() => setConfirm({ type: 'archive-field', field })} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {archivedFields.length > 0 && (
          <details>
            <summary style={{ fontSize: 12, color: 'var(--nx-text-muted)', cursor: 'pointer',
              userSelect: 'none', padding: '4px 0' }}>
              {archivedFields.length} archived field{archivedFields.length !== 1 ? 's' : ''}
            </summary>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 1,
              border: '1px solid var(--nx-border)', borderRadius: 10, overflow: 'hidden', opacity: 0.6 }}>
              {archivedFields.map((field, i) => (
                <div key={field.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 14px', background: 'var(--nx-surface)',
                  borderBottom: i < archivedFields.length - 1 ? '1px solid var(--nx-border)' : 'none' }}>
                  <NexusIcon name={getFieldTypeConfig(field.apiType || field.type).icon} size={14}
                    style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 13, color: 'var(--nx-text-muted)',
                      textDecoration: 'line-through' }}>{field.label}</span>
                    <code style={{ marginLeft: 6, fontSize: 11, color: 'var(--nx-text-faint)' }}>
                      {field.apiName}
                    </code>
                  </div>
                  <Pill tone="gray">Archived</Pill>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {addingField && (
        <FieldModal
          mode="new"
          existingApiNames={activeApiNames}
          onSave={handleAdd}
          onClose={() => setAddingField(false)}
        />
      )}
      {editingField && (
        <FieldModal
          mode="edit"
          field={editingField}
          existingApiNames={activeApiNames}
          onSave={handleEdit}
          onClose={() => setEditingField(null)}
        />
      )}
      {confirm?.type === 'archive-field' && (
        <ConfirmDialog
          title={`Archive "${confirm.field.label}"?`}
          desc="This field will be hidden from new records. Existing field values are preserved. Archived fields can be viewed but not re-activated."
          confirmLabel="Archive Field"
          onConfirm={() => handleArchive(confirm.field)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SettingsTab — edit and lifecycle management for an object
// ─────────────────────────────────────────────────────────────────────────────

// ─── DrawerLayoutConfig ── admin UI for configuring the record drawer ────────
// Generates a drawerConfig JSON blob and saves it via PATCH on the def.
const DrawerLayoutConfig = ({ def, wsId, canManage, onDefUpdate, toast }) => {
  const activeFields = (def.fields || []).filter(f => f.status === 'ACTIVE');
  const initConfig = () => {
    const c = def.drawerConfig || {};
    return {
      summaryFieldIds: c.summaryFieldIds || [],
      sections: (c.sections || []).map(s => ({ ...s, fieldIds: s.fieldIds || [] })),
    };
  };
  const [cfg, setCfg]         = useState(initConfig);
  const [saving, setSaving]   = useState(false);
  const [cfgDirty, setCfgDirty] = useState(false);

  const updateCfg = (fn) => { setCfg(prev => { const next = fn(prev); return next; }); setCfgDirty(true); };

  const handleSumToggle = (fieldId) => {
    updateCfg(prev => {
      const ids = prev.summaryFieldIds.includes(fieldId)
        ? prev.summaryFieldIds.filter(id => id !== fieldId)
        : prev.summaryFieldIds.length < 3
          ? [...prev.summaryFieldIds, fieldId]
          : prev.summaryFieldIds;
      return { ...prev, summaryFieldIds: ids };
    });
  };

  const addSection = () => {
    updateCfg(prev => ({
      ...prev,
      sections: [...prev.sections, { id: `s_${Date.now()}`, label: 'New Section', columns: 1, fieldIds: [] }],
    }));
  };

  const removeSection = (sid) => {
    updateCfg(prev => ({ ...prev, sections: prev.sections.filter(s => s.id !== sid) }));
  };

  const updateSection = (sid, key, val) => {
    updateCfg(prev => ({
      ...prev,
      sections: prev.sections.map(s => s.id === sid ? { ...s, [key]: val } : s),
    }));
  };

  const moveSectionUp = (idx) => {
    if (idx === 0) return;
    updateCfg(prev => {
      const arr = [...prev.sections];
      [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
      return { ...prev, sections: arr };
    });
  };

  const moveSectionDown = (idx) => {
    updateCfg(prev => {
      if (idx >= prev.sections.length - 1) return prev;
      const arr = [...prev.sections];
      [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      return { ...prev, sections: arr };
    });
  };

  const toggleFieldInSection = (sid, fieldId) => {
    updateCfg(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.id !== sid) return s;
        const ids = s.fieldIds.includes(fieldId)
          ? s.fieldIds.filter(id => id !== fieldId)
          : [...s.fieldIds, fieldId];
        return { ...s, fieldIds: ids };
      }),
    }));
  };

  const handleSaveLayout = async () => {
    if (!cfgDirty || saving) return;
    setSaving(true);
    // Strip any fieldIds that no longer exist (safety guard)
    const activeIds = new Set(activeFields.map(f => f.id));
    const sanitized = {
      summaryFieldIds: cfg.summaryFieldIds.filter(id => activeIds.has(id)),
      sections: cfg.sections.map(s => ({ ...s, fieldIds: s.fieldIds.filter(id => activeIds.has(id)) })),
    };
    try {
      const updated = await coApi.updateDef(wsId, def.id, { drawerConfig: sanitized });
      onDefUpdate(updated);
      setCfgDirty(false);
      toast({ tone: 'success', icon: 'Check', title: 'Drawer layout saved' });
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Save failed';
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Save failed', desc: msg });
    } finally {
      setSaving(false);
    }
  };

  const panelStyle = {
    border: '1px solid var(--nx-border)', borderRadius: 8,
    overflow: 'hidden', marginBottom: 8,
  };
  const panelHeadStyle = {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 12px', background: 'var(--nx-surface)',
    borderBottom: '1px solid var(--nx-border)',
    fontSize: 12, fontWeight: 600, color: 'var(--nx-text)',
  };

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14,
        paddingBottom: 8, borderBottom: '1px solid var(--nx-border)' }}>
        Drawer Layout
      </div>
      <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 14 }}>
        Configure what users see when they open a {def.singularName} record.
        Changes are saved independently of the General settings above.
      </div>

      {/* Summary fields */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text-soft)', marginBottom: 6 }}>
          Summary Fields <span style={{ fontWeight: 400, color: 'var(--nx-text-faint)' }}>— shown in the record header (up to 3)</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {activeFields.map(f => {
            const on = cfg.summaryFieldIds.includes(f.id);
            const maxed = cfg.summaryFieldIds.length >= 3 && !on;
            return (
              <button key={f.id} type="button"
                disabled={!canManage || maxed}
                onClick={() => handleSumToggle(f.id)}
                style={{
                  padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: canManage && !maxed ? 'pointer' : 'default',
                  border: `1px solid ${on ? 'var(--nx-accent)' : 'var(--nx-border)'}`,
                  background: on ? 'var(--nx-accent-tint)' : 'var(--nx-surface)',
                  color: on ? 'var(--nx-accent)' : maxed ? 'var(--nx-text-faint)' : 'var(--nx-text)',
                  opacity: maxed ? 0.45 : 1,
                }}>
                <NexusIcon name={getFieldTypeConfig(f.apiType || f.type).icon} size={10} />
                {' '}{f.label}
              </button>
            );
          })}
          {activeFields.length === 0 && (
            <span style={{ fontSize: 12, color: 'var(--nx-text-faint)', fontStyle: 'italic' }}>
              No active fields available.
            </span>
          )}
        </div>
      </div>

      {/* Sections */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text-soft)', marginBottom: 8 }}>
          Overview Sections
        </div>
        {cfg.sections.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--nx-text-faint)', fontStyle: 'italic', marginBottom: 8 }}>
            No sections configured — all fields will be shown in a default "Details" section.
          </div>
        )}
        {cfg.sections.map((section, idx) => (
          <div key={section.id} style={panelStyle}>
            <div style={panelHeadStyle}>
              <NexusIcon name="LayoutGrid" size={13} style={{ color: 'var(--nx-text-muted)' }} />
              <input
                className="nx-input"
                value={section.label}
                onChange={e => updateSection(section.id, 'label', e.target.value)}
                disabled={!canManage}
                style={{ flex: 1, padding: '2px 6px', fontSize: 12, height: 26 }}
                placeholder="Section label"
              />
              <select
                value={section.columns || 1}
                onChange={e => updateSection(section.id, 'columns', Number(e.target.value))}
                disabled={!canManage}
                style={{ fontSize: 12, padding: '2px 4px', border: '1px solid var(--nx-border)',
                  borderRadius: 4, background: 'var(--nx-surface)', color: 'var(--nx-text)', cursor: canManage ? 'pointer' : 'default' }}>
                <option value={1}>1 column</option>
                <option value={2}>2 columns</option>
              </select>
              {canManage && (
                <div style={{ display: 'flex', gap: 2 }}>
                  <IconBtn icon="ChevronUp" size="xs" disabled={idx === 0} onClick={() => moveSectionUp(idx)} aria-label="Move up" />
                  <IconBtn icon="ChevronDown" size="xs" disabled={idx === cfg.sections.length - 1} onClick={() => moveSectionDown(idx)} aria-label="Move down" />
                  <IconBtn icon="Trash2" size="xs" style={{ color: 'var(--nx-danger)' }} onClick={() => removeSection(section.id)} aria-label="Remove section" />
                </div>
              )}
            </div>
            <div style={{ padding: '8px 12px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {activeFields.map(f => {
                const on = section.fieldIds.includes(f.id);
                return (
                  <button key={f.id} type="button"
                    disabled={!canManage}
                    onClick={() => toggleFieldInSection(section.id, f.id)}
                    style={{
                      padding: '3px 8px', borderRadius: 16, fontSize: 11, fontWeight: 600,
                      cursor: canManage ? 'pointer' : 'default',
                      border: `1px solid ${on ? 'var(--nx-accent)' : 'var(--nx-border)'}`,
                      background: on ? 'var(--nx-accent-tint)' : 'var(--nx-surface-alt)',
                      color: on ? 'var(--nx-accent)' : 'var(--nx-text-muted)',
                    }}>
                    <NexusIcon name={getFieldTypeConfig(f.apiType || f.type).icon} size={9} />
                    {' '}{f.label}
                  </button>
                );
              })}
              {activeFields.length === 0 && (
                <span style={{ fontSize: 12, color: 'var(--nx-text-faint)', fontStyle: 'italic' }}>
                  No active fields available.
                </span>
              )}
            </div>
          </div>
        ))}
        {canManage && (
          <button type="button" onClick={addSection}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
              color: 'var(--nx-accent)', background: 'none', border: '1px dashed var(--nx-accent)',
              borderRadius: 6, padding: '6px 12px', cursor: 'pointer', width: '100%',
              justifyContent: 'center', marginTop: 4 }}>
            <NexusIcon name="Plus" size={13} />
            Add Section
          </button>
        )}
      </div>

      {canManage && cfgDirty && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
          <Btn variant="primary" icon="Check" onClick={handleSaveLayout} disabled={saving}>
            {saving ? 'Saving…' : 'Save Drawer Layout'}
          </Btn>
        </div>
      )}
    </div>
  );
};
// ────────────────────────────────────────────────────────────────────────────

const SettingsTab = ({ def, wsId, canManage, onDefUpdate, onDefDelete, onDirtyChange, toast }) => {
  const [form, setForm] = useState({
    singularName: def.singularName || '',
    pluralName:   def.pluralName   || '',
    description:  def.description  || '',
    icon:         def.icon         || 'Database',
  });
  const [saving, setSaving]   = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [dirty, setDirty]     = useState(false);

  const markDirty = (val) => { setDirty(val); onDirtyChange?.(val); };
  const setField = (key, val) => { setForm(prev => ({ ...prev, [key]: val })); markDirty(true); };

  const handleSave = async () => {
    if (!dirty || saving) return;
    if (!form.singularName.trim()) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Name required', desc: 'Singular name cannot be empty.' });
      return;
    }
    setSaving(true);
    try {
      const updated = await coApi.updateDef(wsId, def.id, {
        singularName: form.singularName.trim() || undefined,
        pluralName:   form.pluralName.trim()   || undefined,
        description:  form.description.trim()  || undefined,
        icon:         form.icon,
      });
      onDefUpdate(updated);
      markDirty(false);
      toast({ tone: 'success', icon: 'Check', title: 'Object updated' });
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Failed to save';
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Save failed', desc: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const updated = await coApi.updateDef(wsId, def.id, { status: newStatus });
      onDefUpdate(updated);
      setConfirm(null);
      toast({ tone: 'success', icon: 'Check',
        title: newStatus === 'ACTIVE' ? 'Object published' :
               newStatus === 'ARCHIVED' ? 'Object archived' : 'Object restored' });
      window.dispatchEvent(new CustomEvent('nx-custom-objects-changed'));
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Status change failed';
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: msg });
      setConfirm(null);
    }
  };

  const handleDelete = async () => {
    try {
      const result = await coApi.deleteDef(wsId, def.id);
      setConfirm(null);
      if (result?.status === 'ARCHIVED') {
        toast({ tone: 'warning', icon: 'AlertCircle',
          title: 'Object archived (has records)',
          desc: 'Objects with records are archived, not deleted.' });
        onDefUpdate(result);
      } else {
        toast({ tone: 'success', icon: 'Trash2', title: `"${def.singularName}" deleted` });
        onDefDelete(def.id);
      }
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Delete failed';
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Delete failed', desc: msg });
      setConfirm(null);
    }
  };

  const hasActiveFields = (def.fields || []).some(f => f.status === 'ACTIVE');

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
        {/* General settings */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)',
            textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14,
            paddingBottom: 8, borderBottom: '1px solid var(--nx-border)' }}>
            General
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="nx-form-field">
                <label className="nx-form-label">Singular Name</label>
                <input className="nx-input" value={form.singularName}
                  onChange={e => setField('singularName', e.target.value)}
                  disabled={!canManage} />
              </div>
              <div className="nx-form-field">
                <label className="nx-form-label">Plural Name</label>
                <input className="nx-input" value={form.pluralName}
                  onChange={e => setField('pluralName', e.target.value)}
                  disabled={!canManage} />
              </div>
            </div>

            <div className="nx-form-field">
              <label className="nx-form-label">API Name</label>
              <input className="nx-input" value={def.apiName} readOnly
                style={{ opacity: 0.6, cursor: 'not-allowed' }} />
              <span style={{ fontSize: 11, color: 'var(--nx-text-faint)' }}>
                API names cannot be changed after creation.
              </span>
            </div>

            <div className="nx-form-field">
              <label className="nx-form-label">Description</label>
              <textarea className="nx-input nx-textarea" rows={3} value={form.description}
                onChange={e => setField('description', e.target.value)}
                disabled={!canManage} />
            </div>

            <div className="nx-form-field">
              <label className="nx-form-label">Icon</label>
              <IconPicker value={form.icon} onChange={v => setField('icon', v)} />
            </div>

            {canManage && dirty && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Btn variant="primary" icon="Check" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </Btn>
              </div>
            )}
          </div>
        </div>

        {/* Drawer Layout config */}
        <DrawerLayoutConfig
          def={def} wsId={wsId} canManage={canManage}
          onDefUpdate={onDefUpdate} toast={toast}
        />

        {/* Lifecycle */}
        {canManage && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14,
              paddingBottom: 8, borderBottom: '1px solid var(--nx-border)' }}>
              Lifecycle
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {def.status === 'DRAFT' && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 16px', background: 'var(--nx-surface)',
                  border: '1px solid var(--nx-border)', borderRadius: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>
                      Publish Object
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                      Make this object available for record creation. Requires at least one field.
                    </div>
                  </div>
                  <Btn variant="primary" icon="Globe" disabled={!hasActiveFields}
                    onClick={() => setConfirm({ type: 'publish' })} title={!hasActiveFields ? 'Add at least one field first' : undefined}>
                    Publish
                  </Btn>
                </div>
              )}
              {def.status === 'ACTIVE' && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 16px', background: 'var(--nx-surface)',
                  border: '1px solid var(--nx-border)', borderRadius: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>
                      Archive Object
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                      Hide this object from users. Existing records are preserved.
                    </div>
                  </div>
                  <Btn variant="ghost" icon="Archive" onClick={() => setConfirm({ type: 'archive' })}>
                    Archive
                  </Btn>
                </div>
              )}
              {def.status === 'ARCHIVED' && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 16px', background: 'var(--nx-surface)',
                  border: '1px solid var(--nx-border)', borderRadius: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>
                      Restore Object
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                      Re-activate this object and make it available again.
                    </div>
                  </div>
                  <Btn variant="primary" icon="RefreshCcw" onClick={() => handleStatusChange('ACTIVE')}>
                    Restore
                  </Btn>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Danger zone */}
        {canManage && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-danger)',
              textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14,
              paddingBottom: 8, borderBottom: '1px solid #FCA5A5' }}>
              Danger Zone
            </div>
            <div style={{ border: '1px solid #FCA5A5', borderRadius: 8, padding: '12px 16px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>
                  Delete Object
                </div>
                <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                  Permanently delete this object and all its field definitions.
                  Objects with records will be archived instead.
                </div>
              </div>
              <Btn variant="danger-ghost" icon="Trash2" onClick={() => setConfirm({ type: 'delete' })}>
                Delete
              </Btn>
            </div>
          </div>
        )}
      </div>

      {confirm?.type === 'publish' && (
        <ConfirmDialog
          title={`Publish "${def.singularName}"?`}
          desc="This object will become available for record creation across your workspace. You can archive it later if needed."
          confirmLabel="Publish"
          onConfirm={() => handleStatusChange('ACTIVE')}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm?.type === 'archive' && (
        <ConfirmDialog
          title={`Archive "${def.singularName}"?`}
          desc="This object will be hidden from users. All existing records are preserved and can be recovered by restoring the object."
          confirmLabel="Archive"
          onConfirm={() => handleStatusChange('ARCHIVED')}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm?.type === 'delete' && (
        <ConfirmDialog
          title={`Delete "${def.singularName}"?`}
          desc="This action cannot be undone. Field definitions and configuration will be permanently removed. Objects with existing records will be archived instead of deleted."
          danger
          confirmLabel="Delete Object"
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-004: FieldValueDisplay — renders a single field value for table/detail
// ─────────────────────────────────────────────────────────────────────────────

const FieldValueDisplay = ({ value, field }) => {
  const apiType = field?.apiType || field?.type;
  if (value === null || value === undefined || value === '') {
    return <span style={{ color: 'var(--nx-text-muted)' }}>—</span>;
  }
  if (apiType === 'BOOLEAN') {
    const isTrue = value === 'true';
    return (
      <span style={{ color: isTrue ? 'var(--nx-success, #16A34A)' : 'var(--nx-text-muted)' }}>
        {isTrue ? 'Yes' : 'No'}
      </span>
    );
  }
  if (apiType === 'DATE') {
    const d = new Date(value);
    return <span>{isNaN(d.getTime()) ? value : d.toLocaleDateString()}</span>;
  }
  if (apiType === 'MULTI_SELECT') {
    try {
      const arr = JSON.parse(value);
      if (!Array.isArray(arr) || arr.length === 0) {
        return <span style={{ color: 'var(--nx-text-muted)' }}>—</span>;
      }
      return (
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {arr.map((opt, i) => <Pill key={i} tone="blue">{opt}</Pill>)}
        </span>
      );
    } catch { return <span>{value}</span>; }
  }
  if (apiType === 'EMAIL') {
    return (
      <a href={`mailto:${value}`} style={{ color: 'var(--nx-accent)', textDecoration: 'none' }}
        onClick={e => e.stopPropagation()}>{value}</a>
    );
  }
  if (apiType === 'URL') {
    return (
      <a href={value} target="_blank" rel="noopener noreferrer"
        style={{ color: 'var(--nx-accent)', textDecoration: 'none' }}
        onClick={e => e.stopPropagation()}>{value}</a>
    );
  }
  if (apiType === 'PHONE') {
    return (
      <a href={`tel:${value}`} style={{ color: 'var(--nx-accent)', textDecoration: 'none' }}
        onClick={e => e.stopPropagation()}>{value}</a>
    );
  }
  if (apiType === 'TEXTAREA') {
    return (
      <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13 }}>{value}</span>
    );
  }
  return <span>{value}</span>;
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-004: DynamicFieldInput — renders the right form control per field type
// ─────────────────────────────────────────────────────────────────────────────

const DynamicFieldInput = ({ field, value, onChange, error }) => {
  const apiType = field?.apiType || field?.type;
  const cls = 'nx-input' + (error ? ' is-error' : '');

  if (apiType === 'BOOLEAN') {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
        <input type="checkbox" className="nx-checkbox"
          checked={value === true || value === 'true'}
          onChange={e => onChange(e.target.checked)} />
        <span style={{ fontSize: 13 }}>{value === true || value === 'true' ? 'Yes' : 'No'}</span>
      </label>
    );
  }
  if (apiType === 'DATE') {
    return <input type="date" className={cls} style={{ width: '100%' }}
      value={value || ''} onChange={e => onChange(e.target.value)} />;
  }
  if (apiType === 'NUMBER') {
    return <input type="number" className={cls} style={{ width: '100%' }}
      value={value || ''} onChange={e => onChange(e.target.value)} />;
  }
  if (apiType === 'TEXTAREA') {
    return <textarea className={`${cls} nx-textarea`}
      style={{ width: '100%', minHeight: 80, resize: 'vertical' }}
      value={value || ''} onChange={e => onChange(e.target.value)} rows={3} />;
  }
  if (apiType === 'SELECT') {
    const opts = field.options || [];
    return (
      <select className={cls} style={{ width: '100%' }}
        value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">Select an option…</option>
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (apiType === 'MULTI_SELECT') {
    const opts = field.options || [];
    let selected = [];
    try { selected = Array.isArray(value) ? value : (value ? JSON.parse(value) : []); }
    catch { selected = []; }

    const toggle = (opt) => {
      const next = selected.includes(opt)
        ? selected.filter(o => o !== opt)
        : [...selected, opt];
      onChange(next);
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {opts.length === 0
          ? <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>No options defined.</span>
          : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {opts.map(opt => {
                const isOn = selected.includes(opt);
                return (
                  <button key={opt} type="button" onClick={() => toggle(opt)}
                    style={{
                      fontSize: 12, padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
                      border: `1px solid ${isOn ? 'var(--nx-accent)' : 'var(--nx-border)'}`,
                      background: isOn ? 'var(--nx-accent-tint-2)' : 'var(--nx-surface)',
                      color: isOn ? 'var(--nx-accent)' : 'var(--nx-text-muted)',
                    }}>
                    {opt}
                  </button>
                );
              })}
            </div>
          )
        }
        {error && <span className="nx-form-error">{error}</span>}
      </div>
    );
  }
  if (apiType === 'EMAIL') {
    return <input type="email" className={cls} style={{ width: '100%' }}
      value={value || ''} onChange={e => onChange(e.target.value)} />;
  }
  if (apiType === 'PHONE') {
    return <input type="tel" className={cls} style={{ width: '100%' }}
      value={value || ''} onChange={e => onChange(e.target.value)} />;
  }
  if (apiType === 'URL') {
    return <input type="url" className={cls} style={{ width: '100%' }}
      value={value || ''} onChange={e => onChange(e.target.value)} />;
  }
  return <input type="text" className={cls} style={{ width: '100%' }}
    value={value || ''} onChange={e => onChange(e.target.value)} />;
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-004: RecordFormContent — shared form body for create and edit
// ─────────────────────────────────────────────────────────────────────────────

const RecordFormContent = ({ activeFields, values, onChange, errors }) => {
  if (activeFields.length === 0) {
    return <p style={{ color: 'var(--nx-text-muted)', fontSize: 13 }}>No fields to fill in.</p>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {activeFields.map(field => {
        const apiType = field.apiType || field.type;
        return (
          <div key={field.id} className={`nx-form-field${errors[field.id] ? ' is-error' : ''}`}>
            <label className="nx-form-label">
              {field.label}
              {field.required && <span className="nx-form-req">*</span>}
            </label>
            <DynamicFieldInput
              field={field}
              value={values[field.id] ?? getDefaultFieldValue(apiType)}
              onChange={v => onChange(field.id, v)}
              error={errors[field.id]}
            />
            {errors[field.id] && apiType !== 'MULTI_SELECT' && (
              <span className="nx-form-error">{errors[field.id]}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-FORM-001: CoRecordFormDrawer — right-side drawer matching People's RecordFormDrawer
// Handles both create (mode="create") and edit (mode="edit", recordId required).
// Uses nx-drawer nx-form-drawer + nx-drawer-veil CSS classes, identical to People.
// ─────────────────────────────────────────────────────────────────────────────

const CoRecordFormDrawer = ({ def, wsId, mode, recordId, onSaved, onClose, toast }) => {
  const isCreate    = mode === 'create';
  const activeFields = (def.fields || []).filter(f => f.status === 'ACTIVE');

  const [values,     setValues]     = useState(() => {
    const v = {};
    activeFields.forEach(f => { v[f.id] = getDefaultFieldValue(f.apiType || f.type); });
    return v;
  });
  const [linkValues, setLinkValues] = useState({});
  const [origLinks,  setOrigLinks]  = useState({});
  const [rels,       setRels]       = useState([]);
  const [relsErr,    setRelsErr]    = useState(false);
  const [errors,     setErrors]     = useState({});
  const [loading,    setLoading]    = useState(!isCreate);
  const [loadError,  setLoadError]  = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [dirty,      setDirty]      = useState(false);

  // Escape key — mirrors People's RecordFormDrawer close-on-escape
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [saving]);

  // Load relationships list (both modes) + record data (edit mode)
  useEffect(() => {
    const relsProm = coApi.listRelationships(wsId, def.id).catch(() => null);
    if (isCreate) {
      relsProm.then(res => {
        if (res) setRels((res.relationships ?? []).filter(r => !r.status || r.status === 'ACTIVE'));
        else setRelsErr(true);
      });
      return;
    }
    setLoading(true);
    setLoadError(null);
    Promise.all([
      coApi.getRecord(wsId, def.id, recordId),
      relsProm,
      coApi.getRecordLinks(wsId, def.id, recordId).catch(() => ({ relationships: [] })),
    ]).then(([r, relsRes, linksRes]) => {
      const v = {};
      (r.values || []).forEach(fv => {
        const field = activeFields.find(f => f.id === fv.fieldId);
        if (!field) return;
        const apiType = field.apiType || field.type;
        if (apiType === 'BOOLEAN') {
          v[fv.fieldId] = fv.value === 'true';
        } else if (apiType === 'MULTI_SELECT') {
          try { v[fv.fieldId] = fv.value ? JSON.parse(fv.value) : []; }
          catch { v[fv.fieldId] = []; }
        } else {
          v[fv.fieldId] = fv.value ?? '';
        }
      });
      setValues(v);
      if (relsRes) setRels((relsRes.relationships ?? []).filter(r => !r.status || r.status === 'ACTIVE'));
      const lv = {}; const orig = {};
      for (const group of (linksRes?.relationships ?? [])) {
        const items = (group.links || []).map(l => ({
          id: l.targetEntityId,
          displayName: l.entity ? getEntityDisplayName(l.entity) : l.targetEntityId,
          linkId: l.id,
        }));
        lv[group.relationshipId] = items;
        orig[group.relationshipId] = items;
      }
      setLinkValues(lv);
      setOrigLinks(orig);
    }).catch(err => setLoadError(extractApiError(err)))
      .finally(() => setLoading(false));
  }, [wsId, def.id, recordId, isCreate]);

  const setField = (fieldId, val) => {
    setDirty(true);
    setValues(prev => ({ ...prev, [fieldId]: val }));
    if (errors[fieldId]) setErrors(prev => ({ ...prev, [fieldId]: undefined }));
  };

  const setLinkField = (relId, items) => {
    setDirty(true);
    setLinkValues(prev => ({ ...prev, [relId]: items }));
    if (errors[relId]) setErrors(prev => ({ ...prev, [relId]: undefined }));
  };

  const validate = () => {
    const errs = {};
    activeFields.forEach(field => {
      if (!field.required) return;
      const apiType = field.apiType || field.type;
      if (apiType === 'BOOLEAN') return;
      const v = values[field.id];
      if (apiType === 'MULTI_SELECT') {
        if (!Array.isArray(v) || v.length === 0) errs[field.id] = `${field.label} is required`;
      } else if (!v || String(v).trim() === '') {
        errs[field.id] = `${field.label} is required`;
      }
    });
    rels.forEach(rel => {
      if (rel.required && !(linkValues[rel.id]?.length > 0)) errs[rel.id] = `${rel.label} is required`;
    });
    return errs;
  };

  const handleSave = async () => {
    if (saving) return;
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSaving(true);
    try {
      const fields = activeFields.map(field => ({
        fieldId: field.id,
        value: parseFieldValueForSubmit(values[field.id], field.apiType || field.type),
      }));

      if (isCreate) {
        const created = await coApi.createRecord(wsId, def.id, { fields });
        const linkErrors = [];
        for (const rel of rels) {
          for (const item of (linkValues[rel.id] || [])) {
            try { await coApi.createLink(wsId, def.id, created.id, rel.id, { targetEntityId: item.id }); }
            catch { linkErrors.push(rel.label); }
          }
        }
        if (linkErrors.length && toast) toast({ tone: 'warning', icon: 'AlertCircle',
          title: 'Some links could not be saved', desc: `Failed to link: ${[...new Set(linkErrors)].join(', ')}` });
        onSaved(created);
      } else {
        const updated = await coApi.updateRecord(wsId, def.id, recordId, { fields });
        const linkErrors = [];
        for (const rel of rels) {
          const before = origLinks[rel.id] || [];
          const after  = linkValues[rel.id] || [];
          const afterIds  = new Set(after.map(i => i.id));
          const beforeIds = new Set(before.map(i => i.id));
          for (const item of before) {
            if (!afterIds.has(item.id) && item.linkId) {
              try { await coApi.deleteLink(wsId, def.id, recordId, rel.id, item.linkId); }
              catch { linkErrors.push(rel.label); }
            }
          }
          for (const item of after) {
            if (!beforeIds.has(item.id)) {
              try { await coApi.createLink(wsId, def.id, recordId, rel.id, { targetEntityId: item.id }); }
              catch { linkErrors.push(rel.label); }
            }
          }
        }
        if (linkErrors.length && toast) toast({ tone: 'warning', icon: 'AlertCircle',
          title: 'Some links could not be updated', desc: `Failed: ${[...new Set(linkErrors)].join(', ')}` });
        onSaved(updated);
      }
    } catch (err) {
      setErrors({ _form: extractApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const verb = isCreate ? 'New' : 'Edit';

  return (
    <>
      <div className="nx-drawer-veil" onClick={() => { if (!saving) onClose(); }} style={{ zIndex: 79 }} />
      <aside className="nx-drawer nx-form-drawer" style={{ zIndex: 80 }} role="dialog" aria-modal="true" aria-label={`${verb} ${def.singularName}`}>
        {/* Header — matches People's RecordFormDrawer header exactly */}
        <div className="nx-drawer-head">
          <div className="nx-drawer-head-top">
            <button className="nx-drawer-prev" onClick={() => { if (!saving) onClose(); }} aria-label="Close">
              <NexusIcon name="X" size={14} />
            </button>
            <div className="nx-drawer-breadcrumb">
              <span>{verb}</span>
              <NexusIcon name="ChevronRight" size={11} />
              <span className="is-current">{def.singularName}</span>
            </div>
            {dirty && <span className="nx-dirty-dot" title="Unsaved changes" />}
          </div>
          <div className="nx-form-drawer-hero">
            <h2 className="nx-drawer-title">{verb} {def.singularName}</h2>
            <span className="nx-drawer-sub">{isCreate ? 'Fill in the details below.' : 'Update the fields and save.'}</span>
          </div>
        </div>

        {/* Body — scrollable, matches People's nx-drawer-body */}
        <div className="nx-drawer-body">
          {!isCreate && loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
              <NexusIcon name="Loader" size={22} style={{ animation: 'nx-spin 1s linear infinite' }} />
            </div>
          )}
          {!isCreate && loadError && !loading && (
            <div style={{ padding: '16px 20px', color: 'var(--nx-danger)', fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
              <NexusIcon name="AlertCircle" size={14} /> {loadError}
            </div>
          )}
          {(!loading || isCreate) && !loadError && (
            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {errors._form && (
                <div style={{ background: 'var(--nx-danger-tint,#FEF2F2)',
                  border: '1px solid var(--nx-danger,#DC2626)', borderRadius: 6,
                  padding: '8px 12px', fontSize: 13,
                  color: 'var(--nx-danger,#DC2626)', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <NexusIcon name="AlertCircle" size={14} />
                  {errors._form}
                </div>
              )}
              <RecordFormContent activeFields={activeFields} values={values} onChange={setField} errors={errors} />
              {relsErr && (
                <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', display: 'flex', gap: 6, alignItems: 'center' }}>
                  <NexusIcon name="AlertCircle" size={12} />
                  Relationships could not be loaded — you can add them after saving.
                </div>
              )}
              {rels.length > 0 && (
                <RelationshipPickerSection
                  wsId={wsId}
                  relationships={rels}
                  value={linkValues}
                  onChange={setLinkField}
                  errors={errors}
                />
              )}
            </div>
          )}
        </div>

        {/* Footer — matches People's nx-form-drawer-foot */}
        <div className="nx-form-drawer-foot">
          <span className="nx-muted nx-mini">{dirty ? 'Unsaved changes' : 'No changes'}</span>
          <span style={{ flex: 1 }} />
          <Btn variant="ghost" onClick={() => { if (!saving) onClose(); }} disabled={saving || (!isCreate && loading)}>Cancel</Btn>
          <Btn variant="primary" icon="Check" onClick={handleSave} disabled={saving || (!isCreate && (loading || !!loadError))}>
            {isCreate ? (saving ? 'Creating…' : `Create ${def.singularName}`) : (saving ? 'Saving…' : 'Save Changes')}
          </Btn>
        </div>
      </aside>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-004: RecordDetailDrawer — tabbed slide-over matching People/Companies UX
// ─────────────────────────────────────────────────────────────────────────────

const CO_AVATAR_TONES = ['#125AB0','#0E8C5A','#A56212','#6E3FBA','#B12A12','#0891B2','#2D7D9A','#7C3AED'];
const coToneForId = id => CO_AVATAR_TONES[(id || '').charCodeAt(0) % CO_AVATAR_TONES.length];

// CoDrawerPanel — reusable panel card matching nx-drawer-panel CSS
const CoDrawerPanel = ({ title, children, actions }) => (
  <div className="nx-drawer-panel">
    <div className="nx-drawer-panel-head">
      <h3 className="nx-drawer-panel-title">{title}</h3>
      {actions && <div style={{ display: 'flex', gap: 4 }}>{actions}</div>}
    </div>
    <div className="nx-drawer-panel-body">{children}</div>
  </div>
);

// CoPropRow — a single labeled field value, matching nx-prop pattern
const CoPropRow = ({ field, value }) => {
  const isEmpty = value === null || value === '' || value === undefined;
  return (
    <div className="nx-prop">
      <div className="nx-prop-label" style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
        <NexusIcon name={getFieldTypeConfig(field.apiType || field.type).icon} size={10} style={{ opacity: 0.55 }} />
        {field.label}
      </div>
      <div className={`nx-prop-value${isEmpty ? '' : ''}`}
        style={{ fontSize: 13, color: isEmpty ? 'var(--nx-text-faint)' : 'var(--nx-text)', fontStyle: isEmpty ? 'italic' : 'normal', fontWeight: 500 }}>
        {isEmpty ? '—' : <FieldValueDisplay value={value} field={field} />}
      </div>
    </div>
  );
};

// resolveDrawerSections — converts drawerConfig into renderable sections, always falls back safely
const resolveDrawerSections = (drawerConfig, activeFields) => {
  const activeById = Object.fromEntries(activeFields.map(f => [f.id, f]));
  if (!drawerConfig?.sections?.length) {
    return [{ id: '__default', label: 'Details', columns: 1, fields: activeFields }];
  }
  const seen = new Set();
  return drawerConfig.sections
    .map(s => ({
      ...s,
      fields: (s.fieldIds || []).map(id => activeById[id]).filter(f => {
        if (!f || seen.has(f.id)) return false;
        seen.add(f.id);
        return true;
      }),
    }))
    .filter(s => s.fields.length > 0);
};

const RecordDetailDrawer = ({ def, wsId, record, onClose, onUpdated, onDeleted, canManage, toast }) => {
  const [fullRecord,    setFullRecord]    = useState(null);
  const [loadError,     setLoadError]     = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [editing,       setEditing]       = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting,      setDeleting]      = useState(false);
  const [tab,           setTab]           = useState('overview');

  const activeFields = (def.fields || []).filter(f => f.status === 'ACTIVE');
  const primaryValue = getPrimaryFieldValue(record, def);
  const drawerConfig = def.drawerConfig || null;

  // Summary fields shown in hero (up to 3, from config)
  const summaryFields = (() => {
    const ids = drawerConfig?.summaryFieldIds || [];
    const byId = Object.fromEntries(activeFields.map(f => [f.id, f]));
    return ids.map(id => byId[id]).filter(Boolean).slice(0, 3);
  })();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await coApi.getRecord(wsId, def.id, record.id);
      setFullRecord(r);
    } catch (err) {
      setLoadError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  }, [wsId, def.id, record.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && !editing && !confirmDelete) onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, editing, confirmDelete]);

  const valuesMap = {};
  if (fullRecord?.values && Array.isArray(fullRecord.values)) {
    fullRecord.values.forEach(fv => { valuesMap[fv.fieldId] = fv.value; });
  }

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await coApi.deleteRecord(wsId, def.id, record.id);
      setConfirmDelete(false);
      onDeleted();
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Delete failed', desc: extractApiError(err) });
    } finally {
      setDeleting(false);
    }
  };

  const sections = resolveDrawerSections(drawerConfig, activeFields);

  const tabs = [
    { id: 'overview',  label: 'Overview' },
    { id: 'related',   label: 'Related Records' },
  ];

  return (
    <>
      <div className="nx-drawer-veil" onClick={onClose} />
      <aside className="nx-drawer" role="dialog" aria-modal="true"
        aria-labelledby="co-record-drawer-title">

        {/* ── Head ── */}
        <div className="nx-drawer-head">
          <div className="nx-drawer-head-top">
            <button className="nx-drawer-prev" type="button" onClick={onClose} aria-label="Close">
              <NexusIcon name="X" size={16} />
            </button>
            <div className="nx-drawer-breadcrumb">
              <NexusIcon name={def.icon || 'Database'} size={13} />
              <span>{def.singularName}</span>
            </div>
            <div className="nx-drawer-hero-actions">
              {canManage && def.status === 'ACTIVE' && (
                <Btn variant="ghost" size="sm" icon="Edit" onClick={() => setEditing(true)}>Edit</Btn>
              )}
              {canManage && (
                <IconBtn icon="Trash2" aria-label={`Delete ${def.singularName}`}
                  style={{ color: 'var(--nx-danger)' }} onClick={() => setConfirmDelete(true)} />
              )}
            </div>
          </div>

          {/* ── Hero ── */}
          <div className="nx-drawer-hero is-person">
            <div className="nx-phero-id-row">
              <Avatar name={primaryValue} tone={coToneForId(record.id)} size={44} />
              <div className="nx-drawer-hero-meta">
                <h2 id="co-record-drawer-title" className="nx-drawer-title">{primaryValue}</h2>
                {summaryFields.map(f => {
                  const v = valuesMap[f.id] ?? null;
                  const isEmpty = v === null || v === '' || v === undefined;
                  return (
                    <span key={f.id} style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
                      <span style={{ fontWeight: 600 }}>{f.label}:</span>{' '}
                      {isEmpty ? <em style={{ opacity: 0.5 }}>—</em> : <FieldValueDisplay value={v} field={f} />}
                    </span>
                  );
                })}
                <span style={{ fontSize: 11, color: 'var(--nx-text-faint)', marginTop: 2 }}>
                  {record.createdAt ? `Created ${fmtAbsTime(record.createdAt)}` : ''}
                  {record.updatedAt && record.updatedAt !== record.createdAt
                    ? ` · Updated ${fmtAbsTime(record.updatedAt)}` : ''}
                </span>
              </div>
            </div>
          </div>

          {/* ── Tab strip ── */}
          <div className="nx-drawer-tabs" role="tablist">
            {tabs.map(t => (
              <button key={t.id} type="button" role="tab"
                className={`nx-drawer-tab${tab === t.id ? ' is-active' : ''}`}
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="nx-drawer-body" style={{ padding: 0, background: 'var(--nx-bg)' }}>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <NexusIcon name="Loader" size={22}
                style={{ animation: 'nx-spin 1s linear infinite', color: 'var(--nx-text-muted)' }} />
            </div>
          )}
          {loadError && !loading && (
            <div style={{ color: 'var(--nx-danger)', fontSize: 13, display: 'flex', gap: 8,
              alignItems: 'center', padding: '20px 24px' }}>
              <NexusIcon name="AlertCircle" size={14} />
              {loadError}
              <Btn size="sm" variant="ghost" icon="RotateCcw" onClick={load}>Retry</Btn>
            </div>
          )}

          {!loading && !loadError && fullRecord && tab === 'overview' && (
            <div className="nx-pov-main">
              {sections.length === 0 && (
                <CoDrawerPanel title="Details">
                  <p style={{ color: 'var(--nx-text-muted)', fontSize: 13 }}>
                    No fields defined for this object.
                  </p>
                </CoDrawerPanel>
              )}
              {sections.map(section => (
                <CoDrawerPanel key={section.id} title={section.label}>
                  {section.columns === 2 ? (
                    <div className="nx-prop-grid">
                      {section.fields.map(field => (
                        <CoPropRow key={field.id} field={field} value={valuesMap[field.id] ?? null} />
                      ))}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      {section.fields.map((field, i) => {
                        const val = valuesMap[field.id] ?? null;
                        const isEmpty = val === null || val === '' || val === undefined;
                        return (
                          <div key={field.id} style={{
                            padding: '8px 0',
                            borderBottom: i < section.fields.length - 1 ? '1px solid var(--nx-divider)' : 'none',
                          }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)',
                              textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3,
                              display: 'flex', alignItems: 'center', gap: 4 }}>
                              <NexusIcon name={getFieldTypeConfig(field.apiType || field.type).icon}
                                size={10} style={{ opacity: 0.55 }} />
                              {field.label}
                            </div>
                            <div style={{ fontSize: 13, color: isEmpty ? 'var(--nx-text-faint)' : 'var(--nx-text)',
                              fontStyle: isEmpty ? 'italic' : 'normal', fontWeight: 500 }}>
                              {isEmpty ? '—' : <FieldValueDisplay value={val} field={field} />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CoDrawerPanel>
              ))}

              {/* Record metadata panel */}
              <CoDrawerPanel title="Record Info">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
                    <span style={{ fontWeight: 600, color: 'var(--nx-text-soft)' }}>ID</span><br />
                    <span style={{ fontFamily: 'var(--nx-mono)', fontSize: 11, opacity: 0.7 }}>
                      {record.id}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
                    <span style={{ fontWeight: 600, color: 'var(--nx-text-soft)' }}>Created</span><br />
                    {record.createdAt ? fmtAbsTime(record.createdAt) : '—'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
                    <span style={{ fontWeight: 600, color: 'var(--nx-text-soft)' }}>Last Modified</span><br />
                    {record.updatedAt ? fmtAbsTime(record.updatedAt) : '—'}
                  </div>
                </div>
              </CoDrawerPanel>
            </div>
          )}

          {!loading && !loadError && tab === 'related' && (
            <div className="nx-pov-main">
              <RelatedRecordsSection wsId={wsId} objectId={def.id} recordId={record.id} canManage={canManage} />
            </div>
          )}
        </div>
      </aside>

      {editing && (
        <CoRecordFormDrawer
          def={def} wsId={wsId}
          mode="edit" recordId={record.id}
          onSaved={(r) => { setEditing(false); load(); onUpdated(r); }}
          onClose={() => setEditing(false)}
          toast={toast}
        />
      )}
      {confirmDelete && (
        <ConfirmDialog
          title={`Delete ${def.singularName}?`}
          desc={`"${primaryValue}" will be permanently deleted. This action cannot be undone.`}
          danger
          busy={deleting}
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-LIST-014: CoSavedViewsPanel — active view tracking, rename, update (overwrite), delete
// ─────────────────────────────────────────────────────────────────────────────

const CoSavedViewsPanel = ({ defId, activeViewId, onApply, onUpdate, onRename, onDelete, onClose }) => {
  const storageKey = `co-views-${defId}`;
  const [views, setViews] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch { return []; }
  });

  const handleDelete = (id) => {
    const updated = views.filter(v => v.id !== id);
    setViews(updated);
    try { localStorage.setItem(storageKey, JSON.stringify(updated)); } catch {}
    if (onDelete) onDelete(id);
  };

  return (
    <div style={{
      position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 120,
      background: 'var(--nx-surface)', border: '1px solid var(--nx-border)',
      borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.12)', minWidth: 240, maxWidth: 320,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 12px 8px', borderBottom: '1px solid var(--nx-border)',
      }}>
        <span style={{ fontWeight: 600, fontSize: 11, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Saved Views
        </span>
        <button type="button" onClick={onClose} aria-label="Close saved views"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--nx-text-muted)', display: 'flex', alignItems: 'center' }}>
          <NexusIcon name="X" size={13} />
        </button>
      </div>
      <div style={{ padding: views.length === 0 ? '14px 12px' : '4px 0', maxHeight: 320, overflowY: 'auto' }}>
        {views.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--nx-text-muted)', textAlign: 'center' }}>
            No saved views. Use Save View to create one.
          </div>
        ) : views.map(view => {
          const isActive = view.id === activeViewId;
          return (
            <div key={view.id} style={{
              display: 'flex', alignItems: 'center', padding: '5px 12px', gap: 6,
              background: isActive ? 'var(--nx-accent-subtle, rgba(99,102,241,.06))' : 'transparent',
            }}>
              <NexusIcon name="Bookmark" size={12} style={{ color: isActive ? 'var(--nx-accent)' : 'var(--nx-text-muted)', flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: isActive ? 500 : 400, color: isActive ? 'var(--nx-accent)' : 'var(--nx-text)' }} title={view.name}>
                {view.name}
              </span>
              {isActive ? (
                <button type="button" onClick={() => { onUpdate && onUpdate(view); onClose(); }}
                  style={{ background: 'none', border: '1px solid var(--nx-accent)', borderRadius: 4, cursor: 'pointer', fontSize: 11, padding: '2px 7px', color: 'var(--nx-accent)', flexShrink: 0 }}>
                  Update
                </button>
              ) : (
                <button type="button" onClick={() => { onApply(view); onClose(); }}
                  style={{ background: 'none', border: '1px solid var(--nx-border)', borderRadius: 4, cursor: 'pointer', fontSize: 11, padding: '2px 7px', color: 'var(--nx-accent)', flexShrink: 0 }}>
                  Apply
                </button>
              )}
              <button type="button" onClick={() => { if (onRename) onRename(view); }} aria-label={`Rename view ${view.name}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', display: 'flex', alignItems: 'center', padding: 2, flexShrink: 0 }}>
                <NexusIcon name="Pencil" size={12} />
              </button>
              <button type="button" onClick={() => handleDelete(view.id)} aria-label={`Delete view ${view.name}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', display: 'flex', alignItems: 'center', padding: 2, flexShrink: 0 }}>
                <NexusIcon name="Trash2" size={12} />
              </button>
            </div>
          );
        })}
      </div>
      <div style={{ padding: '8px 12px', borderTop: '1px solid var(--nx-border)', fontSize: 11, color: 'var(--nx-text-muted)', textAlign: 'center' }}>
        Views are saved in this browser only.
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-LIST-014: CoSaveViewModal — matches People's SaveViewModal behavior
//   Duplicate names warn but do not block (OD-05). "__default__" is reserved.
//   onSaved(newId) — passes the new view's ID back so the parent can track it.
// ─────────────────────────────────────────────────────────────────────────────

const CoSaveViewModal = ({ defId, currentState, onSaved, onClose }) => {
  const storageKey = `co-views-${defId}`;
  const [name, setName] = useState('');

  const getViews = () => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch { return []; }
  };

  const trimmed = name.trim();
  const isReserved = trimmed === '__default__';
  const isDuplicate = trimmed !== '' && !isReserved && getViews().some(v => v.name === trimmed);
  const canSubmit = trimmed !== '' && !isReserved;

  const handleSave = () => {
    if (!canSubmit) return;
    const existing = getViews();
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: trimmed,
      ...currentState,
      createdAt: new Date().toISOString(),
    };
    try { localStorage.setItem(storageKey, JSON.stringify([...existing, entry])); } catch {}
    onSaved(entry.id);
  };

  return (
    <Modal title="Save View" icon="Bookmark" onClose={onClose} width={400}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" onClick={handleSave} disabled={!canSubmit}>Save View</Btn>
        </div>
      }>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)', display: 'block', marginBottom: 6 }}>
            View name
          </label>
          <input
            className="nx-input"
            style={{ width: '100%', boxSizing: 'border-box' }}
            placeholder="e.g. Active this month"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') onClose(); }}
            autoFocus
          />
        </div>
        {isReserved && (
          <p style={{ fontSize: 12, color: 'var(--nx-danger)', margin: 0 }}>
            The name &quot;__default__&quot; is reserved and cannot be used.
          </p>
        )}
        {isDuplicate && (
          <p style={{ fontSize: 12, color: 'var(--nx-warning, #b45309)', margin: 0 }}>
            A view with this name already exists. Saving will create a second view with the same name.
          </p>
        )}
        <p style={{ fontSize: 11, color: 'var(--nx-text-muted)', margin: 0 }}>
          Captures current search, filters, sort, and visible columns.
        </p>
      </div>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-LIST-014: CoRenameViewModal — rename an existing saved view in localStorage
// ─────────────────────────────────────────────────────────────────────────────

const CoRenameViewModal = ({ defId, view, onRenamed, onClose }) => {
  const storageKey = `co-views-${defId}`;
  const [name, setName] = useState(view.name);
  const [error, setError] = useState('');

  const getViews = () => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch { return []; }
  };

  const handleSave = () => {
    const t = name.trim();
    if (!t) { setError('Name is required.'); return; }
    if (t.length > 60) { setError('Name must be 60 characters or fewer.'); return; }
    if (t === '__default__') { setError('The name "__default__" is reserved and cannot be used.'); return; }
    const existing = getViews();
    if (existing.some(v => v.id !== view.id && v.name === t)) {
      setError('A view with this name already exists.'); return;
    }
    const updated = existing.map(v => v.id === view.id ? { ...v, name: t } : v);
    try { localStorage.setItem(storageKey, JSON.stringify(updated)); } catch { setError('Failed to rename view.'); return; }
    onRenamed(view.id, t);
  };

  return (
    <Modal title="Rename View" icon="Pencil" onClose={onClose} width={400}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" onClick={handleSave} disabled={!name.trim()}>Rename</Btn>
        </div>
      }>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)', display: 'block', marginBottom: 6 }}>
            View name
          </label>
          <input
            className="nx-input"
            style={{ width: '100%', boxSizing: 'border-box' }}
            value={name}
            maxLength={60}
            onChange={e => { setName(e.target.value); if (error) setError(''); }}
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') onClose(); }}
            autoFocus
          />
        </div>
        {error && <p style={{ fontSize: 12, color: 'var(--nx-danger)', margin: 0 }}>{error}</p>}
      </div>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-LIST-002 → CO-LIST-012: CoImportWizard — multi-step CSV/XLSX import
// ─────────────────────────────────────────────────────────────────────────────

const MAX_IMPORT_ROWS = 2000;
const IMPORT_HISTORY_KEY = 'nx-co-import-history-v1';

const CoImportWizard = ({ def, wsId, toast, onClose, onDone }) => {
  const [step,          setStep]          = useState('upload');
  const [filename,      setFilename]      = useState('');
  const [headers,       setHeaders]       = useState([]);
  const [rows,          setRows]          = useState([]);
  const [parseError,    setParseError]    = useState('');
  const [isDragging,    setIsDragging]    = useState(false);
  const [mapping,       setMapping]       = useState({});
  const [validRows,     setValidRows]     = useState([]);
  const [invalidRows,   setInvalidRows]   = useState([]);
  const [progress,      setProgress]      = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);
  const [failures,      setFailures]      = useState([]);
  const [createdCount,  setCreatedCount]  = useState(0);
  const [showHistory,   setShowHistory]   = useState(false);
  const fileRef = useRef(null);

  const activeFields = (def.fields || []).filter(f => f.status === 'ACTIVE');
  const primaryField = activeFields.find(f => f.isPrimary);

  // ── CSV parser ────────────────────────────────────────────────────────────
  const parseCSVLocal = (text) => {
    const result = [];
    let row = [], field = '', inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i], next = text[i + 1];
      if (inQuotes) {
        if (ch === '"' && next === '"') { field += '"'; i++; }
        else if (ch === '"') { inQuotes = false; }
        else { field += ch; }
      } else if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(field); field = '';
      } else if (ch === '\n' || (ch === '\r' && next === '\n')) {
        if (ch === '\r') i++;
        row.push(field); field = '';
        if (row.some(c => c !== '') || result.length > 0) result.push(row);
        row = [];
      } else if (ch === '\r') {
        row.push(field); field = '';
        if (row.some(c => c !== '') || result.length > 0) result.push(row);
        row = [];
      } else {
        field += ch;
      }
    }
    if (field !== '' || row.length > 0) { row.push(field); result.push(row); }
    return result;
  };

  // ── File handler (CSV + XLSX) ─────────────────────────────────────────────
  const handleFile = (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    const isXlsx = name.endsWith('.xlsx') || name.endsWith('.xls');
    const isCsv  = name.endsWith('.csv');
    if (!isCsv && !isXlsx) { setParseError('Please upload a CSV or XLSX file.'); return; }
    setParseError('');
    setFilename(file.name);

    const processRows = (parsed) => {
      if (parsed.length < 2) { setParseError('File must have a header row and at least one data row.'); return; }
      const hdrs = parsed[0].map(h => String(h ?? ''));
      const dataRows = parsed.slice(1).filter(r => r.some(c => String(c ?? '').trim() !== ''));
      if (!hdrs.length) { setParseError('No columns found.'); return; }
      if (dataRows.length > MAX_IMPORT_ROWS) {
        setParseError(`File has ${dataRows.length.toLocaleString()} data rows; maximum is ${MAX_IMPORT_ROWS.toLocaleString()}. Split the file and import in batches.`);
        return;
      }
      setHeaders(hdrs);
      setRows(dataRows);
      const auto = {};
      hdrs.forEach((h, i) => {
        const match = activeFields.find(f => f.label.toLowerCase() === h.trim().toLowerCase());
        auto[i] = match ? match.id : '';
      });
      setMapping(auto);
      setStep('map');
    };

    if (isXlsx) {
      if (!window.XLSX) { setParseError('XLSX library not loaded — try a CSV file instead.'); return; }
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = window.XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const raw = window.XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
          processRows(raw);
        } catch { setParseError('Could not parse XLSX file.'); }
      };
      reader.onerror = () => setParseError('Could not read file.');
      reader.readAsArrayBuffer(file);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        try { processRows(parseCSVLocal(e.target.result)); }
        catch { setParseError('Could not parse CSV file.'); }
      };
      reader.onerror = () => setParseError('Could not read file.');
      reader.readAsText(file);
    }
  };

  // ── Template download ─────────────────────────────────────────────────────
  const downloadTemplate = () => {
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const hdrs = activeFields.map(f => esc(f.label + (f.isPrimary ? ' (required)' : '')));
    const sample = activeFields.map(f => {
      const t = f.apiType || f.type;
      if (t === 'NUMBER')  return esc('0');
      if (t === 'DATE')    return esc('2026-01-15');
      if (t === 'BOOLEAN') return esc('true');
      if ((t === 'SELECT' || t === 'MULTI_SELECT') && f.options?.length) return esc(f.options[0].label || '');
      return esc('');
    });
    const csv = '﻿' + hdrs.join(',') + '\n' + sample.join(',') + '\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement('a'), { href: url, download: `${def.pluralName.toLowerCase().replace(/\s+/g, '-')}-import-template.csv` }).click();
    URL.revokeObjectURL(url);
  };

  // ── Build + validate ──────────────────────────────────────────────────────
  const buildAndValidate = () => {
    const mapped = Object.entries(mapping).filter(([, fId]) => fId !== '');
    const valid = [], invalid = [];
    rows.forEach((row, ri) => {
      const values = {}, errors = [];
      mapped.forEach(([colIdxStr, fieldId]) => {
        const raw = String(row[Number(colIdxStr)] ?? '').trim();
        const field = activeFields.find(f => f.id === fieldId);
        if (!field) return;
        const apiType = field.apiType || field.type;
        if (apiType === 'NUMBER' && raw !== '' && isNaN(Number(raw)))
          errors.push(`"${field.label}" must be numeric`);
        if (apiType === 'DATE' && raw !== '' && isNaN(new Date(raw).getTime()))
          errors.push(`"${field.label}" must be a valid date (YYYY-MM-DD)`);
        if (apiType === 'SELECT' && raw !== '' && field.options?.length &&
            !field.options.some(o => (o.label || '').toLowerCase() === raw.toLowerCase()))
          errors.push(`"${field.label}": "${raw}" is not a valid option`);
        const parsed = parseFieldValueForSubmit(raw, apiType);
        if (field.isPrimary && (parsed === null || parsed === ''))
          errors.push(`"${field.label}" (primary) is required`);
        if (parsed !== null) values[fieldId] = parsed;
      });
      (errors.length ? invalid : valid).push({ rowIdx: ri + 2, values, errors, row });
    });
    setValidRows(valid);
    setInvalidRows(invalid);
    setStep('review');
  };

  // ── Run import ────────────────────────────────────────────────────────────
  const runImport = async () => {
    setStep('progress');
    setProgress(0);
    setProgressTotal(validRows.length);
    const errs = [];
    let created = 0;
    for (let i = 0; i < validRows.length; i++) {
      const { rowIdx, values } = validRows[i];
      try { await coApi.createRecord(wsId, def.id, { values }); created++; }
      catch (err) { errs.push({ rowIdx, reason: extractApiError(err) }); }
      setProgress(i + 1);
      if (i % 3 === 2) await new Promise(r => setTimeout(r, 0));
    }
    try {
      const hist = JSON.parse(localStorage.getItem(IMPORT_HISTORY_KEY) || '[]');
      hist.unshift({ filename, objectName: def.pluralName, objectId: def.id, created, failed: errs.length, skipped: invalidRows.length, ts: Date.now() });
      localStorage.setItem(IMPORT_HISTORY_KEY, JSON.stringify(hist.slice(0, 30)));
    } catch { /* localStorage unavailable */ }
    setCreatedCount(created);
    setFailures(errs);
    setStep('summary');
  };

  const resetForMore = () => {
    setStep('upload');
    setFilename(''); setHeaders([]); setRows([]); setParseError(''); setIsDragging(false);
    setMapping({}); setValidRows([]); setInvalidRows([]);
    setProgress(0); setProgressTotal(0); setFailures([]); setCreatedCount(0); setShowHistory(false);
  };

  const mappedCols     = Object.entries(mapping).filter(([, fId]) => fId !== '');
  const mappedFieldIds = new Set(mappedCols.map(([, fId]) => fId));
  const primaryMapped  = primaryField ? mappedFieldIds.has(primaryField.id) : true;

  // ── Step breadcrumb ───────────────────────────────────────────────────────
  const STEP_ORDER  = ['upload', 'map', 'review', 'progress', 'summary'];
  const STEP_LABELS = ['Upload', 'Map', 'Review', 'Import', 'Done'];
  const stepIdx = STEP_ORDER.indexOf(step);

  const breadcrumb = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 20 }}>
      {STEP_LABELS.map((label, idx) => (
        <React.Fragment key={label}>
          {idx > 0 && <div style={{ flex: 1, height: 2, background: idx <= stepIdx ? 'var(--nx-accent)' : 'var(--nx-border)', minWidth: 8 }} />}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, background: idx <= stepIdx ? 'var(--nx-accent)' : 'var(--nx-border)', color: idx <= stepIdx ? '#fff' : 'var(--nx-text-muted)' }}>
              {idx < stepIdx ? '✓' : idx + 1}
            </div>
            <div style={{ fontSize: 10, fontWeight: idx === stepIdx ? 600 : 400, color: idx === stepIdx ? 'var(--nx-accent)' : 'var(--nx-text-muted)', whiteSpace: 'nowrap' }}>{label}</div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  const selStyle = { width: '100%', padding: '4px 6px', fontSize: 13, border: '1px solid var(--nx-border)', borderRadius: 5, background: 'var(--nx-surface)', color: 'var(--nx-text)' };

  const footer = step === 'map' ? (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
      <Btn variant="ghost" onClick={() => setStep('upload')}>Back</Btn>
      <Btn variant="primary" onClick={buildAndValidate} disabled={mappedCols.length === 0}>Next: Review</Btn>
    </div>
  ) : step === 'review' ? (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
      <Btn variant="ghost" onClick={() => setStep('map')}>Back</Btn>
      <Btn variant="primary" onClick={runImport} disabled={validRows.length === 0}>
        Import {validRows.length} record{validRows.length !== 1 ? 's' : ''}
      </Btn>
    </div>
  ) : step === 'summary' ? (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
      <Btn variant="ghost" onClick={resetForMore}>Import More</Btn>
      <Btn variant="primary" onClick={() => { onDone(); onClose(); }}>Done</Btn>
    </div>
  ) : null;

  return (
    <Modal title="Import Records" icon="Upload" onClose={step === 'progress' ? () => {} : onClose} width={640} footer={footer}>
      {breadcrumb}

      {step === 'upload' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={e => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current?.click()}
            style={{ border: `2px dashed ${isDragging ? 'var(--nx-accent)' : 'var(--nx-border)'}`, borderRadius: 8, padding: '36px 24px', textAlign: 'center', cursor: 'pointer', background: isDragging ? 'var(--nx-surface-alt)' : 'var(--nx-surface)', transition: 'border-color 0.15s, background 0.15s' }}>
            <NexusIcon name="Upload" size={28} style={{ color: 'var(--nx-text-muted)', marginBottom: 10 }} />
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Drop a file here or click to browse</div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>CSV or XLSX · up to {MAX_IMPORT_ROWS.toLocaleString()} rows</div>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
              onChange={e => { handleFile(e.target.files[0]); e.target.value = ''; }} />
          </div>
          {parseError && (
            <div style={{ fontSize: 13, color: 'var(--nx-danger)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <NexusIcon name="AlertCircle" size={14} />{parseError}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
              First row must be column headers. Each subsequent row becomes one {def.singularName}.
            </div>
            <button type="button" onClick={downloadTemplate}
              style={{ flexShrink: 0, fontSize: 12, color: 'var(--nx-accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
              <NexusIcon name="Download" size={12} />Download template
            </button>
          </div>
          {activeFields.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                Expected columns
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {activeFields.map(f => (
                  <span key={f.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 12, border: '1px solid var(--nx-border)', fontSize: 11, color: 'var(--nx-text-muted)', background: 'var(--nx-surface-alt)' }}>
                    {f.label}{f.isPrimary && <span style={{ color: 'var(--nx-danger)', fontSize: 10, lineHeight: 1 }}>*</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(() => {
            let hist = [];
            try { hist = JSON.parse(localStorage.getItem(IMPORT_HISTORY_KEY) || '[]').filter(h => h.objectId === def.id); } catch { /* ignore */ }
            if (!hist.length) return null;
            return (
              <div>
                <button type="button" onClick={() => setShowHistory(v => !v)}
                  style={{ fontSize: 12, color: 'var(--nx-text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <NexusIcon name="Clock" size={12} />{showHistory ? 'Hide' : 'Show'} import history ({hist.length})
                </button>
                {showHistory && (
                  <div style={{ marginTop: 8, fontSize: 12, maxHeight: 140, overflowY: 'auto', border: '1px solid var(--nx-border)', borderRadius: 6 }}>
                    {hist.map((h, idx) => (
                      <div key={idx} style={{ padding: '6px 10px', borderBottom: idx < hist.length - 1 ? '1px solid var(--nx-border)' : 'none', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }}>{new Date(h.ts).toLocaleString()}</span>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.filename}</span>
                        <span style={{ color: 'var(--nx-success, #16a34a)', flexShrink: 0 }}>{h.created} created</span>
                        {h.failed > 0 && <span style={{ color: 'var(--nx-danger)', flexShrink: 0 }}>{h.failed} failed</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {step === 'map' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
            Match your file's columns to {def.singularName} fields. Unmapped columns are ignored.
          </div>
          {!primaryMapped && primaryField && (
            <div style={{ fontSize: 12, padding: '7px 10px', borderRadius: 6, background: 'var(--nx-warning-bg, #fef3c7)', color: 'var(--nx-warning-text, #92400e)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <NexusIcon name="AlertTriangle" size={13} />
              Map &ldquo;{primaryField.label}&rdquo; (primary) to give imported records a name or title.
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--nx-border)' }}>
                  {['File column', 'Maps to field', 'Type'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: 'var(--nx-text-muted)', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {headers.map((h, i) => {
                  const selField = activeFields.find(f => f.id === mapping[i]);
                  const fType = selField ? (selField.apiType || selField.type) : null;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--nx-border)' }}>
                      <td style={{ padding: '7px 8px', fontFamily: 'monospace', fontSize: 12 }}>
                        {h || <em style={{ color: 'var(--nx-text-muted)' }}>(blank)</em>}
                      </td>
                      <td style={{ padding: '7px 8px' }}>
                        <select value={mapping[i] || ''} style={selStyle}
                          onChange={e => setMapping(prev => ({ ...prev, [i]: e.target.value }))}>
                          <option value="">— Skip —</option>
                          {activeFields.map(f => {
                            const alreadyUsed = f.id !== mapping[i] && mappedFieldIds.has(f.id);
                            return (
                              <option key={f.id} value={f.id} disabled={alreadyUsed}>
                                {f.label}{f.isPrimary ? ' ★' : ''}{alreadyUsed ? ' (in use)' : ''}
                              </option>
                            );
                          })}
                        </select>
                      </td>
                      <td style={{ padding: '7px 8px' }}>
                        {fType && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, padding: '2px 6px', borderRadius: 4, background: 'var(--nx-surface-alt)', color: 'var(--nx-text-muted)' }}>
                            <NexusIcon name={getFieldTypeConfig(fType).icon} size={10} />{getFieldTypeConfig(fType).label}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {step === 'review' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            {[
              { label: 'Total',  value: rows.length,         color: 'var(--nx-text)' },
              { label: 'Ready',  value: validRows.length,    color: 'var(--nx-success, #16a34a)' },
              { label: 'Errors', value: invalidRows.length,  color: invalidRows.length > 0 ? 'var(--nx-danger)' : 'var(--nx-text-muted)' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--nx-border)', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
                <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 3 }}>{label}</div>
              </div>
            ))}
          </div>
          {!primaryMapped && primaryField && (
            <div style={{ fontSize: 12, padding: '8px 10px', borderRadius: 6, background: 'var(--nx-warning-bg, #fef3c7)', color: 'var(--nx-warning-text, #92400e)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <NexusIcon name="AlertTriangle" size={13} />
              Primary field &ldquo;{primaryField.label}&rdquo; is not mapped — records will have no primary value.
            </div>
          )}
          {mappedCols.length > 0 && validRows.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                Preview — first {Math.min(10, validRows.length)} valid rows
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--nx-border)' }}>
                    {mappedCols.map(([ci, fieldId]) => {
                      const f = activeFields.find(x => x.id === fieldId);
                      return <th key={ci} style={{ textAlign: 'left', padding: '5px 8px', fontWeight: 600, color: 'var(--nx-text-muted)', fontSize: 11 }}>{f?.label || fieldId}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {validRows.slice(0, 10).map(({ rowIdx, row }) => (
                    <tr key={rowIdx} style={{ borderBottom: '1px solid var(--nx-border)' }}>
                      {mappedCols.map(([ci]) => {
                        const val = String(row[Number(ci)] ?? '').trim();
                        return <td key={ci} style={{ padding: '5px 8px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val || <em style={{ color: 'var(--nx-text-muted)' }}>—</em>}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {invalidRows.length > 0 && (
            <div style={{ fontSize: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--nx-danger)' }}>Validation errors (will be skipped):</div>
              {invalidRows.slice(0, 5).map(({ rowIdx, errors }) => (
                <div key={rowIdx} style={{ marginBottom: 2 }}>
                  <span style={{ color: 'var(--nx-text-muted)' }}>Row {rowIdx}: </span>
                  <span style={{ color: 'var(--nx-danger)' }}>{errors.join('; ')}</span>
                </div>
              ))}
              {invalidRows.length > 5 && <div style={{ color: 'var(--nx-text-muted)', marginTop: 2 }}>…and {invalidRows.length - 5} more with errors</div>}
            </div>
          )}
        </div>
      )}

      {step === 'progress' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
          <div style={{ fontSize: 13 }}>Importing {def.pluralName}… {progress} / {progressTotal}</div>
          <div style={{ height: 8, borderRadius: 4, background: 'var(--nx-border)', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 4, background: 'var(--nx-accent)', width: progressTotal > 0 ? `${Math.round((progress / progressTotal) * 100)}%` : '0%', transition: 'width 0.12s ease' }} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>Please keep this window open.</div>
        </div>
      )}

      {step === 'summary' && (() => {
        const skipped = invalidRows.length;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 20, padding: '8px 0', flexWrap: 'wrap' }}>
              {[
                { label: 'Created', value: createdCount,    color: 'var(--nx-success, #16a34a)' },
                { label: 'Failed',  value: failures.length, color: 'var(--nx-danger)',           hide: failures.length === 0 },
                { label: 'Skipped', value: skipped,         color: 'var(--nx-text-muted)',        hide: skipped === 0 },
              ].filter(s => !s.hide).map(({ label, value, color }) => (
                <div key={label} style={{ textAlign: 'center', minWidth: 72 }}>
                  <div style={{ fontSize: 32, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
                  <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 4 }}>{label}</div>
                </div>
              ))}
            </div>
            {createdCount > 0 && failures.length === 0 && (
              <div style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>All records imported successfully.</div>
            )}
            {failures.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Failed rows</div>
                <div style={{ maxHeight: 180, overflowY: 'auto', fontSize: 12 }}>
                  {failures.slice(0, 10).map(({ rowIdx, reason }) => (
                    <div key={rowIdx} style={{ padding: '5px 0', borderBottom: '1px solid var(--nx-border)', display: 'flex', gap: 8 }}>
                      <span style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }}>Row {rowIdx}:</span>
                      <span style={{ color: 'var(--nx-danger)' }}>{reason}</span>
                    </div>
                  ))}
                  {failures.length > 10 && <div style={{ padding: '6px 0', color: 'var(--nx-text-muted)' }}>…and {failures.length - 10} more failed rows</div>}
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-004: RecordsTab — server-paginated record list for a custom object
// ─────────────────────────────────────────────────────────────────────────────

const RECORDS_PAGE_SIZE = 25;

const CoRowMenu = ({ record, singularName, isActive, canUse, onOpen, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc   = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <span ref={ref} className="nx-row-actions" onClick={e => e.stopPropagation()}>
      <button type="button" className="nx-iconbtn nx-row-action-btn"
        onClick={() => setOpen(o => !o)}
        aria-label={`More actions for ${singularName}`} aria-haspopup="menu" aria-expanded={open}>
        <NexusIcon name="MoreHorizontalFilled" size={16} fill="currentColor" strokeWidth={0} />
      </button>
      {open && (
        <div className="nx-chip-menu" style={{ right: 0, left: 'auto' }} role="menu">
          <button type="button" role="menuitem" className="nx-chip-menu-item"
            onClick={() => { onOpen(record); setOpen(false); }}>
            <NexusIcon name="ArrowUpRight" size={11} /><span>Open</span>
          </button>
          {canUse && isActive && (
            <button type="button" role="menuitem" className="nx-chip-menu-item"
              onClick={() => { onEdit(record.id); setOpen(false); }}>
              <NexusIcon name="Edit" size={11} /><span>Edit</span>
            </button>
          )}
          {canUse && (
            <button type="button" role="menuitem" className="nx-chip-menu-item is-danger"
              onClick={() => { onDelete(record); setOpen(false); }}>
              <NexusIcon name="Trash2" size={11} /><span>Delete</span>
            </button>
          )}
        </div>
      )}
    </span>
  );
};

const RecordsTab = ({ def, wsId, canManage, canUse, toast, onGoToSettings, fullPage = false, onGoBack }) => {
  const [records,       setRecords]       = useState([]);
  const [meta,          setMeta]          = useState({ total: 0, page: 1, limit: RECORDS_PAGE_SIZE, pages: 1 });
  const [loading,       setLoading]       = useState(false);
  const [loadError,     setLoadError]     = useState(null);
  const [search,        setSearch]        = useState('');
  const [sortField,     setSortField]     = useState('updatedAt');
  const [sortDir,       setSortDir]       = useState('desc');
  const [page,          setPage]          = useState(1);
  const [createOpen,    setCreateOpen]    = useState(false);
  const [editId,        setEditId]        = useState(null);
  const [drawerRecord,  setDrawerRecord]  = useState(null);
  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [deleteBusy,    setDeleteBusy]    = useState(false);
  // CO-011d: filter state
  const [filters,      setFilters]      = useState({});
  const [filterOpen,   setFilterOpen]   = useState(false);
  const [pendingField, setPendingField] = useState(null);
  const [pendingOp,    setPendingOp]    = useState('');
  const [pendingVal,   setPendingVal]   = useState('');
  const [pendingVal2,  setPendingVal2]  = useState('');
  const [pendingMulti, setPendingMulti] = useState([]);
  const [sortAnnounce, setSortAnnounce] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [visibleColIds, setVisibleColIds] = useState(null);
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);
  const [viewMode,       setViewMode]       = useState('table');
  const [exportOpen,        setExportOpen]        = useState(false);
  const [completeExporting, setCompleteExporting] = useState(false);
  const [importOpen,     setImportOpen]     = useState(false);
  const [savedViewsOpen,    setSavedViewsOpen]    = useState(false);
  const [saveViewOpen,      setSaveViewOpen]      = useState(false);
  const [activeViewId,      setActiveViewId]      = useState(null);
  const [renameViewTarget,  setRenameViewTarget]  = useState(null);
  const [calendarRecords, setCalendarRecords] = useState([]);
  const [calLoading,      setCalLoading]      = useState(false);
  const [calReady,        setCalReady]        = useState(false);
  const [kanbanRecords,   setKanbanRecords]   = useState([]);
  const [kanbanLoading,   setKanbanLoading]   = useState(false);
  const debRef        = useRef(null);
  const filterBtnRef  = useRef(null);
  const filterPanelRef = useRef(null);
  const colPickerRef  = useRef(null);
  const exportMenuRef = useRef(null);
  const savedViewsRef = useRef(null);
  const seqRef        = useRef(0);
  const calSeqRef     = useRef(0);
  const kanbanSeqRef  = useRef(0);

  const activeFields = (def.fields || []).filter(f => f.status === 'ACTIVE');
  const primaryField = activeFields.find(f => f.isPrimary);
  const displayCols  = visibleColIds === null
    ? getColumnsFromDef(def, 3)
    : visibleColIds.map(id => activeFields.find(f => f.id === id)).filter(Boolean);

  const getPrimary = useCallback((record) => {
    if (primaryField && record.values) return record.values[primaryField.id] || `#${record.id.slice(0, 8)}`;
    return `#${(record.id || '').slice(0, 8)}`;
  }, [primaryField]);

  const load = useCallback(async (pg, sq, sf, sd, flt = {}) => {
    const seq = ++seqRef.current;
    setLoading(true);
    setLoadError(null);
    setRecords([]);
    try {
      const params = { page: pg, limit: RECORDS_PAGE_SIZE, sortField: sf, sortDir: sd };
      if (sq) params.search = sq;
      if (flt && Object.keys(flt).length > 0) {
        params.filters = {};
        for (const [fieldId, entry] of Object.entries(flt)) {
          params.filters[fieldId] = { op: entry.op };
          if (entry.value !== undefined && entry.value !== '') {
            params.filters[fieldId].value = entry.value;
          }
        }
      }
      const res = await coApi.listRecords(wsId, def.id, params);
      if (seq !== seqRef.current) return;
      setRecords(res.data ?? []);
      setMeta(res.meta ?? { total: 0, page: pg, limit: RECORDS_PAGE_SIZE, pages: 1 });
    } catch (err) {
      if (seq !== seqRef.current) return;
      const msg = err?.status === 422
        ? 'Records are unavailable while the object is in Draft status.'
        : extractApiError(err);
      setLoadError(msg);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [wsId, def.id]);

  useEffect(() => {
    setPage(1); setSearch(''); setSortField('updatedAt'); setSortDir('desc'); setFilters({});
    if (def.status !== 'DRAFT') load(1, '', 'updatedAt', 'desc', {});
  }, [def.id]);

  // Fetch records (up to 200) when calendar view is active.
  // Calendar placement uses record.createdAt — no custom DATE field required.
  useEffect(() => {
    if (viewMode !== 'calendar') return;
    const seq = ++calSeqRef.current;
    setCalLoading(true);
    setCalReady(false);
    coApi.listRecords(wsId, def.id, { page: 1, limit: 200, sortField: 'updatedAt', sortDir: 'desc' })
      .then(res => { if (seq === calSeqRef.current) { setCalendarRecords(res.data ?? []); setCalLoading(false); setCalReady(true); } })
      .catch(() => { if (seq === calSeqRef.current) { setCalLoading(false); setCalReady(true); } });
  }, [viewMode, def.id, wsId]);

  // Fetch records (up to backend max 200) for Kanban grouping. Same @Max(200) limit.
  useEffect(() => {
    if (viewMode !== 'kanban') return;
    const hasSelect = (def.fields || []).some(f => f.status === 'ACTIVE' && (f.apiType || f.type) === 'SELECT');
    if (!hasSelect) return;
    const seq = ++kanbanSeqRef.current;
    setKanbanLoading(true);
    coApi.listRecords(wsId, def.id, { page: 1, limit: 200, sortField: 'updatedAt', sortDir: 'desc' })
      .then(res => { if (seq === kanbanSeqRef.current) { setKanbanRecords(res.data ?? []); setKanbanLoading(false); } })
      .catch(() => { if (seq === kanbanSeqRef.current) setKanbanLoading(false); });
  }, [viewMode, def.id, wsId]);

  const goPage = (pg) => { setPage(pg); load(pg, search, sortField, sortDir, filters); };

  const handleSearch = (v) => {
    setSearch(v);
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => { setPage(1); load(1, v, sortField, sortDir, filters); }, 350);
  };

  const toggleSort = (f) => {
    const newDir = sortField === f && sortDir === 'asc' ? 'desc' : 'asc';
    setSortField(f); setSortDir(newDir); setPage(1);
    load(1, search, f, newDir, filters);
    const col = activeFields.find(af => af.id === f)?.label || f;
    setSortAnnounce(`Sorted by ${col}, ${newDir === 'asc' ? 'ascending' : 'descending'}`);
  };

  const refresh = () => load(page, search, sortField, sortDir, filters);

  // CO-011d: filter helpers
  const filterableFields = activeFields.filter(f => !!FILTER_OPS[f.apiType || f.type]);

  const applyFilter = () => {
    if (!pendingField || !pendingOp) return;
    const fType = pendingField.apiType || pendingField.type;
    const opLabel = (FILTER_OPS[fType] || []).find(([o]) => o === pendingOp)?.[1] || pendingOp;
    let value, displayValue;
    if (pendingOp === 'isEmpty') {
      value = undefined; displayValue = '';
    } else if (pendingOp === 'between') {
      if (!pendingVal || !pendingVal2) return;
      value = JSON.stringify([pendingVal, pendingVal2]);
      displayValue = `${pendingVal} – ${pendingVal2}`;
    } else if (pendingOp === 'in' || pendingOp === 'containsAny' || pendingOp === 'containsAll') {
      if (!pendingMulti.length) return;
      value = JSON.stringify(pendingMulti);
      displayValue = pendingMulti.join(', ');
    } else if (fType === 'BOOLEAN') {
      value = pendingVal || 'true';
      displayValue = value === 'true' ? 'Yes' : 'No';
    } else {
      if (!pendingVal) return;
      value = pendingVal;
      displayValue = pendingVal;
    }
    const entry = { op: pendingOp, value, fieldLabel: pendingField.label, opLabel, displayValue };
    const newFilters = { ...filters, [pendingField.id]: entry };
    setFilters(newFilters);
    setPage(1);
    load(1, search, sortField, sortDir, newFilters);
    setFilterOpen(false);
    setPendingField(null); setPendingOp(''); setPendingVal(''); setPendingVal2(''); setPendingMulti([]);
  };

  const removeFilter = (fieldId) => {
    const newFilters = { ...filters };
    delete newFilters[fieldId];
    setFilters(newFilters);
    setPage(1);
    load(1, search, sortField, sortDir, newFilters);
  };

  const clearAllFilters = () => {
    setFilters({});
    setPage(1);
    load(1, search, sortField, sortDir, {});
  };

  const _coExportRows = () => selectedIds.size > 0 ? records.filter(r => selectedIds.has(r.id)) : records;

  const handleExportCsv = () => {
    const rows = _coExportRows();
    if (!rows.length) { toast({ tone: 'warning', icon: 'AlertCircle', title: 'Nothing to export', desc: 'Load or select records first.' }); return; }
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = activeFields.map(f => esc(f.label)).join(',');
    const body   = rows.map(r => activeFields.map(f => esc(r.values?.[f.id])).join(',')).join('\n');
    const blob = new Blob(['﻿' + header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement('a'), { href: url, download: `${def.pluralName.toLowerCase().replace(/\s+/g, '-')}.csv` }).click();
    URL.revokeObjectURL(url);
    toast({ tone: 'success', icon: 'File', title: `Exported ${rows.length} record${rows.length !== 1 ? 's' : ''} (CSV)` });
  };

  const handleExportExcel = () => {
    const rows = _coExportRows();
    if (!rows.length) { toast({ tone: 'warning', icon: 'AlertCircle', title: 'Nothing to export', desc: 'Load or select records first.' }); return; }
    const cols = activeFields.map(f => ({ label: f.label, get: r => formatFieldValue(r.values?.[f.id] ?? null, f.apiType || f.type, f.options || []) }));
    if (typeof window._toExcelXML === 'function' && typeof window._download === 'function') {
      const stamp = new Date().toISOString().slice(0, 10);
      window._download(`${def.pluralName.toLowerCase().replace(/\s+/g, '-')}-${stamp}.xls`, 'application/vnd.ms-excel;charset=utf-8;', window._toExcelXML(def.pluralName, rows, cols));
    } else {
      handleExportCsv();
    }
    toast({ tone: 'success', icon: 'FileSpreadsheet', title: `Exported ${rows.length} record${rows.length !== 1 ? 's' : ''} (Excel)` });
  };

  const handleExportPdf = () => {
    const rows = _coExportRows();
    if (!rows.length) { toast({ tone: 'warning', icon: 'AlertCircle', title: 'Nothing to export', desc: 'Load or select records first.' }); return; }
    const cols = activeFields.map(f => ({ label: f.label, get: r => formatFieldValue(r.values?.[f.id] ?? null, f.apiType || f.type, f.options || []) }));
    if (typeof window._printPDF === 'function') {
      const ok = window._printPDF(`${def.pluralName} Export`, rows, cols);
      if (!ok) { toast({ tone: 'warning', icon: 'AlertCircle', title: 'Popup blocked', desc: 'Allow pop-ups for this site to export as PDF.' }); return; }
    }
    toast({ tone: 'success', icon: 'Printer', title: `PDF export ready (${rows.length} record${rows.length !== 1 ? 's' : ''})` });
  };

  // Complete Export — fetches ALL records (all pages) with system columns + all active fields.
  // Unlike the regular export, this always fetches server-side and ignores current pagination.
  const handleCompleteExport = async (fmt) => {
    if (completeExporting) return;
    setCompleteExporting(true);
    try {
      // Fetch all records, paginating until exhausted (cap at 10 000 for safety)
      const PAGE_LIMIT = 1000;
      const MAX_RECORDS = 10000;
      let allRecords = [];
      let page = 1;
      while (allRecords.length < MAX_RECORDS) {
        const res = await coApi.listRecords(wsId, def.id, { page, limit: PAGE_LIMIT, sortField: 'updatedAt', sortDir: 'desc' });
        const batch = res.data ?? [];
        allRecords.push(...batch);
        const total = res.meta?.total ?? 0;
        if (allRecords.length >= total || batch.length < PAGE_LIMIT) break;
        page++;
      }

      const stamp = new Date().toISOString().slice(0, 10);
      const base  = `${def.pluralName.toLowerCase().replace(/\s+/g, '-')}-complete-${stamp}`;

      // Columns: system metadata first, then every active custom field
      const sysCols = [
        { label: 'Record ID', get: r => r.id ?? '' },
        { label: 'Created',   get: r => r.createdAt ? fmtAbsTime(r.createdAt) : '' },
        { label: 'Updated',   get: r => r.updatedAt ? fmtAbsTime(r.updatedAt) : '' },
      ];
      const fieldCols = activeFields.map(f => ({
        label: f.label,
        get:   r => formatFieldValue(r.values?.[f.id] ?? null, f.apiType || f.type, f.options || []),
      }));
      const allCols = [...sysCols, ...fieldCols];

      if (fmt === 'xlsx') {
        const XL = window.XLSX;
        if (!XL) throw new Error('XLSX library not available — try CSV instead');
        const wb = XL.utils.book_new();
        const header = allCols.map(c => c.label);
        const dataRows = allRecords.map(r => allCols.map(c => {
          const v = c.get(r);
          return v === '—' ? '' : v; // convert fmtAbsTime/formatFieldValue em-dashes to empty
        }));
        const ws = XL.utils.aoa_to_sheet([header, ...dataRows]);
        ws['!cols'] = header.map(() => ({ wch: 20 }));
        XL.utils.book_append_sheet(wb, ws, def.pluralName.slice(0, 31));
        XL.writeFile(wb, base + '.xlsx');
      } else {
        const esc = v => { const s = String(v === '—' ? '' : (v ?? '')); return /[",\n]/.test(s) ? '"' + s.replace(/"/g,'""') + '"' : s; };
        const header  = allCols.map(c => esc(c.label)).join(',');
        const body    = allRecords.map(r => allCols.map(c => esc(c.get(r))).join(',')).join('\n');
        if (typeof window._download === 'function') {
          window._download(base + '.csv', 'text/csv;charset=utf-8;', '﻿' + header + '\n' + body);
        } else {
          const blob = new Blob(['﻿' + header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          Object.assign(document.createElement('a'), { href: url, download: base + '.csv' }).click();
          URL.revokeObjectURL(url);
        }
      }

      toast({ tone: 'success', icon: 'File', title: 'Complete Export ready', desc: `${allRecords.length} ${def.pluralName} · ${allCols.length} columns` });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Complete Export failed', desc: err?.message || 'An unexpected error occurred. Please try again.' });
    } finally {
      setCompleteExporting(false);
    }
  };

  const handleExport = handleExportCsv;

  const handleBulkDelete = async () => {
    setBulkDeleteBusy(true);
    const ids = [...selectedIds];
    let failed = 0;
    for (const id of ids) {
      try { await coApi.deleteRecord(wsId, def.id, id); } catch { failed++; }
    }
    setBulkDeleteOpen(false);
    setBulkDeleteBusy(false);
    setSelectedIds(new Set());
    toast(failed > 0
      ? { tone: 'error', icon: 'AlertCircle', title: `${ids.length - failed} deleted, ${failed} failed` }
      : { tone: 'success', icon: 'Check', title: `${ids.length} record${ids.length !== 1 ? 's' : ''} deleted` }
    );
    load(page, search, sortField, sortDir, filters);
  };

  useEffect(() => {
    if (!filterOpen) return;
    const handler = (e) => {
      if (!filterBtnRef.current?.contains(e.target) && !filterPanelRef.current?.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filterOpen]);

  useEffect(() => {
    if (!colPickerOpen) return;
    const handler = e => { if (!colPickerRef.current?.contains(e.target)) setColPickerOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [colPickerOpen]);

  useEffect(() => {
    if (!exportOpen) return;
    const handler = e => { if (!exportMenuRef.current?.contains(e.target)) setExportOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [exportOpen]);

  useEffect(() => {
    if (!savedViewsOpen) return;
    const handler = e => { if (!savedViewsRef.current?.contains(e.target)) setSavedViewsOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [savedViewsOpen]);

  const handleCreated = () => {
    setCreateOpen(false);
    toast({ tone: 'success', icon: 'Check', title: `${def.singularName} created` });
    setPage(1); load(1, search, sortField, sortDir, filters);
  };

  const handleUpdated = (r) => {
    setEditId(null);
    toast({ tone: 'success', icon: 'Check', title: `${def.singularName} updated` });
    refresh();
    if (drawerRecord?.id === r?.id) setDrawerRecord(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await coApi.deleteRecord(wsId, def.id, deleteTarget.id);
      toast({ tone: 'success', icon: 'Check', title: `${def.singularName} deleted` });
      if (drawerRecord?.id === deleteTarget.id) setDrawerRecord(null);
      setDeleteTarget(null);
      load(page, search, sortField, sortDir, filters);
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Delete failed', desc: extractApiError(err) });
    } finally {
      setDeleteBusy(false);
    }
  };

  const pm = paginationMeta(meta.total, page, RECORDS_PAGE_SIZE);
  const coTone = coToneForId;

  const colCount = 1 + 1 + displayCols.length + 1 + 1; // checkbox + primary + data + updated + actions
  const allSelected = records.length > 0 && records.every(r => selectedIds.has(r.id));
  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(records.map(r => r.id)));
  const toggleOne = id => setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const coSort = { field: sortField, dir: sortDir };
  const colLabel = s => s ? s.replace(/\b\w/g, c => c.toUpperCase()) : s;

  if (def.status === 'DRAFT') {
    if (fullPage) {
      return (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyState icon="Lock" title="Object not published"
            desc="Publish this object in Settings to start creating records." />
        </div>
      );
    }
    return (
      <div style={{ textAlign: 'center', padding: '40px 24px',
        background: 'var(--nx-surface-alt)', borderRadius: 8, border: '1px solid var(--nx-border)' }}>
        <NexusIcon name="Lock" size={24} style={{ color: 'var(--nx-text-muted)', marginBottom: 12 }} />
        <p style={{ fontWeight: 600, margin: '0 0 6px' }}>Object not published</p>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', margin: '0 0 16px' }}>
          Publish this object in Settings to start creating records.
        </p>
        {canManage && onGoToSettings && (
          <Btn variant="ghost" icon="Settings" onClick={onGoToSettings}>Go to Settings</Btn>
        )}
      </div>
    );
  }

  // ── Shared JSX fragments ────────────────────────────────────────────────────
  const filterCount = Object.keys(filters).length;

  const nonPrimaryCols   = activeFields.filter(f => !f.isPrimary);
  const defaultColArr    = getColumnsFromDef(def, 3).map(c => c.id);
  const defaultColIds    = new Set(defaultColArr);
  // ordered picker list: checked columns in user order, then unchecked in definition order
  const _checkedColIds   = visibleColIds ?? defaultColArr;
  const _checkedColSet   = new Set(_checkedColIds);
  const orderedPickerCols = [
    ..._checkedColIds.map(id => nonPrimaryCols.find(f => f.id === id)).filter(Boolean),
    ...nonPrimaryCols.filter(f => !_checkedColSet.has(f.id)),
  ];

  const dateFields    = activeFields.filter(f => (f.apiType || f.type) === 'DATE');
  const hasDateField  = dateFields.length > 0;
  const calendarField = dateFields[0];

  const selectFields   = activeFields.filter(f => (f.apiType || f.type) === 'SELECT');
  const hasSelectField = selectFields.length > 0;
  const kanbanField    = selectFields[0] || null;

  const currentViewState = { search, filters, sortField, sortDir, visibleColIds };

  const handleApplyView = (view) => {
    setActiveViewId(view.id);
    if (view.search !== undefined) { setSearch(view.search); }
    if (view.filters !== undefined) setFilters(view.filters || {});
    if (view.sortField) { setSortField(view.sortField); setSortDir(view.sortDir || 'desc'); }
    setVisibleColIds(Array.isArray(view.visibleColIds) && view.visibleColIds.length > 0 ? view.visibleColIds : null);
    setPage(1);
    load(1, view.search || '', view.sortField || 'updatedAt', view.sortDir || 'desc', view.filters || {});
    toast({ tone: 'success', icon: 'CheckCircle', title: `View "${view.name}" applied` });
  };

  const handleUpdateView = (view) => {
    const storageKey = `co-views-${def.id}`;
    try {
      const views = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const updated = views.map(v => v.id === view.id ? { ...v, ...currentViewState } : v);
      localStorage.setItem(storageKey, JSON.stringify(updated));
      toast({ tone: 'success', icon: 'Bookmark', title: `View "${view.name}" updated` });
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to update view' });
    }
  };

  const viewSwitcherEl = (
    <div className="nx-segment">
      <IconBtn icon="List" active={viewMode === 'table'} title="Table view" onClick={() => setViewMode('table')} />
      <IconBtn icon="LayoutGrid" active={viewMode === 'kanban'} title="Kanban view" onClick={() => setViewMode('kanban')} />
      <IconBtn icon="Calendar" active={viewMode === 'calendar'} title="Calendar view" onClick={() => setViewMode('calendar')} />
    </div>
  );

  const coExportEl = (
    <span style={{ position: 'relative' }} ref={exportMenuRef}>
      <Btn variant="secondary" icon="Download" disabled={completeExporting}
        onClick={() => setExportOpen(o => !o)} aria-haspopup="menu" aria-expanded={exportOpen}>
        {completeExporting ? 'Building export…' : 'Export'}
      </Btn>
      {exportOpen && (
        <div className="nx-chip-menu" role="menu" style={{ right: 0, left: 'auto', top: 'calc(100% + 6px)', minWidth: 210 }}>
          <button type="button" role="menuitem" className="nx-chip-menu-item"
            onClick={() => { setExportOpen(false); handleExportCsv(); }}>
            <NexusIcon name="FileText" size={12} /><span>Export as CSV</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--nx-text-muted)' }}>{_coExportRows().length}</span>
          </button>
          <button type="button" role="menuitem" className="nx-chip-menu-item"
            onClick={() => { setExportOpen(false); handleExportExcel(); }}>
            <NexusIcon name="FileSpreadsheet" size={12} /><span>Export as Excel</span>
          </button>
          <button type="button" role="menuitem" className="nx-chip-menu-item"
            onClick={() => { setExportOpen(false); handleExportPdf(); }}>
            <NexusIcon name="Printer" size={12} /><span>Export as PDF</span>
          </button>
          <div style={{ borderTop: '1px solid var(--nx-border)', margin: '4px 0' }} />
          <button type="button" role="menuitem" className="nx-chip-menu-item"
            onClick={() => { setExportOpen(false); handleCompleteExport('xlsx'); }}>
            <NexusIcon name="Package" size={12} /><span>Complete Export (XLSX)</span>
          </button>
          <button type="button" role="menuitem" className="nx-chip-menu-item"
            onClick={() => { setExportOpen(false); handleCompleteExport('csv'); }}>
            <NexusIcon name="Archive" size={12} /><span>Complete Export (CSV)</span>
          </button>
        </div>
      )}
    </span>
  );

  const coImportEl = canUse && def.status === 'ACTIVE' ? (
    <>
      <Btn variant="ghost" icon="UploadCloud" onClick={() => setImportOpen(true)}>Import</Btn>
      {importOpen && (
        <CoImportWizard
          def={def} wsId={wsId} toast={toast}
          onClose={() => setImportOpen(false)}
          onDone={() => { setPage(1); load(1, search, sortField, sortDir, filters); }}
        />
      )}
    </>
  ) : null;

  const coSavedViewsEl = (
    <>
      <div style={{ position: 'relative' }} ref={savedViewsRef}>
        <Btn variant="ghost" icon="Star"
          style={activeViewId ? { color: 'var(--nx-accent)' } : undefined}
          onClick={() => setSavedViewsOpen(o => !o)}>
          Views
        </Btn>
        {savedViewsOpen && (
          <CoSavedViewsPanel
            defId={def.id}
            activeViewId={activeViewId}
            onApply={(view) => { handleApplyView(view); setSavedViewsOpen(false); }}
            onUpdate={(view) => { handleUpdateView(view); setSavedViewsOpen(false); }}
            onRename={(view) => { setSavedViewsOpen(false); setRenameViewTarget(view); }}
            onDelete={(id) => { if (id === activeViewId) setActiveViewId(null); }}
            onClose={() => setSavedViewsOpen(false)}
          />
        )}
      </div>
      {renameViewTarget && (
        <CoRenameViewModal
          defId={def.id}
          view={renameViewTarget}
          onRenamed={(id, newName) => {
            setRenameViewTarget(null);
            toast({ tone: 'success', icon: 'Bookmark', title: `View renamed to "${newName}"` });
          }}
          onClose={() => setRenameViewTarget(null)}
        />
      )}
    </>
  );

  const coSaveViewEl = (
    <>
      <Btn variant="secondary" icon="Bookmark" onClick={() => setSaveViewOpen(true)}>Save View</Btn>
      {saveViewOpen && (
        <CoSaveViewModal
          defId={def.id}
          currentState={currentViewState}
          onSaved={(newId) => { setSaveViewOpen(false); setActiveViewId(newId); toast({ tone: 'success', icon: 'Bookmark', title: 'View saved' }); }}
          onClose={() => setSaveViewOpen(false)}
        />
      )}
    </>
  );

  const gridEl = (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14, padding: '4px 0' }}>
      {loading && records.length === 0 && (
        <div style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 40, color: 'var(--nx-text-muted)' }}>
          <NexusIcon name="Loader" size={18} style={{ animation: 'nx-spin 1s linear infinite' }} />
          Loading {def.pluralName}…
        </div>
      )}
      {!loading && !loadError && records.length === 0 && (
        <div style={{ gridColumn: '1/-1' }}>
          <EmptyState icon={def.icon || 'Database'} title={`No ${def.pluralName} yet`}
            desc={canUse && def.status === 'ACTIVE' ? `Create the first ${def.singularName} to get started.` : undefined}
            action={canUse && def.status === 'ACTIVE' ? <Btn size="sm" icon="Plus" onClick={() => setCreateOpen(true)}>New {def.singularName}</Btn> : undefined}
          />
        </div>
      )}
      {records.map(record => {
        const label = getPrimary(record);
        const isChecked = selectedIds.has(record.id);
        return (
          <div key={record.id}
            style={{
              background: 'var(--nx-surface)', border: isChecked ? '2px solid var(--nx-accent)' : '1px solid var(--nx-border)',
              borderRadius: 10, padding: 16, cursor: 'pointer', position: 'relative',
              boxShadow: '0 1px 4px rgba(0,0,0,.06)',
            }}
            onClick={() => setDrawerRecord(record)}>
            <div style={{ position: 'absolute', top: 10, right: 10 }} onClick={e => e.stopPropagation()}>
              <input type="checkbox" className="nx-checkbox" checked={isChecked} onChange={() => toggleOne(record.id)} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingRight: 24 }}>
              <Avatar name={label} tone={coTone(record.id)} size={32} style={{ flexShrink: 0 }} />
              <span style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
            </div>
            {displayCols.slice(0, 4).map(f => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 4, fontSize: 12 }}>
                <span style={{ color: 'var(--nx-text-muted)', minWidth: 70, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.label}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  <FieldValueDisplay value={record.values?.[f.id] ?? null} field={f} />
                </span>
              </div>
            ))}
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--nx-text-muted)' }}>{fmtAbsTime(record.updatedAt)}</div>
          </div>
        );
      })}
    </div>
  );

  // Calendar: use the shared RecordCalendar component (same as People/Companies/Deals).
  // Events are placed by record.createdAt — the system creation timestamp — not a custom
  // DATE field. Uses new Date(createdAt) since createdAt is a full ISO timestamp (not
  // date-only), so UTC timezone handling is correct.
  const calendarEl = (() => {
    if (calLoading || !calReady) return (
      <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--nx-text-muted)', fontSize: 13 }}>
        Loading calendar records…
      </div>
    );
    const Cal = window.RecordCalendar;
    if (!Cal) return (
      <div style={{ padding: '20px', color: 'var(--nx-text-muted)', fontSize: 12 }}>
        Calendar component not yet initialized. Please refresh.
      </div>
    );
    // Navigate to the month of the most recently created record.
    const calAnchor = (() => {
      let maxKey = -1;
      let best = null;
      for (const r of calendarRecords) {
        if (!r.createdAt) continue;
        const dt = new Date(r.createdAt);
        if (isNaN(dt.getTime())) continue;
        const key = dt.getFullYear() * 10000 + (dt.getMonth() + 1) * 100 + dt.getDate();
        if (key > maxKey) { maxKey = key; best = new Date(dt.getFullYear(), dt.getMonth(), 1); }
      }
      return best;
    })();
    return (
      <Cal
        key={def.id}
        rows={calendarRecords}
        initialAnchor={calAnchor}
        getSlot={(r, d, month, year) => {
          if (!r.createdAt) return false;
          const dt = new Date(r.createdAt);
          return dt.getFullYear() === year && dt.getMonth() === month && dt.getDate() === d;
        }}
        getDot={() => 'var(--nx-accent)'}
        getLabel={(r) => getPrimary(r)}
        getTooltip={(r) => getPrimary(r)}
        onOpen={setDrawerRecord}
      />
    );
  })();

  const kanbanEl = hasSelectField && kanbanField ? (() => {
    if (kanbanLoading) return (
      <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--nx-text-muted)', fontSize: 13 }}>
        Loading {def.pluralName}…
      </div>
    );
    const optionValues = (kanbanField.options || []).map(o => (typeof o === 'object' ? o.value : o));
    const columns = [...optionValues, null]; // options + null for unassigned
    const byValue = {};
    kanbanRecords.forEach(r => {
      const v = r.values?.[kanbanField.id] ?? null;
      const key = v === null || v === '' ? null : v;
      if (!byValue[key]) byValue[key] = [];
      byValue[key].push(r);
    });
    return (
      <div className="nx-kanban">
        {columns.map(col => {
          const colRecs = byValue[col] || [];
          const label = col ?? '(Unassigned)';
          return (
            <div key={label} className="nx-kanban-col">
              <div className="nx-kanban-head">
                <span className="nx-kanban-name">{label}</span>
                <span className="nx-kanban-count">{colRecs.length}</span>
              </div>
              <div className="nx-kanban-body">
                {colRecs.map(r => {
                  const primaryLabel = getPrimary(r);
                  const extraCols = displayCols.filter(f => f.id !== kanbanField.id);
                  return (
                    <button key={r.id} className="nx-kanban-card" type="button" onClick={() => setDrawerRecord(r)}>
                      <div className="nx-kanban-card-head">
                        <Avatar name={primaryLabel} tone={coTone(r.id)} size={22} style={{ flexShrink: 0 }} />
                        <div className="nx-kanban-card-meta">
                          <span className="nx-kanban-card-title">{primaryLabel}</span>
                          {extraCols[0] && (
                            <span className="nx-kanban-card-sub">
                              <FieldValueDisplay value={r.values?.[extraCols[0].id] ?? null} field={extraCols[0]} />
                            </span>
                          )}
                        </div>
                      </div>
                      {extraCols.slice(1, 3).map(f => (
                        <div key={f.id} className="nx-kanban-card-row">
                          <span className="nx-muted nx-mini">{f.label}</span>
                          <span style={{ flex: 1 }} />
                          <span className="nx-muted nx-mini">
                            <FieldValueDisplay value={r.values?.[f.id] ?? null} field={f} />
                          </span>
                        </div>
                      ))}
                      <div className="nx-kanban-card-row">
                        <span style={{ flex: 1 }} />
                        <span className="nx-muted nx-mini">{fmtAbsTime(r.updatedAt)}</span>
                      </div>
                    </button>
                  );
                })}
                {colRecs.length === 0 && (
                  <div className="nx-kanban-empty">
                    <NexusIcon name="Inbox" size={14} />
                    <span className="nx-muted nx-mini">No records</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  })() : null;

  const colPickerEl = (
    <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 200, background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 8, minWidth: 260, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid var(--nx-border)' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text)' }}>Columns</span>
        <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', color: 'var(--nx-text-muted)' }} onClick={() => setColPickerOpen(false)} aria-label="Close column manager">
          <NexusIcon name="X" size={13} />
        </button>
      </div>
      {/* Scrollable body: primary locked row first, then draggable rows — matches People's ColumnManager structure */}
      <div style={{ padding: '4px 0', maxHeight: 360, overflowY: 'auto' }}>
        {primaryField && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', fontSize: 13, color: 'var(--nx-text-muted)', userSelect: 'none' }}>
            <NexusIcon name="Lock" size={12} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{primaryField.label}</span>
          </div>
        )}
        {nonPrimaryCols.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', padding: '8px 12px' }}>No additional fields.</div>
        )}
        {orderedPickerCols.map((f, idx) => {
          const checked = _checkedColSet.has(f.id);
          return (
            <div key={f.id}
              draggable
              onDragStart={e => e.dataTransfer.setData('text/plain', String(idx))}
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                const from = parseInt(e.dataTransfer.getData('text/plain'));
                if (isNaN(from) || from === idx) return;
                const next = [...orderedPickerCols];
                next.splice(idx, 0, next.splice(from, 1)[0]);
                const newChecked = next.filter(c => _checkedColSet.has(c.id)).map(c => c.id);
                setVisibleColIds(
                  newChecked.length === defaultColArr.length && newChecked.every((id, i) => id === defaultColArr[i])
                    ? null : newChecked
                );
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', fontSize: 13, color: 'var(--nx-text)', cursor: 'grab', borderRadius: 4 }}
            >
              <span style={{ color: 'var(--nx-text-muted)', fontSize: 11, lineHeight: 1, flexShrink: 0 }} aria-hidden>⠿</span>
              <input type="checkbox" className="nx-checkbox" checked={checked}
                aria-label={(checked ? 'Hide' : 'Show') + ' ' + f.label}
                onChange={e => setVisibleColIds(prev => {
                  const cur = prev ?? defaultColArr;
                  const next = e.target.checked ? [...cur, f.id] : cur.filter(id => id !== f.id);
                  return next.length === defaultColArr.length && next.every((id, i) => id === defaultColArr[i]) ? null : next;
                })} />
              <span style={{ flex: 1 }}>{f.label}</span>
            </div>
          );
        })}
      </div>
      <div style={{ borderTop: '1px solid var(--nx-border)', padding: '6px 12px' }}>
        <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--nx-accent)', padding: 0 }} onClick={() => setVisibleColIds(null)}>Restore defaults</button>
      </div>
    </div>
  );

  const exportBtnEl = (
    <IconBtn icon="Download"
      title={selectedIds.size > 0 ? `Export ${selectedIds.size} selected` : 'Export current page (CSV)'}
      onClick={handleExport} />
  );

  const bulkBarEl = selectedIds.size > 0 ? (
    <div className="nx-bulk-bar">
      <span><strong>{selectedIds.size}</strong> {selectedIds.size === 1 ? def.singularName : def.pluralName} selected</span>
      <Btn icon="Download" variant="ghost" onClick={handleExport}>Export</Btn>
      {canUse && (
        <Btn icon="Trash2" variant="danger-ghost" onClick={() => setBulkDeleteOpen(true)}>Delete</Btn>
      )}
      <span style={{ flex: 1 }} />
      <IconBtn icon="X" onClick={() => setSelectedIds(new Set())} title="Clear selection" />
    </div>
  ) : null;

  const filterPanelDropdown = filterOpen ? (
    <div ref={filterPanelRef} style={{
      position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 120,
      background: 'var(--nx-surface)', border: '1px solid var(--nx-border)',
      borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: 16,
      minWidth: 280, maxWidth: 360,
    }}>
      <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--nx-text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
        Filter by field
      </div>
      <select
        value={pendingField?.id || ''}
        onChange={e => {
          const f = filterableFields.find(x => x.id === e.target.value) || null;
          setPendingField(f); setPendingOp(''); setPendingVal(''); setPendingVal2(''); setPendingMulti([]);
        }}
        style={{ width: '100%', marginBottom: 8, padding: '6px 8px', fontSize: 13,
          border: '1px solid var(--nx-border)', borderRadius: 6,
          background: 'var(--nx-surface)', color: 'var(--nx-text)' }}>
        <option value="">Select field…</option>
        {filterableFields.map(f => (
          <option key={f.id} value={f.id}>{f.label}</option>
        ))}
      </select>
      {pendingField && (() => {
        const fType = pendingField.apiType || pendingField.type;
        const ops = FILTER_OPS[fType] || [];
        return (
          <select
            value={pendingOp}
            onChange={e => { setPendingOp(e.target.value); setPendingVal(''); setPendingVal2(''); setPendingMulti([]); }}
            style={{ width: '100%', marginBottom: 8, padding: '6px 8px', fontSize: 13,
              border: '1px solid var(--nx-border)', borderRadius: 6,
              background: 'var(--nx-surface)', color: 'var(--nx-text)' }}>
            <option value="">Select operator…</option>
            {ops.map(([op, label]) => <option key={op} value={op}>{label}</option>)}
          </select>
        );
      })()}
      {pendingField && pendingOp && pendingOp !== 'isEmpty' && (() => {
        const fType = pendingField.apiType || pendingField.type;
        const inputStyle = { width: '100%', padding: '6px 8px', fontSize: 13,
          border: '1px solid var(--nx-border)', borderRadius: 6,
          background: 'var(--nx-surface)', color: 'var(--nx-text)', boxSizing: 'border-box' };
        if (fType === 'BOOLEAN') return (
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            {[['true','Yes'],['false','No']].map(([v,l]) => (
              <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }}>
                <input type="radio" name="bool-filter" value={v}
                  checked={pendingVal === v} onChange={() => setPendingVal(v)} />
                {l}
              </label>
            ))}
          </div>
        );
        if ((fType === 'SELECT' && pendingOp === 'in') || fType === 'MULTI_SELECT') {
          const options = pendingField.options || [];
          return (
            <div style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid var(--nx-border)',
              borderRadius: 6, marginBottom: 8, padding: '4px 0' }}>
              {options.length === 0
                ? <div style={{ padding: '6px 10px', fontSize: 12, color: 'var(--nx-text-muted)' }}>No options defined</div>
                : options.map(opt => {
                  const val = typeof opt === 'object' ? opt.value : opt;
                  return (
                    <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6,
                      padding: '4px 10px', fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" value={val}
                        checked={pendingMulti.includes(val)}
                        onChange={e => setPendingMulti(prev =>
                          e.target.checked ? [...prev, val] : prev.filter(x => x !== val))} />
                      {val}
                    </label>
                  );
                })}
            </div>
          );
        }
        if (fType === 'SELECT' && pendingOp === 'eq') {
          const options = pendingField.options || [];
          return (
            <select value={pendingVal} onChange={e => setPendingVal(e.target.value)}
              style={{ ...inputStyle, marginBottom: 8 }}>
              <option value="">Select option…</option>
              {options.map(opt => {
                const val = typeof opt === 'object' ? opt.value : opt;
                return <option key={val} value={val}>{val}</option>;
              })}
            </select>
          );
        }
        const inputType = fType === 'NUMBER' ? 'number' : fType === 'DATE' ? 'date' : 'text';
        if (pendingOp === 'between') return (
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <input type={inputType} value={pendingVal} onChange={e => setPendingVal(e.target.value)}
              placeholder="From" style={{ ...inputStyle, width: '50%' }} />
            <input type={inputType} value={pendingVal2} onChange={e => setPendingVal2(e.target.value)}
              placeholder="To" style={{ ...inputStyle, width: '50%' }} />
          </div>
        );
        return (
          <input type={inputType} value={pendingVal} onChange={e => setPendingVal(e.target.value)}
            placeholder="Value…" style={{ ...inputStyle, marginBottom: 8 }} />
        );
      })()}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <Btn size="sm" variant="ghost" onClick={() => setFilterOpen(false)}>Cancel</Btn>
        <Btn size="sm" variant="primary" onClick={applyFilter}
          disabled={!pendingField || !pendingOp}>Apply filter</Btn>
      </div>
    </div>
  ) : null;

  const filterChipsEl = Object.entries(filters).map(([fieldId, entry]) => (
    <span key={fieldId} className="nx-chip is-active" style={{ gap: 5, cursor: 'default' }}>
      <span style={{ fontWeight: 500 }}>{entry.fieldLabel}:</span>
      <strong>{entry.opLabel}{entry.displayValue ? ` "${entry.displayValue}"` : ''}</strong>
      <button type="button" onClick={() => removeFilter(fieldId)}
        aria-label={`Remove ${entry.fieldLabel} filter`}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 0 1px',
          color: 'inherit', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
        <NexusIcon name="X" size={9} />
      </button>
    </span>
  ));

  const tableEl = (
    <table className="nx-table">
      <thead>
        <tr>
          <th className="nx-th-check">
            <input type="checkbox" className="nx-checkbox" checked={allSelected} onChange={toggleAll} />
          </th>
          {/* CO-011d: primary field sorts by field ID (P1-01 fix); was 'createdAt' */}
          <SortableTh field={primaryField ? primaryField.id : 'createdAt'}
            sort={coSort} onSort={toggleSort} sticky>
            {colLabel(primaryField ? primaryField.label : def.singularName)}
          </SortableTh>
          {/* CO-011d: custom field columns use SortableTh except MULTI_SELECT (P0-03 fix) */}
          {displayCols.map(f => {
            const fType = f.apiType || f.type;
            const fieldIcon = getFieldTypeConfig(fType).icon;
            const isSortable = fType !== 'MULTI_SELECT';
            if (isSortable) return (
              <SortableTh key={f.id} field={f.id} icon={fieldIcon}
                sort={coSort} onSort={toggleSort} style={{ width: 150 }}>
                {colLabel(f.label)}
              </SortableTh>
            );
            return (
              <th key={f.id} scope="col" style={{ width: 150 }}>
                <span className="nx-th-inner">
                  <NexusIcon name={fieldIcon} size={12} />
                  <span>{colLabel(f.label)}</span>
                </span>
              </th>
            );
          })}
          <SortableTh field="updatedAt" sort={coSort} onSort={toggleSort} style={{ width: 120 }}>
            Updated
          </SortableTh>
          <th className="nx-th-end" scope="col">Actions</th>
        </tr>
      </thead>
      <tbody>
        {loading && records.length === 0 && (
          <tr>
            <td colSpan={colCount}>
              <div className="nx-empty" style={{ gap: 8 }}>
                <NexusIcon name="Loader" size={18}
                  style={{ animation: 'nx-spin 1s linear infinite', color: 'var(--nx-text-muted)' }} />
                <span style={{ color: 'var(--nx-text-muted)' }}>Loading {def.pluralName}…</span>
              </div>
            </td>
          </tr>
        )}
        {loadError && !loading && (
          <tr>
            <td colSpan={colCount}>
              <div className="nx-empty" style={{ flexDirection: 'column', gap: 8 }}>
                <NexusIcon name="AlertCircle" size={18} style={{ color: 'var(--nx-danger)' }} />
                <span>{loadError}</span>
                <Btn size="sm" variant="ghost" icon="RotateCcw" onClick={refresh}>Retry</Btn>
              </div>
            </td>
          </tr>
        )}
        {!loading && !loadError && records.length === 0 && (
          <tr>
            <td colSpan={colCount}>
              {/* CO-011d: differentiate no-records vs search-empty vs filter-empty */}
              {(() => {
                const hasFilters = Object.keys(filters).length > 0;
                if (search) return (
                  <EmptyState icon="Search"
                    title={`No ${def.pluralName} match "${search}"`}
                    desc="Try a different search or clear the filter."
                    action={<Btn size="sm" variant="ghost" onClick={() => handleSearch('')}>Clear search</Btn>}
                  />
                );
                if (hasFilters) return (
                  <EmptyState icon="Filter"
                    title={`No ${def.pluralName} match the active filters`}
                    desc="Adjust your filters or clear them to see all records."
                    action={<Btn size="sm" variant="ghost" onClick={clearAllFilters}>Clear filters</Btn>}
                  />
                );
                return (
                  <EmptyState icon={def.icon || 'Database'}
                    title={`No ${def.pluralName} yet`}
                    desc={canUse && def.status === 'ACTIVE'
                      ? `Create the first ${def.singularName} to get started.` : undefined}
                    action={canUse && def.status === 'ACTIVE'
                      ? <Btn size="sm" icon="Plus" onClick={() => setCreateOpen(true)}>New {def.singularName}</Btn>
                      : undefined}
                  />
                );
              })()}
            </td>
          </tr>
        )}
        {records.map(record => {
          const label = getPrimary(record);
          const isChecked = selectedIds.has(record.id);
          return (
            <tr key={record.id} className={`nx-tr${isChecked ? ' is-checked' : ''}`} style={{ cursor: 'pointer' }}
              onClick={() => setDrawerRecord(record)}>
              <td className="nx-td-check" onClick={e => e.stopPropagation()}>
                <input type="checkbox" className="nx-checkbox" checked={isChecked}
                  onChange={() => toggleOne(record.id)} />
              </td>
              <td className="is-sticky">
                <button className="nx-row-name" type="button"
                  onClick={e => { e.stopPropagation(); setDrawerRecord(record); }}>
                  <Avatar name={label} tone={coTone(record.id)} size={22} style={{ flexShrink: 0 }} />
                  <span className="nx-row-name-text">{label}</span>
                  <NexusIcon name="ArrowUpRight" size={11} className="nx-row-arrow" style={{ flexShrink: 0 }} />
                </button>
              </td>
              {displayCols.map(f => (
                <td key={f.id}>
                  <span className="nx-cell">
                    <FieldValueDisplay value={record.values?.[f.id] ?? null} field={f} />
                  </span>
                </td>
              ))}
              <td>
                <span className="nx-cell nx-muted" style={{ fontSize: 12 }}>
                  {fmtAbsTime(record.updatedAt)}
                </span>
              </td>
              <td className="nx-td-end">
                <CoRowMenu
                  record={record}
                  singularName={def.singularName}
                  isActive={def.status === 'ACTIVE'}
                  canUse={canUse}
                  onOpen={setDrawerRecord}
                  onEdit={setEditId}
                  onDelete={setDeleteTarget}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const modalsEl = (
    <>
      {createOpen && (
        <CoRecordFormDrawer
          def={def} wsId={wsId}
          mode="create"
          onSaved={r => { setCreateOpen(false); handleCreated(r); }}
          onClose={() => setCreateOpen(false)}
          toast={toast}
        />
      )}
      {editId && (
        <CoRecordFormDrawer
          def={def} wsId={wsId}
          mode="edit" recordId={editId}
          onSaved={r => { setEditId(null); handleUpdated(r); }}
          onClose={() => setEditId(null)}
          toast={toast}
        />
      )}
      {drawerRecord && !editId && (
        <RecordDetailDrawer
          def={def} wsId={wsId} record={drawerRecord}
          canManage={canUse} toast={toast}
          onClose={() => setDrawerRecord(null)}
          onUpdated={(r) => { refresh(); setDrawerRecord(null); }}
          onDeleted={() => { refresh(); setDrawerRecord(null); }}
        />
      )}
      {deleteTarget && (
        <ConfirmDialog
          title={`Delete ${def.singularName}?`}
          desc={`"${getPrimary(deleteTarget)}" will be permanently deleted. This action cannot be undone.`}
          danger
          busy={deleteBusy}
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      {bulkDeleteOpen && (
        <ConfirmDialog
          title={`Delete ${selectedIds.size} ${selectedIds.size === 1 ? def.singularName : def.pluralName}?`}
          desc="These records will be permanently deleted. This action cannot be undone."
          danger
          busy={bulkDeleteBusy}
          confirmLabel={`Delete ${selectedIds.size}`}
          onConfirm={handleBulkDelete}
          onCancel={() => setBulkDeleteOpen(false)}
        />
      )}
    </>
  );

  // ── Full-page CRM layout (CustomObjectRecordsPage) ──────────────────────────
  if (fullPage) {
    return (
      <>
        <span role="status" aria-live="polite"
          style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>
          {sortAnnounce}
        </span>

        <div className="nx-module-header">
          <div className="nx-module-title-row">
            <div className="nx-module-title-left">
              <h2 className="nx-module-title">{def.pluralName}</h2>
              <span className="nx-module-count">{meta.total.toLocaleString()}</span>
            </div>
            <div className="nx-module-actions">
              {viewSwitcherEl}
              {coExportEl}
              {coImportEl}
              <div style={{ position: 'relative' }} ref={colPickerRef}>
                <IconBtn icon="Columns" title="Choose columns"
                  style={visibleColIds !== null ? { color: 'var(--nx-accent)' } : undefined}
                  onClick={() => setColPickerOpen(o => !o)} />
                {colPickerOpen && colPickerEl}
              </div>
              {coSavedViewsEl}
              {coSaveViewEl}
              <IconBtn icon="RotateCcw" aria-label="Refresh" onClick={refresh} disabled={loading} />
              {canUse && def.status === 'ACTIVE' && (
                <Btn variant="primary" icon="Plus" onClick={() => setCreateOpen(true)}>
                  New {def.singularName}
                </Btn>
              )}
            </div>
          </div>
          <div className="nx-module-tabs-bar">
            <div className="nx-tabs" role="tablist">
              <button type="button" role="tab" aria-selected="true" className="nx-tab is-active">
                <span>All {def.pluralName}</span>
                <span className="nx-tab-count">{meta.total.toLocaleString()}</span>
              </button>
            </div>
          </div>
          <div className="nx-toolbar-filters">
            <SearchField
              value={search}
              onChange={e => handleSearch(e.target.value)}
              onClear={() => handleSearch('')}
              placeholder={`Search ${def.pluralName}…`}
              aria-label={`Search ${def.pluralName}`}
              width={200}
            />
            <div style={{ position: 'relative' }}>
              <button ref={filterBtnRef}
                className={'nx-chip' + (filterCount > 0 ? ' is-active' : '')}
                style={{ cursor: 'pointer' }}
                onClick={() => setFilterOpen(o => !o)}>
                <NexusIcon name="Filter" size={11} />
                Add filter
                <NexusIcon name="ChevronDown" size={10} />
              </button>
              {filterPanelDropdown}
            </div>
            {filterChipsEl}
            {filterCount > 0 && (
              <Btn variant="ghost" size="sm" icon="X" onClick={clearAllFilters}>
                Clear ({filterCount})
              </Btn>
            )}
            {loading && records.length > 0 && (
              <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5,
                fontSize: 12, color: 'var(--nx-text-muted)' }}>
                <NexusIcon name="Loader" size={13}
                  style={{ animation: 'nx-spin 1s linear infinite', flexShrink: 0 }} />
                Loading…
              </span>
            )}
          </div>
        </div>

        {bulkBarEl}

        {def.status === 'ARCHIVED' && (
          <div style={{ background: 'var(--nx-surface-alt)', borderTop: '1px solid var(--nx-border)',
            borderBottom: '1px solid var(--nx-border)', padding: '8px 20px', fontSize: 12,
            color: 'var(--nx-text-muted)', display: 'flex', gap: 8, alignItems: 'center' }}>
            <NexusIcon name="Archive" size={13} />
            This object is archived — records are read-only.
          </div>
        )}

        {viewMode === 'calendar' ? (
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {calendarEl}
          </div>
        ) : viewMode === 'kanban' && hasSelectField ? (
          <div style={{ padding: '16px 20px', overflowX: 'auto' }}>
            {kanbanEl}
          </div>
        ) : viewMode === 'kanban' ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--nx-text-muted)', padding: 48 }}>
            <NexusIcon name="LayoutGrid" size={28} />
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--nx-text)' }}>Kanban unavailable</div>
            <div style={{ fontSize: 13, textAlign: 'center', maxWidth: 280 }}>Add a SELECT field to this object to enable the Kanban board view.</div>
          </div>
        ) : viewMode === 'grid' ? (
          <div style={{ padding: '16px 20px' }}>
            {gridEl}
            <div className="nx-table-foot" style={{ marginTop: 12 }}>
              <span>{records.length} of {meta.total}</span>
              <span className="nx-foot-sep">·</span>
              <span style={{ flex: 1 }} />
              <Btn variant="ghost" icon="ChevronLeft" disabled={!pm.hasPrev || loading} aria-label="Previous page" onClick={() => goPage(page - 1)} />
              <span className="nx-page">Page {pm.safePage} of {pm.pages}</span>
              <Btn variant="ghost" iconRight="ChevronRight" disabled={!pm.hasNext || loading} aria-label="Next page" onClick={() => goPage(page + 1)} />
            </div>
          </div>
        ) : (
          <div className="nx-table-wrap">
            <div className="nx-table-scroll">
              {tableEl}
            </div>
            <div className="nx-table-foot">
              <span>{records.length} of {meta.total}</span>
              <span className="nx-foot-sep">·</span>
              <span>Updated just now</span>
              <span style={{ flex: 1 }} />
              <Btn variant="ghost" icon="ChevronLeft" disabled={!pm.hasPrev || loading}
                aria-label="Previous page" onClick={() => goPage(page - 1)} />
              <span className="nx-page">Page {pm.safePage} of {pm.pages}</span>
              <Btn variant="ghost" iconRight="ChevronRight" disabled={!pm.hasNext || loading}
                aria-label="Next page" onClick={() => goPage(page + 1)} />
            </div>
          </div>
        )}

        {modalsEl}
      </>
    );
  }

  // ── Console tab layout (ObjectDetailPanel > Records tab) ────────────────────
  return (
    <>
      <span role="status" aria-live="polite"
        style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>
        {sortAnnounce}
      </span>
      {def.status === 'ARCHIVED' && (
        <div style={{ background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)',
          borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 12,
          color: 'var(--nx-text-muted)', display: 'flex', gap: 8, alignItems: 'center' }}>
          <NexusIcon name="Archive" size={13} />
          This object is archived — records are read-only.
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        {viewSwitcherEl}
        <SearchField
          value={search}
          onChange={e => handleSearch(e.target.value)}
          onClear={() => handleSearch('')}
          placeholder={`Search ${def.pluralName}…`}
          aria-label={`Search ${def.pluralName}`}
          width={180}
        />
        <div style={{ position: 'relative' }}>
          <Btn ref={filterBtnRef} variant="ghost" icon="Filter" size="sm"
            style={filterCount > 0 ? { color: 'var(--nx-accent)', borderColor: 'var(--nx-accent)' } : {}}
            onClick={() => setFilterOpen(o => !o)}>
            Filter{filterCount > 0 ? ` (${filterCount})` : ''}
          </Btn>
          {filterPanelDropdown}
        </div>
        <span style={{ flex: 1 }} />
        {loading && records.length > 0 ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--nx-text-muted)' }}>
            <NexusIcon name="Loader" size={13} style={{ animation: 'nx-spin 1s linear infinite', flexShrink: 0 }} />
            Loading…
          </span>
        ) : meta.total > 0 ? (
          <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{meta.total} record{meta.total !== 1 ? 's' : ''}</span>
        ) : null}
        {coExportEl}
        {coImportEl}
        <div style={{ position: 'relative' }} ref={colPickerRef}>
          <IconBtn icon="Columns" title="Choose columns"
            style={visibleColIds !== null ? { color: 'var(--nx-accent)' } : undefined}
            onClick={() => setColPickerOpen(o => !o)} />
          {colPickerOpen && colPickerEl}
        </div>
        {coSavedViewsEl}
        {coSaveViewEl}
        <IconBtn icon="RotateCcw" aria-label="Refresh" onClick={refresh} disabled={loading} />
        {canUse && def.status === 'ACTIVE' && (
          <Btn variant="primary" icon="Plus" size="sm" onClick={() => setCreateOpen(true)}>
            New {def.singularName}
          </Btn>
        )}
      </div>

      {bulkBarEl}

      {filterCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {filterChipsEl}
          <Btn variant="ghost" size="sm" icon="X" onClick={clearAllFilters}>Clear all</Btn>
        </div>
      )}

      {viewMode === 'calendar' ? calendarEl
      : viewMode === 'kanban' && hasSelectField ? (
        <div style={{ overflowX: 'auto' }}>{kanbanEl}</div>
      ) : viewMode === 'kanban' ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--nx-text-muted)', padding: 48 }}>
          <NexusIcon name="LayoutGrid" size={28} />
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--nx-text)' }}>Kanban unavailable</div>
          <div style={{ fontSize: 13, textAlign: 'center', maxWidth: 280 }}>Add a SELECT field to this object to enable the Kanban board view.</div>
        </div>
      ) : viewMode === 'grid' ? (
        <>
          {gridEl}
          <div className="nx-table-foot" style={{ marginTop: 12 }}>
            <span>{records.length} of {meta.total}</span>
            <span className="nx-foot-sep">·</span>
            <span style={{ flex: 1 }} />
            <Btn variant="ghost" icon="ChevronLeft" disabled={!pm.hasPrev || loading} aria-label="Previous page" onClick={() => goPage(page - 1)} />
            <span className="nx-page">Page {pm.safePage} of {pm.pages}</span>
            <Btn variant="ghost" iconRight="ChevronRight" disabled={!pm.hasNext || loading} aria-label="Next page" onClick={() => goPage(page + 1)} />
          </div>
        </>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            {tableEl}
          </div>
          <div className="nx-table-foot">
            <span>{records.length} of {meta.total}</span>
            <span className="nx-foot-sep">·</span>
            <span>Updated just now</span>
            <span style={{ flex: 1 }} />
            <Btn variant="ghost" icon="ChevronLeft" disabled={!pm.hasPrev || loading} aria-label="Previous page" onClick={() => goPage(page - 1)} />
            <span className="nx-page">Page {pm.safePage} of {pm.pages}</span>
            <Btn variant="ghost" iconRight="ChevronRight" disabled={!pm.hasNext || loading} aria-label="Next page" onClick={() => goPage(page + 1)} />
          </div>
        </>
      )}

      {modalsEl}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-007: entity search helpers
// ─────────────────────────────────────────────────────────────────────────────

const ENTITY_API_MAP = {
  people:    (wsId, params) => peopleApi.list(wsId, params),
  companies: (wsId, params) => companyApi.list(wsId, params),
  deals:     (wsId, params) => dealApi.list(wsId, params),
  tasks:     (wsId, params) => taskApi.list(wsId, params),
};

function normalizeEntityList(targetType, raw) {
  const rows = Array.isArray(raw) ? raw : (raw?.data ?? raw?.items ?? raw?.people ?? raw?.companies ?? raw?.deals ?? raw?.tasks ?? []);
  return rows.map(r => {
    let displayName = r.name || r.title || r.email || r.id;
    return { id: r.id, displayName };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// CO-007: RelationshipPicker — search + select CRM entities for a relationship
// Props:
//   wsId, targetType, cardinality, value (array of {id, displayName}), onChange(items)
//   readOnly — disables interaction
// ─────────────────────────────────────────────────────────────────────────────

const RelationshipPicker = ({ wsId, targetType, cardinality, value = [], onChange, readOnly = false }) => {
  const cfg = TARGET_TYPE_CONFIG[targetType] || {};
  const multi = isMultiLink(cardinality);

  const [open,    setOpen]    = useState(false);
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [focusIdx, setFocusIdx] = useState(-1);
  const debRef  = useRef(null);
  const inputRef = useRef(null);
  const listRef  = useRef(null);
  const wrapperRef = useRef(null);
  const pickerSeqRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const loadEntities = useCallback(async (q) => {
    const apiCall = ENTITY_API_MAP[targetType];
    if (!apiCall || !wsId) return;
    const seq = ++pickerSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = { pageSize: 20 };
      if (q) params.search = q;
      const raw = await apiCall(wsId, params);
      const items = normalizeEntityList(targetType, raw);
      if (seq !== pickerSeqRef.current) return;
      setResults(items);
      setFocusIdx(-1);
    } catch (err) {
      if (seq !== pickerSeqRef.current) return;
      setError(err?.message || 'Failed to load');
    } finally {
      if (seq === pickerSeqRef.current) setLoading(false);
    }
  }, [wsId, targetType]);

  useEffect(() => {
    if (open) loadEntities('');
  }, [open, loadEntities]);

  const handleQueryChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => loadEntities(q), 300);
  };

  const isSelected = (id) => value.some(v => v.id === id);

  const select = (entity) => {
    if (multi) {
      if (isSelected(entity.id)) {
        onChange(value.filter(v => v.id !== entity.id));
      } else {
        onChange([...value, entity]);
      }
    } else {
      onChange([entity]);
      setOpen(false);
      setQuery('');
    }
  };

  const remove = (id) => onChange(value.filter(v => v.id !== id));

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ') { setOpen(true); e.preventDefault(); }
      return;
    }
    if (e.key === 'Escape') { setOpen(false); setQuery(''); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && focusIdx >= 0 && results[focusIdx]) {
      e.preventDefault();
      select(results[focusIdx]);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }} onKeyDown={handleKeyDown}>
      {/* Selected chips */}
      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
          {value.map(item => (
            <span key={item.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: 'var(--nx-accent-tint-2)', border: '1px solid var(--nx-accent)',
              borderRadius: 20, padding: '2px 8px 2px 6px', fontSize: 12, color: 'var(--nx-accent)',
            }}>
              <NexusIcon name={cfg.icon || 'Link'} size={11} />
              {item.displayName}
              {!readOnly && (
                <button type="button" onClick={() => remove(item.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--nx-accent)', padding: 0, display: 'flex', lineHeight: 1, marginLeft: 2 }}>
                  <NexusIcon name="X" size={10} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Trigger button */}
      {!readOnly && (
        <button
          type="button"
          ref={inputRef}
          onClick={() => setOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, width: '100%',
            padding: '7px 10px', border: '1px solid var(--nx-border)',
            borderRadius: 6, background: 'var(--nx-surface)', cursor: 'pointer',
            fontSize: 13, color: value.length ? 'var(--nx-text)' : 'var(--nx-text-muted)',
            textAlign: 'left',
          }}
        >
          <NexusIcon name={cfg.icon || 'Link'} size={13} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1 }}>
            {value.length === 0
              ? `Link ${cfg.plural || targetType}…`
              : multi ? `Add more ${cfg.plural || targetType}…` : `Change ${cfg.label || targetType}…`}
          </span>
          <NexusIcon name={open ? 'ChevronUp' : 'ChevronDown'} size={12} />
        </button>
      )}

      {/* Dropdown */}
      {open && !readOnly && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          marginTop: 2, background: 'var(--nx-surface)',
          border: '1px solid var(--nx-border)', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', overflow: 'hidden',
        }}>
          <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--nx-border)' }}>
            <input
              autoFocus
              className="nx-input"
              style={{ width: '100%', fontSize: 13 }}
              placeholder={`Search ${cfg.plural || targetType}…`}
              value={query}
              onChange={handleQueryChange}
              onKeyDown={e => {
                if (e.key === 'ArrowDown') { e.preventDefault(); setFocusIdx(0); listRef.current?.focus(); }
                if (e.key === 'Escape') { setOpen(false); setQuery(''); }
              }}
            />
          </div>
          <div ref={listRef} style={{ maxHeight: 220, overflowY: 'auto' }}>
            {loading && (
              <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 13, color: 'var(--nx-text-muted)' }}>
                <NexusIcon name="Loader" size={13}
                  style={{ animation: 'nx-spin 1s linear infinite', flexShrink: 0 }} />
                Searching…
              </div>
            )}
            {error && !loading && (
              <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--nx-danger)',
                display: 'flex', alignItems: 'center', gap: 6 }}>
                <NexusIcon name="AlertCircle" size={13} /> {error}
              </div>
            )}
            {!loading && !error && results.length === 0 && (
              <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--nx-text-muted)',
                textAlign: 'center' }}>
                No {cfg.plural || targetType} found
              </div>
            )}
            {!loading && !error && results.map((entity, i) => {
              const sel = isSelected(entity.id);
              return (
                <button
                  key={entity.id}
                  type="button"
                  onClick={() => select(entity)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '8px 14px', background: i === focusIdx ? 'var(--nx-surface-alt)' : 'transparent',
                    border: 'none', cursor: 'pointer', fontSize: 13, textAlign: 'left',
                    color: sel ? 'var(--nx-accent)' : 'var(--nx-text)',
                  }}
                >
                  {multi && (
                    <NexusIcon name={sel ? 'CheckSquare' : 'Square'} size={13}
                      style={{ color: sel ? 'var(--nx-accent)' : 'var(--nx-text-muted)', flexShrink: 0 }} />
                  )}
                  <NexusIcon name={cfg.icon || 'Link'} size={13}
                    style={{ color: sel ? 'var(--nx-accent)' : 'var(--nx-text-muted)', flexShrink: 0 }} />
                  <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {entity.displayName}
                  </span>
                  {!multi && sel && <NexusIcon name="Check" size={12} style={{ color: 'var(--nx-accent)' }} />}
                </button>
              );
            })}
          </div>
          {multi && value.length > 0 && (
            <div style={{ padding: '6px 10px', borderTop: '1px solid var(--nx-border)',
              display: 'flex', justifyContent: 'flex-end' }}>
              <Btn size="sm" variant="ghost" onClick={() => setOpen(false)}>Done</Btn>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-007: RelationshipModal — create or edit a relationship definition
// Props:
//   mode: 'new' | 'edit'
//   rel: existing relationship (edit mode) | null (new)
//   onSave(dto): called with payload (no API call for wizard-staged mode)
//   onClose()
//   staged: true = caller handles the API call (wizard pre-create mode)
//   wsId, objectId: required when staged=false
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_REL = {
  label:        '',
  reverseLabel: '',
  targetType:   'people',
  cardinality:  'BELONGS_TO',
  required:     false,
  showOnTarget: true,
};

const RelationshipModal = ({ mode = 'new', rel = null, onSave, onClose, staged = false, wsId, objectId, toast }) => {
  const isEdit = mode === 'edit';

  const [form, setForm] = useState(() => {
    if (isEdit && rel) {
      return {
        label:        rel.label        || '',
        reverseLabel: rel.reverseLabel || '',
        targetType:   rel.targetType   || 'people',
        cardinality:  rel.cardinality  || 'BELONGS_TO',
        required:     rel.required     ?? false,
        showOnTarget: rel.showOnTarget ?? true,
      };
    }
    return { ...EMPTY_REL };
  });
  const [errors,  setErrors]  = useState({});
  const [saving,  setSaving]  = useState(false);

  const setF = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const validate = () => {
    const errs = {};
    if (!form.label.trim())        errs.label        = 'Label is required';
    if (!form.reverseLabel.trim()) errs.reverseLabel = 'Reverse label is required';
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const dto = isEdit
      ? { label: form.label.trim(), reverseLabel: form.reverseLabel.trim(), required: form.required, showOnTarget: form.showOnTarget }
      : { label: form.label.trim(), reverseLabel: form.reverseLabel.trim(), targetType: form.targetType, cardinality: form.cardinality, required: form.required, showOnTarget: form.showOnTarget };

    if (staged) {
      onSave(dto);
      return;
    }

    setSaving(true);
    try {
      let result;
      if (isEdit) {
        result = await coApi.updateRelationship(wsId, objectId, rel.id, dto);
      } else {
        result = await coApi.createRelationship(wsId, objectId, dto);
      }
      onSave(result);
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Failed to save relationship';
      if (toast) toast({ tone: 'error', icon: 'AlertCircle', title: 'Save failed', desc: msg });
      else setErrors({ _form: msg });
    } finally {
      setSaving(false);
    }
  };

  const ttCfg = TARGET_TYPE_CONFIG[form.targetType] || {};

  return (
    <Modal
      title={isEdit ? 'Edit Relationship' : 'Add Relationship'}
      icon="Share2"
      onClose={onClose}
      width={500}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" icon="Check" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Relationship'}
          </Btn>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {errors._form && (
          <div style={{ background: 'var(--nx-danger-tint, #FEF2F2)',
            border: '1px solid var(--nx-danger, #DC2626)', borderRadius: 6,
            padding: '8px 12px', fontSize: 13, color: 'var(--nx-danger, #DC2626)',
            display: 'flex', gap: 8, alignItems: 'center' }}>
            <NexusIcon name="AlertCircle" size={14} /> {errors._form}
          </div>
        )}

        {/* Target type — immutable after creation */}
        <div className="nx-form-field">
          <label className="nx-form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Links to
            {isEdit && (
              <span style={{ fontSize: 11, color: 'var(--nx-text-muted)',
                background: 'var(--nx-surface-alt)', borderRadius: 4, padding: '1px 5px' }}>
                immutable
              </span>
            )}
          </label>
          {isEdit ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px',
              background: 'var(--nx-surface-alt)', borderRadius: 6, fontSize: 13,
              color: 'var(--nx-text-muted)', border: '1px solid var(--nx-border)' }}>
              <NexusIcon name={TARGET_TYPE_CONFIG[form.targetType]?.icon || 'Link'} size={14} />
              {TARGET_TYPE_CONFIG[form.targetType]?.plural || form.targetType}
            </div>
          ) : (
            <select className="nx-input" value={form.targetType}
              onChange={e => setF('targetType', e.target.value)}>
              {TARGET_TYPES.map(t => (
                <option key={t} value={t}>
                  {TARGET_TYPE_CONFIG[t]?.plural || t}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Link type — immutable after creation */}
        <div className="nx-form-field">
          <label className="nx-form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Link type
            {isEdit && (
              <span style={{ fontSize: 11, color: 'var(--nx-text-muted)',
                background: 'var(--nx-surface-alt)', borderRadius: 4, padding: '1px 5px' }}>
                immutable
              </span>
            )}
          </label>
          {isEdit ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px',
              background: 'var(--nx-surface-alt)', borderRadius: 6, fontSize: 13,
              color: 'var(--nx-text-muted)', border: '1px solid var(--nx-border)' }}>
              <NexusIcon name={CARDINALITY_CONFIG[form.cardinality]?.icon || 'Link'} size={14} />
              {CARDINALITY_CONFIG[form.cardinality]?.label || form.cardinality}
            </div>
          ) : (
            <select className="nx-input" value={form.cardinality}
              onChange={e => setF('cardinality', e.target.value)}>
              {Object.entries(CARDINALITY_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label} — {v.desc}</option>
              ))}
            </select>
          )}
        </div>

        {/* Label */}
        <div className={`nx-form-field${errors.label ? ' is-error' : ''}`}>
          <label className="nx-form-label">
            Label <span className="nx-form-req">*</span>
          </label>
          <input
            className="nx-input"
            autoFocus={!isEdit}
            value={form.label}
            onChange={e => setF('label', e.target.value)}
            placeholder={`e.g. Primary ${ttCfg.label || 'Contact'}`}
          />
          <span style={{ fontSize: 11, color: 'var(--nx-text-faint)' }}>
            Shown on this object's records (e.g. "Primary Contact")
          </span>
          {errors.label && <span className="nx-form-error">{errors.label}</span>}
        </div>

        {/* Reverse label */}
        <div className={`nx-form-field${errors.reverseLabel ? ' is-error' : ''}`}>
          <label className="nx-form-label">
            Reverse Label <span className="nx-form-req">*</span>
          </label>
          <input
            className="nx-input"
            value={form.reverseLabel}
            onChange={e => setF('reverseLabel', e.target.value)}
            placeholder="e.g. Related Contracts"
          />
          <span style={{ fontSize: 11, color: 'var(--nx-text-faint)' }}>
            Shown on the target side (e.g. "Related Contracts")
          </span>
          {errors.reverseLabel && <span className="nx-form-error">{errors.reverseLabel}</span>}
        </div>

        {/* Toggles — card style */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)',
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
            Options
          </div>
          {[
            { key: 'required',     icon: 'AlertCircle', label: 'Required',
              desc: 'Records must set this relationship before saving' },
            { key: 'showOnTarget', icon: 'Eye',         label: 'Show on target',
              desc: "Display reverse links on the target entity's detail view" },
          ].map(({ key, icon, label, desc }) => (
            <label key={key} style={{
              display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer',
              padding: '10px 12px', borderRadius: 8,
              border: `1.5px solid ${form[key] ? 'var(--nx-accent)' : 'var(--nx-border)'}`,
              background: form[key] ? 'var(--nx-accent-tint-2)' : 'transparent',
              transition: 'border-color 0.1s, background 0.1s',
            }}>
              <div style={{ position: 'relative', width: 16, height: 16, flexShrink: 0, marginTop: 2 }}>
                <input type="checkbox" checked={form[key]}
                  onChange={e => setF(key, e.target.checked)}
                  style={{ opacity: 0, position: 'absolute', inset: 0, cursor: 'pointer', margin: 0, width: '100%', height: '100%' }} />
                <div style={{
                  width: 16, height: 16, borderRadius: 4, pointerEvents: 'none',
                  background: form[key] ? 'var(--nx-accent)' : 'transparent',
                  border: `1.5px solid ${form[key] ? 'var(--nx-accent)' : 'var(--nx-border)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.1s',
                }}>
                  {form[key] && <NexusIcon name="Check" size={10} style={{ color: '#fff' }} />}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600,
                  color: form[key] ? 'var(--nx-accent)' : 'var(--nx-text)' }}>{label}</div>
                <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 1 }}>{desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-007: InlineRelRow — compact row in the wizard relationship list
// ─────────────────────────────────────────────────────────────────────────────

const InlineRelRow = ({ rel, onEdit, onRemove }) => {
  const ttCfg   = TARGET_TYPE_CONFIG[rel.targetType] || {};
  const cardCfg = CARDINALITY_CONFIG[rel.cardinality] || {};
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
      background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 8 }}>
      <NexusIcon name={cardCfg.icon || 'Link'} size={14}
        style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>{rel.label}</div>
        <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'flex', gap: 6, alignItems: 'center', marginTop: 1 }}>
          <NexusIcon name={ttCfg.icon || 'Link'} size={10} />
          {ttCfg.plural || rel.targetType}
          <span>·</span>
          {cardCfg.label || rel.cardinality}
          {rel.reverseLabel && <><span>·</span><span style={{ fontStyle: 'italic' }}>{rel.reverseLabel}</span></>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 2 }}>
        {onEdit && <IconBtn icon="Edit2" title="Edit relationship" onClick={onEdit} />}
        {onRemove && <IconBtn icon="Trash2" title="Remove relationship" onClick={onRemove}
          style={{ color: 'var(--nx-text-muted)' }} />}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-007: RelationshipsTab — manage relationship definitions on a saved object
// ─────────────────────────────────────────────────────────────────────────────

const RelationshipsTab = ({ def, wsId, canManage, toast }) => {
  const [rels,    setRels]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [adding,  setAdding]  = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const res = await coApi.listRelationships(wsId, def.id);
      setRels(res?.relationships ?? []);
    } catch (err) {
      setLoadErr(err?.body?.message || err?.message || 'Failed to load relationships');
    } finally {
      setLoading(false);
    }
  }, [wsId, def.id]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = (created) => {
    setRels(prev => [...prev, created]);
    setAdding(false);
    toast({ tone: 'success', icon: 'Check', title: `Relationship "${created.label}" added` });
  };

  const handleEdit = (updated) => {
    setRels(prev => prev.map(r => r.id === updated.id ? updated : r));
    setEditing(null);
    toast({ tone: 'success', icon: 'Check', title: `Relationship "${updated.label}" updated` });
  };

  const handleDelete = async (rel) => {
    try {
      await coApi.deleteRelationship(wsId, def.id, rel.id);
      setRels(prev => prev.filter(r => r.id !== rel.id));
      setConfirm(null);
      toast({ tone: 'success', icon: 'Trash2', title: `Relationship "${rel.label}" deleted` });
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Delete failed';
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Delete failed', desc: msg });
      setConfirm(null);
    }
  };

  const activeRels   = rels.filter(r => r.status === 'ACTIVE' || !r.status);
  const archivedRels = rels.filter(r => r.status === 'ARCHIVED');

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
            {loading ? 'Loading…' : `${activeRels.length} relationship${activeRels.length !== 1 ? 's' : ''}`}
            {archivedRels.length > 0 && `, ${archivedRels.length} archived`}
          </div>
          {canManage && (
            <Btn variant="ghost" icon="Plus" onClick={() => setAdding(true)}>
              Add Relationship
            </Btn>
          )}
        </div>

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <NexusIcon name="Loader" size={20}
              style={{ animation: 'nx-spin 1s linear infinite', color: 'var(--nx-text-muted)' }} />
          </div>
        )}

        {loadErr && !loading && (
          <div style={{ color: 'var(--nx-danger)', fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
            <NexusIcon name="AlertCircle" size={14} /> {loadErr}
            <Btn size="sm" variant="ghost" icon="RotateCcw" onClick={load}>Retry</Btn>
          </div>
        )}

        {!loading && !loadErr && activeRels.length === 0 && (
          <EmptyState
            icon="Share2"
            title="No relationships yet"
            desc="Link this object's records to People, Companies, Deals, or Tasks."
            action={canManage
              ? <Btn variant="primary" icon="Plus" onClick={() => setAdding(true)}>Add Relationship</Btn>
              : null}
          />
        )}

        {!loading && !loadErr && activeRels.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1,
            border: '1px solid var(--nx-border)', borderRadius: 10, overflow: 'hidden' }}>
            {activeRels.map((rel, i) => {
              const ttCfg   = TARGET_TYPE_CONFIG[rel.targetType] || {};
              const cardCfg = CARDINALITY_CONFIG[rel.cardinality] || {};
              return (
                <div key={rel.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
                  background: 'var(--nx-surface)',
                  borderBottom: i < activeRels.length - 1 ? '1px solid var(--nx-border)' : 'none',
                }}>
                  <NexusIcon name={cardCfg.icon || 'Link'} size={15}
                    style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>
                        {rel.label}
                      </span>
                      {rel.required && <Pill tone="gray">Required</Pill>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2,
                      display: 'flex', gap: 6, alignItems: 'center' }}>
                      <NexusIcon name={ttCfg.icon || 'Link'} size={10} />
                      {ttCfg.plural || rel.targetType}
                      <span>·</span>
                      {cardCfg.label || rel.cardinality}
                      {rel.reverseLabel && (
                        <><span>·</span>
                          <span style={{ fontStyle: 'italic' }}>↩ {rel.reverseLabel}</span>
                        </>
                      )}
                    </div>
                  </div>
                  {canManage && (
                    <div style={{ display: 'flex', gap: 2 }}>
                      <IconBtn icon="Edit2" title="Edit relationship"
                        onClick={() => setEditing(rel)} />
                      <IconBtn icon="Trash2" title="Delete relationship"
                        style={{ color: 'var(--nx-danger)' }}
                        onClick={() => setConfirm(rel)} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {archivedRels.length > 0 && (
          <details>
            <summary style={{ fontSize: 12, color: 'var(--nx-text-muted)', cursor: 'pointer',
              userSelect: 'none', padding: '4px 0' }}>
              {archivedRels.length} archived relationship{archivedRels.length !== 1 ? 's' : ''}
            </summary>
            <div style={{ marginTop: 8, opacity: 0.6 }}>
              {archivedRels.map(rel => (
                <InlineRelRow key={rel.id} rel={rel} />
              ))}
            </div>
          </details>
        )}
      </div>

      {adding && (
        <RelationshipModal
          mode="new"
          staged={false}
          wsId={wsId}
          objectId={def.id}
          onSave={handleAdd}
          onClose={() => setAdding(false)}
          toast={toast}
        />
      )}
      {editing && (
        <RelationshipModal
          mode="edit"
          rel={editing}
          staged={false}
          wsId={wsId}
          objectId={def.id}
          onSave={handleEdit}
          onClose={() => setEditing(null)}
          toast={toast}
        />
      )}
      {confirm && (
        <ConfirmDialog
          title={`Delete relationship "${confirm.label}"?`}
          desc="All existing links created from this relationship will be permanently removed. This cannot be undone."
          danger
          confirmLabel="Delete Relationship"
          onConfirm={() => handleDelete(confirm)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-008: RelatedRecordsSection — full related-record experience in RecordDetailDrawer
// Evolved from CO-007 RelatedSection. All relationship groups shown (including empty).
// Props: wsId, objectId, recordId, canManage (default false)
// ─────────────────────────────────────────────────────────────────────────────

const RelatedRecordsSection = ({ wsId, objectId, recordId, canManage = false }) => {
  const toast = useToast();
  const [groups,       setGroups]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [expanded,     setExpanded]     = useState({});   // relId → true
  const [addingTo,     setAddingTo]     = useState(null); // relId being added to
  const [addBusy,      setAddBusy]      = useState(false);
  const [removingLink, setRemovingLink] = useState(null); // { relId, linkId, displayName, targetType }
  const [removeBusy,   setRemoveBusy]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await coApi.getRecordLinks(wsId, objectId, recordId);
      setGroups(groupLinksByRelDefinition(res?.relationships));
    } catch (err) {
      setError(err?.body?.message || err?.message || 'Failed to load related records');
    } finally {
      setLoading(false);
    }
  }, [wsId, objectId, recordId]);

  useEffect(() => { load(); }, [load]);

  const handleNavigate = useCallback((targetType, entityId) => {
    window.dispatchEvent(new CustomEvent('nx-open-crm-record', { detail: { type: targetType, id: entityId } }));
  }, []);

  const handleAdd = useCallback(async (relId, item) => {
    if (!item) { setAddingTo(null); return; }
    setAddBusy(true);
    try {
      await coApi.createLink(wsId, objectId, recordId, relId, { targetEntityId: item.id });
      await load();
      setAddingTo(null);
      toast({ tone: 'success', icon: 'Link', title: 'Link added' });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to add link',
        desc: err?.body?.message || err?.message });
    } finally {
      setAddBusy(false);
    }
  }, [wsId, objectId, recordId, load, toast]);

  const handleRemoveConfirm = useCallback(async () => {
    if (!removingLink) return;
    setRemoveBusy(true);
    try {
      await coApi.deleteLink(wsId, objectId, recordId, removingLink.relId, removingLink.linkId);
      await load();
      setRemovingLink(null);
      toast({ tone: 'success', icon: 'Unlink', title: 'Link removed' });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to remove link',
        desc: err?.body?.message || err?.message });
    } finally {
      setRemoveBusy(false);
    }
  }, [wsId, objectId, recordId, removingLink, load, toast]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0',
        fontSize: 13, color: 'var(--nx-text-muted)' }}>
        <NexusIcon name="Loader" size={13}
          style={{ animation: 'nx-spin 1s linear infinite', flexShrink: 0 }} />
        Loading related records…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '10px 0' }}>
        <div style={{ fontSize: 12, color: 'var(--nx-danger)', display: 'flex', gap: 6,
          alignItems: 'center', marginBottom: 6 }}>
          <NexusIcon name="AlertCircle" size={12} /> {error}
        </div>
        <Btn size="sm" variant="ghost" icon="RotateCcw" onClick={load}>Retry</Btn>
      </div>
    );
  }

  if (groups.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {groups.map(group => {
        const ttCfg      = TARGET_TYPE_CONFIG[group.targetType] || {};
        const relId      = group.relationshipId;
        const links      = group.links || [];
        const count      = links.length;
        const isExp      = !!expanded[relId];
        const shown      = isExp ? links : links.slice(0, RELATED_RECORDS_DISPLAY_LIMIT);
        const hidden     = count - RELATED_RECORDS_DISPLAY_LIMIT;
        const isAdding   = addingTo === relId;
        const isBelongsTo = group.cardinality === 'BELONGS_TO';
        const canAdd     = canManage && (!isBelongsTo || count === 0);

        const panelTitle = (
          <>
            <NexusIcon name={ttCfg.icon || 'Link'} size={11} style={{ marginRight: 3, opacity: 0.7 }} />
            {formatRelGroupHeader(group)}
            {count > 0 && (
              <span style={{ background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)',
                borderRadius: 10, fontSize: 10, padding: '0 5px', fontWeight: 700, marginLeft: 5 }}>
                {count}
              </span>
            )}
          </>
        );
        const linkBtn = canAdd && !isAdding
          ? (
            <button type="button" onClick={() => setAddingTo(relId)}
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                padding: '2px 0', fontSize: 11, color: 'var(--nx-accent)',
                display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
              <NexusIcon name="Plus" size={11} /> Link {ttCfg.label || group.targetType}
            </button>
          )
          : null;
        return (
          <CoDrawerPanel key={relId} title={panelTitle} actions={linkBtn}>

            {/* Inline entity picker */}
            {isAdding && (
              <div style={{ marginBottom: 8 }}>
                <RelationshipPicker
                  wsId={wsId}
                  targetType={group.targetType}
                  cardinality="BELONGS_TO"
                  value={[]}
                  onChange={(items) => { if (items.length > 0) handleAdd(relId, items[0]); }}
                  readOnly={addBusy}
                />
                <button type="button" onClick={() => setAddingTo(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 4, padding: 0 }}>
                  Cancel
                </button>
              </div>
            )}

            {/* Per-group empty state */}
            {count === 0 && !isAdding && (
              <div style={{ fontSize: 12, color: 'var(--nx-text-faint)', fontStyle: 'italic',
                padding: '4px 0' }}>
                No linked {ttCfg.plural || ttCfg.label || group.targetType}
                {canManage && (
                  <button type="button" onClick={() => setAddingTo(relId)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 12, color: 'var(--nx-accent)', marginLeft: 6, padding: 0,
                      textDecoration: 'underline' }}>
                    Link one
                  </button>
                )}
              </div>
            )}

            {/* Link rows */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {shown.map((link, idx) => {
                const unavail = isEntityUnavailable(link);
                const name    = link.entity ? getEntityDisplayName(link.entity) : link.targetEntityId;
                return (
                  <div key={link.id} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 0',
                    borderBottom: idx < shown.length - 1 ? '1px solid var(--nx-divider)' : 'none',
                    fontSize: 13, opacity: unavail ? 0.5 : 1,
                  }}>
                    <NexusIcon name={unavail ? 'AlertCircle' : (ttCfg.icon || 'Link')} size={12}
                      style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
                    <span style={{ color: 'var(--nx-text)', flex: 1, minWidth: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {unavail
                        ? <span style={{ color: 'var(--nx-text-faint)' }}>[Unavailable]</span>
                        : name}
                    </span>
                    {!unavail && (
                      <button type="button"
                        aria-label={`Open ${name} in ${ttCfg.label || group.targetType}`}
                        title={`Open ${name}`}
                        onClick={() => handleNavigate(group.targetType, link.targetEntityId)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer',
                          padding: 2, color: 'var(--nx-text-muted)',
                          display: 'flex', alignItems: 'center', borderRadius: 3, flexShrink: 0 }}>
                        <NexusIcon name="ExternalLink" size={11} />
                      </button>
                    )}
                    {canManage && (
                      <button type="button"
                        aria-label={`Remove link to ${name}`}
                        title="Remove link"
                        onClick={() => setRemovingLink({
                          relId, linkId: link.id, displayName: name, targetType: group.targetType,
                        })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer',
                          padding: 2, color: 'var(--nx-text-faint)',
                          display: 'flex', alignItems: 'center', borderRadius: 3, flexShrink: 0 }}>
                        <NexusIcon name="X" size={11} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Show more / less toggle */}
            {count > RELATED_RECORDS_DISPLAY_LIMIT && (
              <button type="button"
                onClick={() => setExpanded(ex => ({ ...ex, [relId]: !isExp }))}
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 12, color: 'var(--nx-accent)', padding: '4px 0',
                  display: 'block', marginTop: 6,
                  borderTop: '1px solid var(--nx-divider)', width: '100%', textAlign: 'left' }}>
                {isExp ? 'Show less' : `Show ${hidden} more`}
              </button>
            )}
          </CoDrawerPanel>
        );
      })}

      {/* Remove link confirm dialog */}
      {removingLink && (
        <ConfirmDialog
          title="Remove link"
          desc={getRemoveLinkConfirmMsg(removingLink.displayName, removingLink.targetType)}
          danger
          busy={removeBusy}
          confirmLabel="Remove"
          onConfirm={handleRemoveConfirm}
          onCancel={() => setRemovingLink(null)}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-007: RelationshipPickerSection — inline pickers for create/edit record modals
// Manages link state for a set of relationship definitions.
// Props:
//   wsId, relationships (array of rel defs), value (map relId→[{id,displayName}]),
//   onChange(relId, items), readOnly
// ─────────────────────────────────────────────────────────────────────────────

const RelationshipPickerSection = ({ wsId, relationships, value, onChange, readOnly = false, errors = {} }) => {
  if (!relationships || relationships.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: 4,
        borderBottom: '1px solid var(--nx-border)' }}>
        Relationships
      </div>
      {relationships.map(rel => (
        <div key={rel.id} className="nx-form-field">
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600,
            color: 'var(--nx-text-muted)', marginBottom: 6,
            textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {rel.label}
            {rel.required && <span style={{ color: 'var(--nx-danger)', marginLeft: 3 }}>*</span>}
          </label>
          <RelationshipPicker
            wsId={wsId}
            targetType={rel.targetType}
            cardinality={rel.cardinality}
            value={value[rel.id] || []}
            onChange={items => onChange(rel.id, items)}
            readOnly={readOnly}
          />
          {errors[rel.id] ? (
            <span className="nx-form-error">{errors[rel.id]}</span>
          ) : (
            <span style={{ fontSize: 11, color: 'var(--nx-text-faint)', marginTop: 3, display: 'block' }}>
              {CARDINALITY_CONFIG[rel.cardinality]?.label || rel.cardinality}
              {' · '}
              {TARGET_TYPE_CONFIG[rel.targetType]?.plural || rel.targetType}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ObjectDetailPanel — right panel tabs
// ─────────────────────────────────────────────────────────────────────────────

const DETAIL_TABS = ['Overview', 'Records', 'Fields', 'Relationships', 'Settings'];

const ObjectDetailPanel = ({ def, wsId, canManage, canUse, onDefUpdate, onDefDelete, toast }) => {
  const [tab, setTab] = useState('Overview');
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [pendingTab, setPendingTab]       = useState(null);
  const [settingsKey, setSettingsKey]     = useState(0);

  // Reset to Overview when selected object changes; clear guard state
  useEffect(() => { setTab('Overview'); setSettingsDirty(false); setPendingTab(null); }, [def?.id]);

  const handleTabClick = (t) => {
    if (tab === 'Settings' && settingsDirty && t !== 'Settings') {
      setPendingTab(t);
      return;
    }
    setTab(t);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Detail header */}
      <div style={{ padding: '14px 20px 0', borderBottom: '1px solid var(--nx-border)',
        background: 'var(--nx-surface)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 14 }}>
          <div style={{ width: 42, height: 42, borderRadius: 10,
            background: 'var(--nx-accent-tint-2)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <NexusIcon name={def.icon || 'Database'} size={20}
              style={{ color: 'var(--nx-accent)' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--nx-text)', margin: 0,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: '0 1 auto' }}
                title={def.singularName}>
                {def.singularName}
              </h2>
              <StatusPill status={def.status} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 3,
              display: 'flex', gap: 6, alignItems: 'center' }}>
              <code style={{ fontSize: 11, color: 'var(--nx-text-faint)' }}>{def.apiName}</code>
              <span style={{ opacity: 0.4 }}>·</span>
              {(() => {
                const n = (def.fields || []).filter(f => f.status === 'ACTIVE').length;
                return <span>{n} active {n !== 1 ? 'fields' : 'field'}</span>;
              })()}
            </div>
          </div>
        </div>

        {/* Tab strip */}
        <div style={{ display: 'flex', gap: 0, marginLeft: -20, marginRight: -20, paddingLeft: 20 }}>
          {DETAIL_TABS.map(t => (
            <button key={t} type="button" onClick={() => handleTabClick(t)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '8px 12px', fontSize: 13, fontWeight: tab === t ? 600 : 400,
                color: tab === t ? 'var(--nx-accent)' : 'var(--nx-text-muted)',
                borderBottom: `2px solid ${tab === t ? 'var(--nx-accent)' : 'transparent'}`,
                marginBottom: -1, transition: 'color 0.12s, border-color 0.12s',
                whiteSpace: 'nowrap',
              }}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content — Records fills the space; all others have padding */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column',
        padding: tab === 'Records' ? 0 : 24 }}>
        {tab === 'Records' && def.status === 'DRAFT' && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ textAlign: 'center', maxWidth: 340 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--nx-surface-alt)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <NexusIcon name="Lock" size={20} style={{ color: 'var(--nx-text-muted)' }} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nx-text)', marginBottom: 6 }}>
                Not published yet
              </div>
              <div style={{ fontSize: 13, color: 'var(--nx-text-muted)', lineHeight: 1.5, marginBottom: 16 }}>
                This object is in Draft. Publish it to start creating records.
              </div>
              {canManage && (
                <Btn variant="primary" icon="Zap" onClick={() => setTab('Settings')}>
                  Publish in Settings
                </Btn>
              )}
            </div>
          </div>
        )}
        {tab === 'Records' && def.status !== 'DRAFT' && (
          <div style={{ flex: 1, padding: '20px 24px' }}>
            <RecordsTab
              def={def}
              wsId={wsId}
              canManage={canManage}
              canUse={canUse}
              toast={toast}
              onGoToSettings={() => setTab('Settings')}
            />
          </div>
        )}
        {tab === 'Overview'  && <OverviewTab def={def} />}
        {tab === 'Fields'    && (
          <FieldsTab
            def={def}
            wsId={wsId}
            canManage={canManage}
            onDefUpdate={onDefUpdate}
            toast={toast}
          />
        )}
        {tab === 'Relationships' && (
          <RelationshipsTab
            def={def}
            wsId={wsId}
            canManage={canManage}
            toast={toast}
          />
        )}
        {tab === 'Settings'  && (
          <SettingsTab
            key={settingsKey}
            def={def}
            wsId={wsId}
            canManage={canManage}
            onDefUpdate={onDefUpdate}
            onDefDelete={onDefDelete}
            onDirtyChange={setSettingsDirty}
            toast={toast}
          />
        )}
      </div>

      {pendingTab && (
        <Modal
          title="Unsaved changes"
          onClose={() => setPendingTab(null)}
          width={420}
          footer={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={() => setPendingTab(null)}>Stay</Btn>
              <Btn variant="danger" onClick={() => {
                setSettingsKey(k => k + 1);
                setSettingsDirty(false);
                setTab(pendingTab);
                setPendingTab(null);
              }}>Discard changes</Btn>
            </div>
          }
        >
          <p style={{ margin: 0, fontSize: 14, color: 'var(--nx-text-muted)', lineHeight: 1.5 }}>
            You have unsaved changes. Are you sure you want to leave without saving?
          </p>
        </Modal>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ObjectRow — table row for the directory (CO-06R)
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Icon color map — maps CO_ICONS and common icon names to category-specific
// background/foreground pairs. Drives the ObjectRow colored icon containers.
// Colors are semantic (people=purple, finance=green, docs=sky, etc.) and do not
// conflict with the accent brand color used for primary actions.
// ─────────────────────────────────────────────────────────────────────────────
const ICON_COLOR_MAP = {
  // People / social
  User:          { bg: '#EDE9FE', c: '#7C3AED' },
  Users:         { bg: '#EDE9FE', c: '#7C3AED' },
  UsersRound:    { bg: '#EDE9FE', c: '#7C3AED' },
  // Buildings / organisations
  Building:      { bg: '#DBEAFE', c: '#2563EB' },
  Building2:     { bg: '#DBEAFE', c: '#2563EB' },
  Briefcase:     { bg: '#EDE9FE', c: '#6D28D9' },
  // Finance
  DollarSign:    { bg: '#D1FAE5', c: '#059669' },
  CreditCard:    { bg: '#EDE9FE', c: '#6D28D9' },
  Wallet:        { bg: '#D1FAE5', c: '#059669' },
  // Documents
  FileText:      { bg: '#E0F2FE', c: '#0284C7' },
  File:          { bg: '#E0F2FE', c: '#0284C7' },
  BookOpen:      { bg: '#E0F2FE', c: '#0284C7' },
  StickyNote:    { bg: '#F3E8FF', c: '#9333EA' },
  ClipboardList: { bg: '#FEE2E2', c: '#DC2626' },
  // Calendar / time
  Calendar:      { bg: '#FEF3C7', c: '#D97706' },
  Clock:         { bg: '#FEF3C7', c: '#D97706' },
  // Analytics / charts
  BarChart:      { bg: '#DBEAFE', c: '#1D4ED8' },
  BarChart3:     { bg: '#DBEAFE', c: '#1D4ED8' },
  Activity:      { bg: '#FEE2E2', c: '#DC2626' },
  TrendingUp:    { bg: '#D1FAE5', c: '#059669' },
  PieChart:      { bg: '#DBEAFE', c: '#2563EB' },
  // Awards / recognition
  Award:         { bg: '#FEF3C7', c: '#D97706' },
  Star:          { bg: '#FEF9C3', c: '#CA8A04' },
  // Location
  MapPin:        { bg: '#ECFDF5', c: '#059669' },
  Globe:         { bg: '#D1FAE5', c: '#059669' },
  // Commerce / inventory
  Package:       { bg: '#FEF3C7', c: '#D97706' },
  Tag:           { bg: '#F3E8FF', c: '#9333EA' },
  Box:           { bg: '#F3F4F6', c: '#6B7280' },
  // Security
  Shield:        { bg: '#DBEAFE', c: '#1D4ED8' },
  Key:           { bg: '#FEF9C3', c: '#CA8A04' },
  Lock:          { bg: '#FEF3C7', c: '#D97706' },
  // Tech / layers
  Layers:        { bg: '#E0F2FE', c: '#0EA5E9' },
  Zap:           { bg: '#FEF9C3', c: '#EAB308' },
  // Health
  HeartPulse:    { bg: '#FEE2E2', c: '#DC2626' },
  Target:        { bg: '#FEE2E2', c: '#DC2626' },
  // Database / default
  Database:      { bg: '#EDF4FD', c: '#125AB0' },
};

const _ICON_FALLBACK_PALETTE = [
  { bg: '#EDF4FD', c: '#125AB0' },
  { bg: '#EDE9FE', c: '#7C3AED' },
  { bg: '#D1FAE5', c: '#059669' },
  { bg: '#FEF3C7', c: '#D97706' },
  { bg: '#E0F2FE', c: '#0284C7' },
  { bg: '#FEE2E2', c: '#DC2626' },
];
const getIconStyle = (name) => {
  if (ICON_COLOR_MAP[name]) return ICON_COLOR_MAP[name];
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + (name || '').charCodeAt(i)) >>> 0;
  return _ICON_FALLBACK_PALETTE[h % _ICON_FALLBACK_PALETTE.length];
};

function getPaginationPages(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, 2, current - 1, current, current + 1, total - 1, total].filter(p => p >= 1 && p <= total));
  const sorted = [...set].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
    result.push(sorted[i]);
  }
  return result;
}

const ObjectRow = ({ def, isSelected, canManage, onSelect, onArchive, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const activeFields = (def.fields || []).filter(f => f.status === 'ACTIVE').length;
  const iconStyle = getIconStyle(def.icon || 'Database');

  return (
    <tr
      className={`nx-tr nx-co-list-item${isSelected ? ' is-checked' : ''}`}
      tabIndex={0}
      onClick={() => onSelect(def)}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onSelect(def))}
      style={{ cursor: 'pointer' }}
    >
      {/* Name — paddingLeft overrides the CSS 12px to align with icon */}
      <td style={{ paddingLeft: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
            background: iconStyle.bg,
            border: `1px solid ${iconStyle.c}33`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: iconStyle.c,
          }}>
            <NexusIcon name={def.icon || 'Database'} size={17} strokeWidth={2}
              style={{ color: iconStyle.c, stroke: iconStyle.c, display: 'block' }} />
          </div>
          <span style={{
            flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: 'var(--nx-text)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }} title={def.singularName}>
            {def.singularName}
          </span>
        </div>
      </td>
      {/* Description — text directly in td; nx-table CSS handles overflow + ellipsis */}
      <td title={def.description || undefined}
        style={{ color: 'var(--nx-text-muted)', fontSize: 13 }}>
        {def.description}
      </td>
      {/* Status */}
      <td><StatusPill status={def.status} /></td>
      {/* Fields */}
      <td style={{ textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 13 }}>
        {activeFields}
      </td>
      {/* Modified */}
      <td style={{ color: 'var(--nx-text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}
          title={def.updatedAt ? new Date(def.updatedAt).toISOString() : undefined}>
        {fmtAbsTime(def.updatedAt)}
      </td>
      {/* Actions */}
      <td className="nx-td-end" style={menuOpen ? { zIndex: 10 } : undefined} onClick={e => e.stopPropagation()}>
        {canManage && (
          <div className="nx-row-actions">
            <Menu
              trigger={
                <IconBtn icon="MoreHorizontalFilled" title="More options"
                  style={{ color: menuOpen ? 'var(--nx-text)' : 'var(--nx-text-muted)' }}
                  onClick={e => { e.stopPropagation(); setMenuOpen(o => !o); }} />
              }
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
            >
              <MenuItem icon="Settings" onClick={() => { onSelect(def); setMenuOpen(false); }}>
                Configure
              </MenuItem>
              {def.status === 'ACTIVE' && (
                <MenuItem icon="Archive" onClick={() => { onArchive(def); setMenuOpen(false); }}>
                  Archive
                </MenuItem>
              )}
              <MenuDivider />
              <MenuItem icon="Trash2" danger onClick={() => { onDelete(def); setMenuOpen(false); }}>
                Delete
              </MenuItem>
            </Menu>
          </div>
        )}
      </td>
    </tr>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ObjectCard — left sidebar list item
// ─────────────────────────────────────────────────────────────────────────────

const ObjectCard = ({ def, isSelected, onSelect, canManage, onArchive, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const activeFields = (def.fields || []).filter(f => f.status === 'ACTIVE').length;
  const showActions = hovered || menuOpen || isSelected;

  return (
    <div
      role="button"
      tabIndex={0}
      className="nx-co-list-item"
      onClick={() => onSelect(def)}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onSelect(def))}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px',
        paddingRight: canManage ? 38 : 10,
        borderRadius: 8, cursor: 'pointer',
        background: isSelected ? 'var(--nx-accent-tint-2)' : hovered ? 'var(--nx-surface-alt)' : 'transparent',
        border: `1px solid ${isSelected ? 'var(--nx-accent)' : 'transparent'}`,
        transition: 'background 0.1s, border-color 0.1s',
      }}
    >
      <div style={{ width: 30, height: 30, borderRadius: 7,
        background: isSelected ? 'var(--nx-accent-tint)' : 'var(--nx-surface-alt)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <NexusIcon name={def.icon || 'Database'} size={15}
          style={{ color: isSelected ? 'var(--nx-accent)' : 'var(--nx-text-muted)' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div title={def.singularName} style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {def.singularName}
        </div>
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', marginTop: 3, flexWrap: 'nowrap', overflow: 'hidden' }}>
          <code title={def.apiName} style={{ fontSize: 10, color: 'var(--nx-text-faint)', minWidth: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 1 }}>{def.apiName}</code>
          <StatusPill status={def.status} />
          <span style={{ opacity: 0.3, flexShrink: 0, fontSize: 10 }}>·</span>
          <span style={{ whiteSpace: 'nowrap', flexShrink: 0, fontSize: 10, color: 'var(--nx-text-faint)' }}>
            {activeFields}{activeFields !== 1 ? ' fields' : ' field'}
          </span>
        </div>
      </div>
      {canManage && (
        <div
          style={{
            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
            opacity: showActions ? 1 : 0, transition: 'opacity 0.12s',
            pointerEvents: showActions ? 'auto' : 'none',
          }}
          onClick={e => e.stopPropagation()}
        >
          <Menu
            trigger={
              <IconBtn
                icon="MoreHorizontal"
                title="More options"
                onClick={e => { e.stopPropagation(); setMenuOpen(o => !o); }}
              />
            }
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
          >
            <MenuItem icon="Settings" onClick={() => { onSelect(def); setMenuOpen(false); }}>
              Configure
            </MenuItem>
            {def.status === 'ACTIVE' && (
              <MenuItem icon="Archive" onClick={() => { onArchive(def); setMenuOpen(false); }}>
                Archive
              </MenuItem>
            )}
            <MenuDivider />
            <MenuItem icon="Trash2" danger onClick={() => { onDelete(def); setMenuOpen(false); }}>
              Delete
            </MenuItem>
          </Menu>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CustomObjectsManagerView — main exported component
// ─────────────────────────────────────────────────────────────────────────────

const CustomObjectsManagerView = () => {
  const workspace = useWorkspace();
  const wsId      = workspace?.id;
  const { can }   = useRbac();
  const canManage = can('crm.manage');
  const canUse    = can('crm.use');
  const toast     = useToast();

  const [defs, setDefs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch]   = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showCreate, setShowCreate] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await coApi.listDefs(wsId, { includeArchived: 'true' });
      const list = Array.isArray(result) ? result : [];
      setDefs(list);
      // Keep selected in sync; do NOT auto-select on initial load (manager landing page must show the object list, not jump into a record tab)
      setSelected(prev => prev ? (list.find(d => d.id === prev.id) || null) : null);
    } catch (err) {
      setError(err?.message || 'Failed to load custom objects');
    } finally {
      setLoading(false);
    }
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  // Step 1: filter by search query across all user-facing name fields
  const searchQ = search.trim().toLowerCase();
  const searchFilteredDefs = !searchQ ? defs : defs.filter(d =>
    d.singularName.toLowerCase().includes(searchQ) ||
    (d.pluralName || '').toLowerCase().includes(searchQ) ||
    d.apiName.toLowerCase().includes(searchQ) ||
    (d.description || '').toLowerCase().includes(searchQ)
  );

  // Step 2: apply status filter on top of search-filtered set
  const filteredDefs = statusFilter === 'ALL'
    ? searchFilteredDefs
    : searchFilteredDefs.filter(d => d.status === statusFilter);

  // Tab counts reflect the search-filtered universe so badge and result are consistent
  const statusTabCounts = {
    ALL:      searchFilteredDefs.length,
    ACTIVE:   searchFilteredDefs.filter(d => d.status === 'ACTIVE').length,
    DRAFT:    searchFilteredDefs.filter(d => d.status === 'DRAFT').length,
    ARCHIVED: searchFilteredDefs.filter(d => d.status === 'ARCHIVED').length,
  };

  // Reset to page 1 whenever the filter or search changes
  useEffect(() => { setCurrentPage(1); }, [search, statusFilter]);

  const ITEMS_PER_PAGE = 10;
  const totalFiltered  = filteredDefs.length;
  const totalPages     = Math.max(1, Math.ceil(totalFiltered / ITEMS_PER_PAGE));
  const safePage       = Math.min(currentPage, totalPages);
  const pageStart      = (safePage - 1) * ITEMS_PER_PAGE;
  const pagedDefs      = filteredDefs.slice(pageStart, pageStart + ITEMS_PER_PAGE);

  const handleCreated = (created) => {
    setDefs(prev => [created, ...prev]);
    setSelected(created);
    setShowCreate(false);
    if (created.status === 'ACTIVE') {
      window.dispatchEvent(new CustomEvent('nx-custom-objects-changed'));
    }
  };

  const handleDefUpdate = (updated) => {
    setDefs(prev => {
      const next = prev.map(d => d.id === updated.id ? updated : d);
      return next.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    });
    setSelected(prev => prev?.id === updated.id ? updated : prev);
  };

  const handleDefDelete = (defId) => {
    setDefs(prev => prev.filter(d => d.id !== defId));
    setSelected(prev => prev?.id === defId ? null : prev);
  };

  const handleQuickArchive = async (def) => {
    try {
      const updated = await coApi.updateDef(wsId, def.id, { status: 'ARCHIVED' });
      handleDefUpdate(updated);
      toast({ tone: 'success', icon: 'Archive', title: `"${def.singularName}" archived` });
      window.dispatchEvent(new CustomEvent('nx-custom-objects-changed'));
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Archive failed',
        desc: err?.body?.message || err?.message });
    }
    setConfirm(null);
  };

  const handleQuickDelete = async (def) => {
    try {
      const result = await coApi.deleteDef(wsId, def.id);
      if (result?.status === 'ARCHIVED') {
        toast({ tone: 'warning', icon: 'AlertCircle',
          title: 'Object archived (has records)',
          desc: 'Objects with records are archived instead of deleted.' });
        handleDefUpdate(result);
      } else {
        toast({ tone: 'success', icon: 'Trash2', title: `"${def.singularName}" deleted` });
        handleDefDelete(def.id);
      }
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Delete failed',
        desc: err?.body?.message || err?.message });
    }
    setConfirm(null);
  };

  const existingApiNames = defs.map(d => d.apiName);

  /* CO-07R: status label map for empty-state message */
  const STATUS_LABELS = { ACTIVE: 'Active', DRAFT: 'Draft', ARCHIVED: 'Archived' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--nx-surface)' }}>

      {/* ── Page header ────────────────────────────────────────────────────────── */}
      <div style={{
        padding: '15px 24px', borderBottom: '1px solid var(--nx-border)',
        background: 'var(--nx-surface)', flexShrink: 0,
      }}>
        {selected && (
          <button type="button" className="nx-back-nav" onClick={() => setSelected(null)}>
            <NexusIcon name="ChevronLeft" size={13} />
            All Objects
          </button>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {!selected && (
              <div style={{
                width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                background: '#EDF4FD', border: '1px solid #C7DCFA',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <NexusIcon name="Database" size={22} style={{ color: '#125AB0' }} />
              </div>
            )}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h1 style={{ fontSize: 17, fontWeight: 700, color: 'var(--nx-text)', margin: 0, lineHeight: 1.2 }}>
                  Custom Objects
                </h1>
                {!loading && defs.length > 0 && (
                  <span style={{
                    fontSize: 11, fontWeight: 600, color: 'var(--nx-text-faint)',
                    background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)',
                    borderRadius: 10, padding: '1px 7px', letterSpacing: '0.01em',
                  }}>
                    {defs.length}
                  </span>
                )}
              </div>
              <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', margin: '3px 0 0' }}>
                Define and manage workspace-specific data entities, fields, and relationships.
              </p>
            </div>
          </div>
          {canManage && !selected && (
            <div style={{ flexShrink: 0 }}>
              <Btn variant="primary" icon="Plus" onClick={() => setShowCreate(true)}>
                Create Object
              </Btn>
            </div>
          )}
        </div>
      </div>

      {/* ── Main content ───────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {loading ? (
          /* Loading — full-width table skeleton */
          <div style={{ padding: '12px 24px' }}>
            <div style={{ height: 36, borderRadius: 6, background: 'var(--nx-surface-alt)',
              animation: 'nx-pulse 1.4s ease infinite', marginBottom: 10, maxWidth: 260 }} />
            {[1,2,3,4].map(i => (
              <div key={i} style={{ height: 50, borderRadius: 6, marginBottom: 1,
                background: 'var(--nx-surface-alt)', animation: 'nx-pulse 1.4s ease infinite' }} />
            ))}
          </div>

        ) : error ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <NexusIcon name="AlertCircle" size={32} style={{ color: 'var(--nx-danger)', marginBottom: 12 }} />
              <div style={{ fontSize: 14, color: 'var(--nx-text-muted)', marginBottom: 12 }}>{error}</div>
              <Btn variant="ghost" icon="RefreshCcw" onClick={load}>Retry</Btn>
            </div>
          </div>

        ) : selected ? (
          /* ── Object detail panel (full width) ─────────────────────────────── */
          <ObjectDetailPanel
            key={selected.id}
            def={selected}
            wsId={wsId}
            canManage={canManage}
            canUse={canUse}
            onDefUpdate={handleDefUpdate}
            onDefDelete={handleDefDelete}
            toast={toast}
          />

        ) : defs.length === 0 ? (
          /* ── Empty state ─────────────────────────────────────────────────── */
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EmptyState
              icon="Database"
              title="No custom objects yet"
              desc="Custom objects extend your CRM with workspace-specific data entities."
              action={canManage
                ? <Btn variant="primary" icon="Plus" onClick={() => setShowCreate(true)}>
                    Create Object
                  </Btn>
                : null}
            />
          </div>

        ) : (
          /* ── Directory ───────────────────────────────────────────────────── */
          <>
            {/* Toolbar */}
            <div style={{
              padding: '8px 24px', borderBottom: '1px solid var(--nx-border)',
              background: 'var(--nx-surface)', display: 'flex', alignItems: 'center',
              gap: 16, flexShrink: 0, flexWrap: 'wrap',
            }}>
              <div style={{ flex: '0 0 auto', minWidth: 200, maxWidth: 280 }}>
                <SearchField
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onClear={() => setSearch('')}
                  placeholder="Search objects…"
                />
              </div>
              {/* Status filter dropdown */}
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text-muted)', whiteSpace: 'nowrap' }}>
                  Status
                </span>
                <select
                  className="nx-input"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  aria-label="Filter by status"
                  style={{ fontSize: 12, width: 150 }}
                >
                  <option value="ALL">All statuses ({statusTabCounts.ALL})</option>
                  <option value="ACTIVE">Active ({statusTabCounts.ACTIVE})</option>
                  <option value="DRAFT">Draft ({statusTabCounts.DRAFT})</option>
                  {statusTabCounts.ARCHIVED > 0 && (
                    <option value="ARCHIVED">Archived ({statusTabCounts.ARCHIVED})</option>
                  )}
                </select>
              </label>
            </div>

            {/* Table */}
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
              {filteredDefs.length === 0 ? (
                <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                  <NexusIcon name="Search" size={28} style={{ opacity: 0.25, marginBottom: 10 }} />
                  <div style={{ fontSize: 14, color: 'var(--nx-text-muted)', marginBottom: 4 }}>
                    {search.trim() && statusFilter !== 'ALL'
                      ? `No objects match "${search.trim()}" with status ${STATUS_LABELS[statusFilter] || statusFilter}`
                      : search.trim()
                        ? `No objects match "${search.trim()}"`
                        : `No ${STATUS_LABELS[statusFilter] || statusFilter} objects`}
                  </div>
                  {search.trim() && statusFilter !== 'ALL' ? (
                    <Btn variant="ghost" onClick={() => { setSearch(''); setStatusFilter('ALL'); }}>
                      Clear filters
                    </Btn>
                  ) : search.trim() ? (
                    <Btn variant="ghost" onClick={() => setSearch('')}>
                      Clear search
                    </Btn>
                  ) : (
                    <Btn variant="ghost" onClick={() => setStatusFilter('ALL')}>
                      Show all statuses
                    </Btn>
                  )}
                </div>
              ) : (
                <table className="nx-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 288, paddingLeft: 24 }}>Name</th>
                      <th>Description</th>
                      <th style={{ width: 110 }}>Status</th>
                      <th style={{ width: 80, textAlign: 'center' }}>Fields</th>
                      <th style={{ width: 130 }}>Modified</th>
                      <th className="nx-th-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedDefs.map(def => (
                      <ObjectRow
                        key={def.id}
                        def={def}
                        isSelected={selected?.id === def.id}
                        canManage={canManage}
                        onSelect={setSelected}
                        onArchive={d => setConfirm({ type: 'archive', def: d })}
                        onDelete={d => setConfirm({ type: 'delete', def: d })}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination footer */}
            {totalFiltered > 0 && (
              <div style={{
                borderTop: '1px solid var(--nx-border)', padding: '9px 24px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: 'var(--nx-surface)', flexShrink: 0,
                fontSize: 12, color: 'var(--nx-text-soft)',
              }}>
                <span>
                  Showing {pageStart + 1}–{Math.min(pageStart + ITEMS_PER_PAGE, totalFiltered)} of {totalFiltered} object{totalFiltered !== 1 ? 's' : ''}
                </span>
                {totalPages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <button
                      disabled={safePage <= 1}
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      style={{
                        width: 28, height: 28, borderRadius: 6, border: '1px solid var(--nx-border)',
                        background: safePage <= 1 ? 'transparent' : 'var(--nx-surface-alt)',
                        color: safePage <= 1 ? 'var(--nx-text-faint)' : 'var(--nx-text-soft)',
                        cursor: safePage <= 1 ? 'default' : 'pointer', fontSize: 14,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                      aria-label="Previous page"
                    >‹</button>
                    {getPaginationPages(safePage, totalPages).map((p, i) =>
                      p === null
                        ? <span key={`ellipsis-${i}`} style={{ width: 20, textAlign: 'center', color: 'var(--nx-text-faint)' }}>…</span>
                        : (
                          <button
                            key={p}
                            onClick={() => setCurrentPage(p)}
                            style={{
                              width: 28, height: 28, borderRadius: 6,
                              border: `1px solid ${p === safePage ? 'var(--nx-accent)' : 'var(--nx-border)'}`,
                              background: p === safePage ? 'var(--nx-accent-tint)' : 'transparent',
                              color: p === safePage ? 'var(--nx-accent)' : 'var(--nx-text-soft)',
                              fontWeight: p === safePage ? 700 : 400,
                              cursor: 'pointer', fontSize: 12,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >{p}</button>
                        )
                    )}
                    <button
                      disabled={safePage >= totalPages}
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      style={{
                        width: 28, height: 28, borderRadius: 6, border: '1px solid var(--nx-border)',
                        background: safePage >= totalPages ? 'transparent' : 'var(--nx-surface-alt)',
                        color: safePage >= totalPages ? 'var(--nx-text-faint)' : 'var(--nx-text-soft)',
                        cursor: safePage >= totalPages ? 'default' : 'pointer', fontSize: 14,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                      aria-label="Next page"
                    >›</button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────────────── */}
      {showCreate && (
        <CreateObjectModal
          existingApiNames={existingApiNames}
          onCreated={handleCreated}
          onClose={() => setShowCreate(false)}
        />
      )}

      {confirm?.type === 'archive' && (
        <ConfirmDialog
          title={`Archive "${confirm.def.singularName}"?`}
          desc="This object will be hidden from users. Existing records are preserved."
          confirmLabel="Archive"
          onConfirm={() => handleQuickArchive(confirm.def)}
          onCancel={() => setConfirm(null)}
        />
      )}

      {confirm?.type === 'delete' && (
        <ConfirmDialog
          title={`Delete "${confirm.def.singularName}"?`}
          desc="This cannot be undone. Objects with existing records will be archived instead."
          danger
          confirmLabel="Delete"
          onConfirm={() => handleQuickDelete(confirm.def)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FormBuilderView (unchanged functional scope — toast + API patterns fixed)
// ─────────────────────────────────────────────────────────────────────────────

const FormBuilderView = () => {
  const workspace = useWorkspace();
  const wsId = workspace?.id;
  const toast = useToast();
  const [forms, setForms] = useState([]);
  const [tab, setTab] = useState('list');
  const [form, setForm] = useState({ name: '', description: '', fields: [] });
  const { can } = useRbac();
  const canManage = can('crm.manage');

  useEffect(() => {
    if (!wsId || !window.__nx_api?.forms?.list) return;
    window.__nx_api.forms.list(wsId)
      .then(d => setForms(Array.isArray(d) ? d : []))
      .catch(err => { /* forms module is optional — silently skip if unavailable */ });
  }, [wsId]);

  const handleSave = async () => {
    if (!form.name?.trim() || !wsId || !window.__nx_api?.forms?.create) return;
    try {
      const d = await window.__nx_api.forms.create(wsId, form);
      if (d) {
        setForms(prev => [d, ...prev]);
        setTab('list');
        setForm({ name: '', description: '', fields: [] });
        toast({ tone: 'success', icon: 'Check', title: 'Form saved' });
      }
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to save form',
        desc: err?.message });
    }
  };

  const handlePublish = async (id) => {
    if (!wsId || !window.__nx_api?.forms?.publish) return;
    try {
      const d = await window.__nx_api.forms.publish(wsId, id);
      if (d) setForms(prev => prev.map(f => f.id === id ? { ...f, isPublished: true } : f));
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Publish failed', desc: err?.message });
    }
  };

  const addField = () => setForm(f => ({
    ...f, fields: [...f.fields, { label: '', type: 'text', required: false, name: `field_${f.fields.length + 1}` }]
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--nx-border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'var(--nx-surface)' }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--nx-text)',
          display: 'flex', alignItems: 'center', gap: 8 }}>
          <NexusIcon name="FileText" size={17} style={{ color: 'var(--nx-accent)' }} />
          Dynamic Forms
        </div>
        {canManage && (
          <Btn variant="primary" icon="Plus" onClick={() => setTab('builder')}>New Form</Btn>
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {tab === 'list' && (
          forms.length === 0 ? (
            <EmptyState icon="FileText" title="No forms yet"
              desc="Build a dynamic form to capture structured data."
              action={canManage
                ? <Btn variant="primary" icon="Plus" onClick={() => setTab('builder')}>New Form</Btn>
                : null}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {forms.map(f => (
                <div key={f.id} style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)',
                  borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <NexusIcon name="FileText" size={20} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)' }}>{f.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                      {(f.fields || []).length} fields · {f.isPublished ? 'Published' : 'Draft'}
                    </div>
                  </div>
                  {canManage && !f.isPublished && (
                    <Btn variant="primary" onClick={() => handlePublish(f.id)}>Publish</Btn>
                  )}
                  <Pill tone={f.isPublished ? 'green' : 'gray'}>{f.isPublished ? 'Live' : 'Draft'}</Pill>
                </div>
              ))}
            </div>
          )
        )}
        {tab === 'builder' && (
          <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)',
            borderRadius: 10, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--nx-text)' }}>Form Builder</div>
            <div className="nx-form-field">
              <label className="nx-form-label">Form Name <span className="nx-form-req">*</span></label>
              <input className="nx-input" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Form name" />
            </div>
            <div className="nx-form-field">
              <label className="nx-form-label">Description</label>
              <input className="nx-input" value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Description (optional)" />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 8 }}>
                FIELDS
              </div>
              {form.fields.map((fld, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 80px 28px',
                  gap: 6, marginBottom: 6 }}>
                  <input value={fld.label} className="nx-input"
                    onChange={e => setForm(f => ({ ...f, fields: f.fields.map((x, idx) => idx === i
                      ? { ...x, label: e.target.value, name: e.target.value.toLowerCase().replace(/\s+/g, '_') }
                      : x) }))}
                    placeholder="Field label" />
                  <select value={fld.type} className="nx-input"
                    onChange={e => setForm(f => ({ ...f, fields: f.fields.map((x, idx) => idx === i
                      ? { ...x, type: e.target.value } : x) }))}>
                    {['text','email','number','select','textarea','date','boolean'].map(t =>
                      <option key={t} value={t}>{t}</option>)}
                  </select>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: 11, color: 'var(--nx-text-muted)' }}>
                    <input type="checkbox" checked={fld.required}
                      onChange={e => setForm(f => ({ ...f, fields: f.fields.map((x, idx) => idx === i
                        ? { ...x, required: e.target.checked } : x) }))} /> Req
                  </label>
                  <button onClick={() => setForm(f => ({ ...f, fields: f.fields.filter((_, idx) => idx !== i) }))}
                    style={{ background: 'transparent', border: 'none', color: '#EF4444', cursor: 'pointer' }}>
                    <NexusIcon name="X" size={13} />
                  </button>
                </div>
              ))}
              <Btn variant="ghost" icon="Plus" onClick={addField}>Add Field</Btn>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={() => setTab('list')}>Cancel</Btn>
              <Btn variant="primary" icon="Check" onClick={handleSave}>Save Form</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// IndustryPacksView (toast + API patterns fixed)
// ─────────────────────────────────────────────────────────────────────────────

const INDUSTRY_PACKS = [
  { id: 'bfsi', name: 'BFSI', icon: 'DollarSign', desc: 'Banking, Financial Services & Insurance — loan tracking, policy management, KYC workflows', color: '#0EA5E9' },
  { id: 'healthcare', name: 'Healthcare', icon: 'Activity', desc: 'Patient relationships, provider management, referral tracking, compliance workflows', color: '#16A34A' },
  { id: 'technology', name: 'Technology', icon: 'Cpu', desc: 'SaaS metrics, renewal tracking, license management, ARR/MRR forecasting', color: '#6366f1' },
  { id: 'real_estate', name: 'Real Estate', icon: 'Map', desc: 'Property listings, transaction management, agent performance, territory mapping', color: '#F59E0B' },
  { id: 'manufacturing', name: 'Manufacturing', icon: 'Settings', desc: 'Channel partners, distributor management, quote-to-order, supply chain CRM', color: '#8B5CF6' },
];

const PACK_DEFINITIONS = {
  bfsi: [
    { singularName: 'Loan Application', pluralName: 'Loan Applications', apiName: 'loan_application', icon: 'DollarSign',
      fields: [{ label: 'Amount', apiName: 'amount', type: 'NUMBER', required: true },
               { label: 'Status', apiName: 'loan_status', type: 'SELECT', options: ['Pending','Approved','Rejected','Disbursed'] }] },
    { singularName: 'Insurance Policy', pluralName: 'Insurance Policies', apiName: 'insurance_policy', icon: 'Shield',
      fields: [{ label: 'Policy Number', apiName: 'policy_number', type: 'TEXT', required: true, isPrimary: true },
               { label: 'Premium', apiName: 'premium', type: 'NUMBER' }] },
  ],
  healthcare: [
    { singularName: 'Patient Record', pluralName: 'Patient Records', apiName: 'patient_record', icon: 'Activity',
      fields: [{ label: 'Date of Birth', apiName: 'dob', type: 'DATE' },
               { label: 'Condition', apiName: 'condition', type: 'TEXT' }] },
    { singularName: 'Provider', pluralName: 'Providers', apiName: 'healthcare_provider', icon: 'User',
      fields: [{ label: 'Specialty', apiName: 'specialty', type: 'TEXT', required: true, isPrimary: true },
               { label: 'NPI Number', apiName: 'npi', type: 'TEXT' }] },
  ],
  technology: [
    { singularName: 'SaaS Subscription', pluralName: 'SaaS Subscriptions', apiName: 'saas_subscription', icon: 'Cpu',
      fields: [{ label: 'MRR', apiName: 'mrr', type: 'NUMBER', required: true },
               { label: 'Renewal Date', apiName: 'renewal_date', type: 'DATE' }] },
    { singularName: 'License', pluralName: 'Licenses', apiName: 'software_license', icon: 'Key',
      fields: [{ label: 'Seats', apiName: 'seats', type: 'NUMBER' },
               { label: 'Tier', apiName: 'tier', type: 'TEXT', isPrimary: true }] },
  ],
  real_estate: [
    { singularName: 'Property Listing', pluralName: 'Property Listings', apiName: 'property_listing', icon: 'Map',
      fields: [{ label: 'Address', apiName: 'address', type: 'TEXT', required: true, isPrimary: true },
               { label: 'Price', apiName: 'price', type: 'NUMBER', required: true },
               { label: 'Square Feet', apiName: 'sqft', type: 'NUMBER' }] },
    { singularName: 'Transaction', pluralName: 'Transactions', apiName: 're_transaction', icon: 'DollarSign',
      fields: [{ label: 'Closing Date', apiName: 'closing_date', type: 'DATE' },
               { label: 'Commission', apiName: 'commission', type: 'NUMBER' }] },
  ],
  manufacturing: [
    { singularName: 'Channel Partner', pluralName: 'Channel Partners', apiName: 'channel_partner', icon: 'Settings',
      fields: [{ label: 'Region', apiName: 'region', type: 'TEXT', required: true, isPrimary: true },
               { label: 'Tier', apiName: 'tier', type: 'SELECT', options: ['Bronze','Silver','Gold','Platinum'] }] },
    { singularName: 'Distributor', pluralName: 'Distributors', apiName: 'distributor', icon: 'Truck',
      fields: [{ label: 'Territory', apiName: 'territory', type: 'TEXT' },
               { label: 'Annual Volume', apiName: 'annual_volume', type: 'NUMBER' }] },
  ],
};

const IndustryPacksView = () => {
  const workspace = useWorkspace();
  const wsId = workspace?.id;
  const toast = useToast();
  const [applied, setApplied]   = useState(new Set());
  const [applying, setApplying] = useState(null);

  const handleApply = async (pack) => {
    if (!wsId || applying === pack.id || applied.has(pack.id)) return;
    setApplying(pack.id);
    try {
      const defs = PACK_DEFINITIONS[pack.id] || [];
      if (defs.length === 0) throw new Error('No definitions available for this pack');
      const results = await Promise.allSettled(defs.map(def => coApi.createDef(wsId, def)));
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.length - succeeded;
      if (succeeded === 0) throw new Error('All definitions failed to create');
      setApplied(prev => new Set([...prev, pack.id]));
      if (failed === 0) {
        toast({ tone: 'success', icon: 'Check',
          title: `${pack.name} pack applied`,
          desc: `${defs.length} custom object${defs.length !== 1 ? 's' : ''} created` });
      } else {
        toast({ tone: 'warning', icon: 'AlertCircle',
          title: `${pack.name} partially applied`,
          desc: `${succeeded} of ${defs.length} created — ${failed} failed` });
      }
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Pack apply failed',
        desc: err?.body?.message || err?.message || 'Failed to apply pack' });
    } finally {
      setApplying(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--nx-border)',
        background: 'var(--nx-surface)' }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--nx-text)',
          display: 'flex', alignItems: 'center', gap: 8 }}>
          <NexusIcon name="Globe" size={17} style={{ color: 'var(--nx-accent)' }} />
          Industry Packs
        </div>
        <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
          Pre-built templates tailored for your industry
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {INDUSTRY_PACKS.map(pack => (
            <div key={pack.id} style={{ background: 'var(--nx-surface)',
              border: `1px solid ${applied.has(pack.id) ? pack.color : 'var(--nx-border)'}`,
              borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 42, height: 42, borderRadius: 10,
                  background: pack.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <NexusIcon name={pack.icon} size={20} style={{ color: pack.color }} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--nx-text)' }}>{pack.name}</div>
                  {applied.has(pack.id) && (
                    <Pill tone="green">Applied</Pill>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 13, color: 'var(--nx-text-muted)', lineHeight: 1.5 }}>
                {pack.desc}
              </div>
              <Btn
                variant="ghost"
                icon={applied.has(pack.id) ? 'Check' : applying === pack.id ? 'Loader' : 'Download'}
                disabled={applying === pack.id || applied.has(pack.id)}
                onClick={() => handleApply(pack)}
                style={{ borderColor: pack.color, color: applied.has(pack.id) ? pack.color : undefined }}
              >
                {applying === pack.id ? 'Applying…' : applied.has(pack.id) ? 'Pack Applied' : 'Apply Pack'}
              </Btn>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-011c: CustomObjectRecordsPage — full-width operational records page
// Route: /app/:wsId/custom-objects/:apiName
// Resolves the object definition by apiName, then renders RecordsTab full-width.
// ─────────────────────────────────────────────────────────────────────────────

const CustomObjectRecordsPage = ({ wsId: wsIdProp, apiName, onGoToManager }) => {
  const workspace = useWorkspace();
  const wsId      = wsIdProp || workspace?.id;
  const { can }   = useRbac();
  const canManage = can('crm.manage');
  const canUse    = can('crm.use');
  const toast     = useToast();

  const [def, setDef]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadErr, setLoadErr] = useState(false);

  const fetchDef = useCallback(async (silent = false) => {
    if (!wsId || !apiName) return;
    if (!silent) { setLoading(true); setNotFound(false); setLoadErr(false); }
    try {
      const result = await coApi.listDefs(wsId, { includeArchived: 'true' });
      const list   = Array.isArray(result) ? result : [];
      const found  = list.find(d => d.apiName === apiName) || null;
      setDef(found);
      if (!found) setNotFound(true);
    } catch {
      if (!silent) setLoadErr(true);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [wsId, apiName]);

  useEffect(() => { fetchDef(); }, [fetchDef]);

  useEffect(() => {
    const handler = () => fetchDef(true);
    window.addEventListener('nx-custom-objects-changed', handler);
    return () => window.removeEventListener('nx-custom-objects-changed', handler);
  }, [fetchDef]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100%', gap: 10, color: 'var(--nx-text-muted)' }}>
        <NexusIcon name="Loader" size={18}
          style={{ animation: 'nx-spin 1s linear infinite', color: 'var(--nx-text-muted)' }} />
        <span style={{ fontSize: 14 }}>Loading…</span>
      </div>
    );
  }

  if (loadErr) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <EmptyState
          icon="AlertCircle"
          title="Failed to load"
          desc="Could not load this object. Check your connection and try again."
          action={<Btn icon="RotateCcw" variant="ghost" onClick={() => fetchDef()}>Retry</Btn>}
        />
      </div>
    );
  }

  if (notFound || !def) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <EmptyState
          icon="Database"
          title="Object not found"
          desc={`No custom object with the identifier "${apiName}" exists in this workspace.`}
        />
      </div>
    );
  }

  return (
    <div className="nx-view">
      <RecordsTab
        def={def}
        wsId={wsId}
        canManage={canManage}
        canUse={canUse}
        toast={toast}
        onGoToSettings={null}
        fullPage
        onGoBack={onGoToManager || null}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CO-NAV-001: CustomObjectsLandingView — neutral CRM landing for /custom-objects
// Shown when the user navigates to the Objects area without selecting a specific
// object. Lists only ACTIVE objects in the current workspace. No admin controls.
// Admin users see a "Manage Objects" shortcut to the WS Console.
// Route: /app/:wsId/custom-objects  (replaces prior redirect → Console)
// ─────────────────────────────────────────────────────────────────────────────
const CustomObjectsLandingView = ({ onGoToManager, onSelectObject }) => {
  const workspace = useWorkspace();
  const wsId = workspace?.id;
  const { can } = useRbac();
  const canManage = can('crm.manage');

  const [defs, setDefs]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!wsId) return;
    setLoading(true);
    coApi.listDefs(wsId)
      .then(r => setDefs((Array.isArray(r) ? r : []).filter(d => d.status === 'ACTIVE')))
      .catch(() => setDefs([]))
      .finally(() => setLoading(false));
  }, [wsId]);

  const goToObject = (apiName) => {
    if (onSelectObject) onSelectObject(apiName);
  };

  return (
    <div className="nx-view" style={{ padding: '32px 40px', maxWidth: 920 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--nx-text)', margin: 0 }}>Custom Objects</h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', margin: '4px 0 0' }}>
            Select an object to view and manage its records.
          </p>
        </div>
        {canManage && onGoToManager && (
          <Btn variant="ghost" icon="Settings" onClick={onGoToManager}>
            Manage Objects
          </Btn>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--nx-text-muted)', padding: '40px 0' }}>
          <NexusIcon name="Loader" size={18} style={{ animation: 'nx-spin 1s linear infinite' }} />
          <span style={{ fontSize: 14 }}>Loading…</span>
        </div>
      ) : defs.length === 0 ? (
        <EmptyState
          icon="Database"
          title="No custom objects"
          desc="No active custom objects have been configured for this workspace."
          action={canManage && onGoToManager
            ? <Btn icon="Settings" variant="primary" onClick={onGoToManager}>Go to Settings</Btn>
            : null}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
          {defs.map(obj => {
            const fieldCount = (obj.fields || []).filter(f => f.status === 'ACTIVE').length;
            return (
              <button
                key={obj.id}
                onClick={() => goToObject(obj.apiName)}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 8,
                  padding: '18px 20px', textAlign: 'left', cursor: 'pointer',
                  background: 'var(--nx-surface)', border: '1px solid var(--nx-border)',
                  borderRadius: 10, transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--nx-accent)'; e.currentTarget.style.boxShadow = '0 0 0 2px var(--nx-accent-subtle, rgba(0,0,0,0.06))'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--nx-border)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: 'var(--nx-accent-subtle, var(--nx-bg-hover, rgba(0,0,0,0.06)))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <NexusIcon name={obj.icon || 'Database'} size={16} style={{ color: 'var(--nx-accent)' }} />
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--nx-text)' }}>
                    {obj.pluralName || obj.singularName}
                  </span>
                </div>
                <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', paddingLeft: 42 }}>
                  {fieldCount} {fieldCount === 1 ? 'field' : 'fields'}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Window exports — consumed by workspace module router
// Guard prevents ReferenceError in Node/Vitest environments.
// ─────────────────────────────────────────────────────────────────────────────
if (typeof window !== 'undefined') {
  window.CustomObjectsManagerView  = CustomObjectsManagerView;
  window.CustomObjectRecordsPage   = CustomObjectRecordsPage;
  window.CustomObjectsLandingView  = CustomObjectsLandingView;
  window.FormBuilderView           = FormBuilderView;
  window.IndustryPacksView         = IndustryPacksView;
}
